import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';

/* ─────────────────────────────────────────────────────────────────────
   TRANSLATIONS
───────────────────────────────────────────────────────────────────── */

const en = {
  lang_btn: 'SQ',
  lang_label: 'Shqip',

  // ── Common ──
  save: 'Save',
  cancel: 'Cancel',
  back: 'Back',
  close: 'Close',
  loading: 'Loading...',
  free: 'Free',
  error: 'Error',
  unknown_error: 'Unknown error. Please try again.',
  per_piece: '/ pc',
  notfound_title: 'This page isn\'t on the menu',
  notfound_sub: 'The link you followed doesn\'t exist or was moved.',
  notfound_home_btn: '← Back to home',

  // ── Nav / Landing ──
  nav_features: 'Features',
  nav_how: 'How it works',
  nav_pricing: 'Pricing',
  nav_register: 'Sign up',
  nav_login: 'Log in',
  nav_open_admin: 'Admin panel',
  nav_discover: 'Discover',
  nav_blog: 'Blog',

  // Discover page (client-facing directory)
  discover_title: 'Somewhere new to explore',
  discover_subtitle: "Get a feel for the food, the vibe, and the menu at spots near you — then pick where to go.",
  discover_all_cities: 'All cities',
  discover_all_types: 'All',
  discover_type_cafe: 'Cafe',
  discover_type_restaurant: 'Restaurant',
  discover_type_fastfood: 'Fast-food',
  discover_type_mensa: 'Canteen',
  discover_type_other: 'Other',
  discover_empty_none: 'No restaurants listed yet — check back soon.',
  discover_empty_filtered: 'No match for this filter — try a different city or type.',
  discover_geo_title: 'Hungry for something new?',
  discover_geo_body: "Let us show you what's good nearby — the closest spots, right at the top.",
  discover_geo_cta: 'Find places near me',
  discover_geo_loading: 'Locating…',
  discover_geo_dismiss: 'Not now',
  discover_geo_sorted: 'Showing what\'s closest to you first',
  discover_geo_clear: 'Clear',

  // Profile — public listing toggle
  adm_profile_publicly_listed: 'Show in the public Assaggioo directory',
  adm_profile_publicly_listed_hint: 'Lets clients discover you at assaggioo.com/discover. Turn off to stay reachable only via your own link or QR code.',

  // ── Landing hero ──
  hero_title: 'Digital menu for your restaurant',
  hero_subtitle: 'QR menu, online orders and management panel — all in one platform.',
  hero_cta: 'Start free, 7 days',
  hero_demo: 'View demo',

  // ── Landing features section ──
  feat_title: 'Everything your restaurant needs',
  feat_subtitle: 'Powerful tools to digitize your restaurant in 5 minutes',

  // ── Landing how-it-works ──
  how_title: 'How it works',
  how_sub: 'Three simple steps',
  how_step1_title: 'Create your account',
  how_step1_desc: 'Sign up in 2 minutes. No credit card required.',
  how_step2_title: 'Add your menu',
  how_step2_desc: 'Add categories, dishes, photos and prices.',
  how_step3_title: 'Print the QR code',
  how_step3_desc: 'Place the QR code on the tables. Customers scan and order.',

  // ── Landing pricing ──
  pricing_title: 'Transparent pricing',
  pricing_sub: 'No hidden fees, no contract, and no commission on your orders.',
  period_1m: '1 Month',
  period_3m: '3 Months',
  period_12m: 'Annual',
  per_month: '/month',
  billed_as: 'billed',
  most_popular: '⭐ Most Popular',
  save_pct: 'Save',
  plan_cta_trial_prefix: 'Start',
  plan_cta_trial_suffix: '-day Trial',
  plan_cta_get_started: 'Get Started',
  plan_cta_contact: 'Contact us',

  // ── Landing FAQ ──
  faq_title: 'Common questions.',

  // ── Landing CTA section ──
  cta_title: "Ready to try it?",
  cta_sub: 'Put your QR menu online today, free for 7 days. No credit card, no contract — and you decide afterwards.',
  cta_btn: 'Start free →',
  cta_hint: 'No credit card · Cancel anytime · Setup help included',

  // ── Lead capture form (primary conversion) ──
  nav_talk: 'Book a demo',
  lead_cta_primary: 'Book a free demo',
  lead_cta_hero_reassure: 'No credit card · no contract · we build your first menu for you',
  lead_form_title: "Tell us about your place. We'll build your first menu for you.",
  lead_form_sub: 'Book a short demo or ask for a callback — no obligation, and no pressure to sign up.',
  lead_form_name: 'Your name',
  lead_form_restaurant: 'Restaurant / café name',
  lead_form_phone: 'Phone (WhatsApp)',
  lead_form_city: 'City',
  lead_form_type: 'Type of place (optional)',
  lead_form_type_cafe: 'Café / bar',
  lead_form_type_rest: 'Restaurant',
  lead_form_type_fast: 'Fast-food',
  lead_form_type_mensa: 'Canteen',
  lead_form_type_hotel: 'Hotel',
  lead_form_message: 'Anything you want to tell us (optional)',
  lead_form_submit: 'Book my demo',
  lead_form_sending: 'Sending…',
  lead_form_wa: 'Or reach us by WhatsApp or email — whatever\'s easiest for you.',
  lead_form_self: 'Prefer to explore on your own? Start free, 7 days →',
  lead_form_success_title: 'Got it, {name} 🎉',
  lead_form_success_body: "We'll be in touch within a few hours about the menu for {restaurant}.",
  lead_form_success_wa: 'Message us now on WhatsApp →',
  lead_form_err_required: 'Name, restaurant, phone and city are all required.',
  lead_form_err_generic: 'Something went wrong. Please try again or message us on WhatsApp.',
  lead_band_title: 'Looks good? Book a demo and we\'ll build it for your restaurant.',
  lead_band_cta: 'Book a free demo',

  // ── Landing footer ──
  footer_product: 'Product',
  footer_contact: 'Contact',
  footer_rights: 'All rights reserved.',

  // ── Landing page ──
  // Hero
  lp_hero_pill: 'QR menu · online ordering · rewards that bring guests back',
  lp_hero_title_1: 'The guests you already have —',
  lp_hero_title_2: 'coming back more often.',
  lp_hero_sub: 'A QR menu, commission-free ordering, and a prize wheel and loyalty card that turn first-time visitors into regulars.',
  lp_hero_cta_demo: 'Book a free demo',
  lp_hero_see_menu: 'See a real menu →',
  // Starter card (replaces the old contact band under the features)
  lp_starter_tag: 'Simple pricing',
  lp_starter_name: 'Starter',
  lp_starter_price_note: '/month',
  lp_starter_free: '7 days free — no card required',
  lp_starter_li1: 'Your QR menu, live on its own link',
  lp_starter_li2: 'Unlimited dishes, photos and categories',
  lp_starter_li3: 'You edit all of it yourself, any time',
  lp_starter_cta: 'Start free',
  lp_starter_wizard: 'or build your own package, feature by feature →',
  lp_starter_scan: 'Scan to see a live menu',
  lp_hero_watch: 'See how it works',
  lp_trust_text: 'A new, independent platform — no contract, cancel whenever you want.',
  lp_stat_noapp: 'No app for your guests',
  lp_stat_fiscal: 'Works alongside your POS',
  lp_stat_commission: 'Zero commission on every order',
  lp_stat_setup: 'Online the same day',

  // Who it's for
  lp_who_tag: 'Who it\'s for',
  lp_who_title_1: 'If people eat at your place,',
  lp_who_title_2: 'it fits.',
  lp_who_sub: 'Five kinds of business, one platform — you switch on only what your own room needs and pay for nothing else.',
  lp_who_cafe_title: 'Café',
  lp_who_cafe_desc: 'A QR menu on the table, and regulars who keep coming back.',
  lp_who_rest_title: 'Restaurant',
  lp_who_rest_desc: 'Guests order from the table; waiters and kitchen stay in sync.',
  lp_who_fast_title: 'Fast food',
  lp_who_fast_desc: 'Delivery orders from the menu, tracked to the door — no marketplace cut.',
  lp_who_mensa_title: 'Canteen',
  lp_who_mensa_desc: 'Today\'s menu updated in seconds — nothing to print or reprint.',
  lp_who_hotel_title: 'Hotel',
  lp_who_hotel_desc: 'Room service from the same QR — the room number replaces the table.',

  // The problem — kept only for the AdminPage plan-comparison copy that reuses these
  // keys; the landing page no longer renders a standalone Problem section (folded into
  // the retention section's subtitle below).
  lp_prob_tag: 'The real problem',
  lp_prob_title_1: 'The problem isn\'t the menu.',
  lp_prob_title_2: 'It\'s that the guest forgets you.',
  lp_prob_sub: 'A menu only shows prices. Once the guest leaves, nothing brings them back.',
  lp_prob_1_title: 'Full one night, empty the next',
  lp_prob_1_desc: 'Nobody has a reason to remember your place between one visit and the next.',
  lp_prob_2_title: 'You don\'t know who your regulars are',
  lp_prob_2_desc: 'You know the faces, not how often each came — and never which ones stopped.',
  lp_prob_3_title: 'Ads cost more than they bring back',
  lp_prob_3_desc: 'The people already through your door cost almost nothing to bring back.',

  // The difference — retention mechanics (the old Problem section folds into lp_ret_sub)
  lp_ret_tag: 'The difference',
  lp_ret_title_1: 'The menu was never the problem.',
  lp_ret_title_2: 'Being forgotten is.',
  lp_ret_sub: 'Printed or digital, a menu only shows prices. Once the guest leaves, nothing brings them back — unless you give them one.',
  lp_ret_wheel_title: 'Lucky Bite — the prize wheel',
  lp_ret_wheel_desc: 'It appears only for guests actually inside your restaurant — they spin and walk away with a real code to redeem at the counter.',
  lp_ret_loyalty_title: 'Loyalty card',
  lp_ret_loyalty_desc: 'You set the visits and the reward; when a guest reaches it they get a redemption code your staff checks at the counter.',
  lp_ret_email_title: 'Win-back emails',
  lp_ret_email_desc: 'A guest who hasn\'t ordered in two weeks gets an email from you — a new dish, an offer, or just "we\'ve missed you" — and you see how many come back.',
  lp_ret_wof_lead: 'Lucky Bite, step by step:',
  lp_wof_step1_title: 'The wheel appears',
  lp_wof_step1_desc: 'A guest opens your menu inside the restaurant and the wheel pops up.',
  lp_wof_step2_title: 'They spin, then share',
  lp_wof_step2_desc: 'They spin for a prize and share it to unlock the claim.',
  lp_wof_step3_title: 'They redeem in person',
  lp_wof_step3_desc: 'The prize becomes a real code your staff checks against the till.',
  lp_feat_learn_more: 'See how it works →',

  // Features — the main section of the page now
  lp_sec_features_tag: 'What it does',
  lp_sec_features_title_1: 'Everything it does,',
  lp_sec_features_title_2: 'and why it grows your business.',
  lp_sec_features_sub: 'Tap any one to see how it works in detail.',
  lp_feat_qr_title: 'QR code menu',
  lp_feat_qr_desc: 'Guests scan and see your menu instantly; you edit photos, prices and text in seconds.',
  lp_feat_orders_title: 'Table & online orders',
  lp_feat_orders_desc: 'Guests order from the table QR or for delivery — every order lands in your panel live.',
  lp_feat_staff_title: 'Waiters & kitchen',
  lp_feat_staff_desc: 'Waiters send orders from their phone; the kitchen marks each one ready on its own screen.',
  lp_feat_courier_title: 'Couriers & tracking',
  lp_feat_courier_desc: 'Assign a courier from the panel; they get a map, and the guest follows delivery live.',
  lp_feat_clients_title: 'Guest accounts & reviews',
  lp_feat_clients_desc: 'Guests get an account, their order history, and can rate or comment on individual dishes.',
  lp_feat_stats_title: 'Analytics',
  lp_feat_stats_desc: 'See which dishes sell, at which hours, and daily takings — exportable to CSV.',

  // Honest trust line under the hero
  lp_trust_new: 'New — built with the first restaurants using it. No inflated numbers here.',

  // The problem (3 beats, before any product talk)
  lp_problem_tag: 'The real problem',
  lp_problem_title: 'Full on Friday. Empty on Tuesday.',
  lp_problem_sub: 'The food isn\'t the problem. The problem is that once a guest walks out, nothing brings them to mind again.',
  lp_problem_1_q: '"They loved it — and never came back."',
  lp_problem_1_p: 'A great first visit with no second reason is just an expensive sample.',
  lp_problem_2_q: '"I know my regulars by face."',
  lp_problem_2_p: 'And nothing else. Not how often, not what they spend, not who quietly stopped coming.',
  lp_problem_3_q: '"Every new customer costs more than the last."',
  lp_problem_3_p: 'Ads and delivery apps rent you strangers. The cheapest guest to win back is the one who already likes you.',

  // The difference — section wrapper (cards reuse lp_ret_*)
  lp_diff_tag: 'What makes Assaggioo different',
  lp_diff_title: 'Three mechanics that turn one visit into a habit.',
  lp_diff_sub: 'Every restaurant tool digitises the menu. These are the parts that change how often a guest comes back.',

  // Everything else — compact list (reuses lp_feat_* titles/descriptions)
  lp_rest_tag: 'And the basics, done right',
  lp_rest_title: 'Yes, it covers everything else too.',

  // Pricing — 4 fixed-price bundles from plan_configs (Menu/Counter/Regulars/Everything)
  lp_pricing_tag: 'Pricing',
  lp_pricing_title: 'Start with the menu. Add what grows the business.',
  lp_pricing_sub: '7-day free trial to start. No card to start. Cancel yourself from the panel, any time.',
  lp_pricing_monthly: 'Monthly',
  lp_pricing_3m: '3 months',
  lp_pricing_annual: 'Annual',
  lp_bundle_menu_for: 'Just wants an always-current menu online.',
  lp_bundle_menu_li1: 'QR menu, unlimited items & photos',
  lp_bundle_menu_li2: 'Menu in English & Albanian, one tap to switch',
  lp_bundle_menu_li3: 'Customer accounts, reviews & likes',
  lp_bundle_menu_li4: 'Analytics & CSV export',
  lp_bundle_counter_for: 'Fast-food & delivery-first. Own every order.',
  lp_bundle_counter_li1: 'Everything in Menu',
  lp_bundle_counter_li2: 'Delivery & pickup ordering, 0% commission',
  lp_bundle_counter_li3: 'Table & waiter ordering',
  lp_bundle_counter_li4: 'Loyalty card, free the moment delivery is on',
  lp_bundle_regulars_for: 'Cafés, bars & sit-down restaurants that want repeat guests.',
  lp_bundle_regulars_li1: 'Everything in Menu',
  lp_bundle_regulars_li2: 'Lucky Bite prize wheel',
  lp_bundle_regulars_li3: 'Loyalty card that rewards repeat visits',
  lp_bundle_regulars_li4: 'Win-back emails to bring guests back',
  lp_bundle_everything_for: 'Established venues running every channel.',
  lp_bundle_everything_li1: 'Everything in Counter & Regulars',
  lp_bundle_everything_li2: 'Delivery, dine-in & waiter ordering — every channel',
  lp_bundle_everything_li3: 'Lucky Bite prize wheel + loyalty card',
  lp_bundle_everything_li4: 'Win-back email campaigns',
  lp_bundle_popular: 'Most restaurants pick this',
  lp_bundle_start_free: 'Start free',
  lp_bundle_start_trial: 'Start 7-day trial',
  lp_bundle_billed: 'billed',
  lp_bundle_yr: 'yr',
  lp_bundle_3mo: '3mo',
  lp_bundle_customize: 'None of these fit?',
  lp_bundle_customize_link: 'Build it feature by feature →',

  // How it works (reuses lp_golive_lead/1/2/3 below)
  lp_how_tag: 'Getting started',
  lp_how_title: 'Live tonight. No hardware, no contract.',

  // Final CTA — direct, no lead form
  lp_final_title: 'Your menu can be live tonight.',
  lp_final_sub: 'Lucky Bite spinning for your guests by tomorrow.',
  lp_final_whatsapp: 'Talk to us on WhatsApp',

  // "3 steps to go live" strip inside the features section
  lp_golive_lead: '3 steps to go live:',
  lp_golive_1: 'Add your restaurant — name, address, logo.',
  lp_golive_2: 'Build your menu — dishes, photos, prices, categories.',
  lp_golive_3: 'Print your QR — one sheet per table, and you\'re live.',

  // Legacy step keys — still referenced by AdminPage / other pages, kept here.
  lp_sec_how_tag: 'How it works',
  lp_sec_how_title_1: 'Online in',
  lp_sec_how_title_2: '5 minutes',
  lp_sec_how_sub: 'Three steps to your first QR menu.',
  lp_how_tab_start: 'Getting started',
  lp_how_tab_delivery: 'Delivery',
  lp_step1_title: 'Register your restaurant',
  lp_step1_desc: 'Create your account and fill in the basics: name, logo, opening hours, city.',
  lp_step2_title: 'Add your menu',
  lp_step2_desc: 'Categories and dishes with photos, prices and descriptions. Changing a price later takes two taps.',
  lp_step3_title: 'Print the QR code',
  lp_step3_desc: 'Download the QR — one for the whole menu, one for each table — print it and put it out.',
  lp_delivery_title: 'How delivery works',
  lp_delivery_sub: 'From tap to door, in four steps.',
  lp_delivery_step1_title: 'Order placed',
  lp_delivery_step1_desc: 'The guest browses your menu and places the order from their phone, with the address on the map.',
  lp_delivery_step2_title: 'Courier assigned',
  lp_delivery_step2_desc: 'You assign a courier right from the admin panel — no phone call, no paper.',
  lp_delivery_step3_title: 'Live tracking',
  lp_delivery_step3_desc: 'The courier sees the pinned location on a map and can call the guest with one tap.',
  lp_delivery_step4_title: 'Delivered',
  lp_delivery_step4_desc: 'The courier marks it delivered and the guest is notified straight away.',

  // Panel preview — heading kept, the 6-bullet list retired (was Features again).
  lp_sec_preview_tag: 'The panel',
  lp_sec_preview_title_1: 'One screen',
  lp_sec_preview_title_2: 'runs the whole shift.',
  lp_sec_preview_sub: 'The same panel on your phone or laptop that your staff uses every service.',
  lp_preview_li1: 'Live orders, with an alert the moment one comes in',
  lp_preview_li2: 'Dishes, categories, photos and prices — you edit them yourself',
  lp_preview_li3: 'A QR for the menu and one per table, ready to print',
  lp_preview_li4: 'Accounts for waiters, kitchen and couriers',
  lp_preview_li5: 'Sales, best-selling dishes and your busiest hours',
  lp_preview_li6: 'Reward codes checked at the counter, with a non-fiscal reference slip',

  // Straight talk (early stage, in place of invented social proof)
  lp_early_tag: 'Straight talk',
  lp_early_title_1: 'Assaggioo is new.',
  lp_early_title_2: 'We\'re not hiding it.',
  lp_early_sub: 'You won\'t find big client logos or inflated numbers on this page. You\'ll find a platform that works today, and the people who keep building it every week.',
  lp_early_1_title: 'Among the first',
  lp_early_1_desc: 'So the product gets built around how you actually work.',
  lp_early_2_title: 'You talk to the people who built it',
  lp_early_2_desc: 'Not a call centre.',
  lp_early_3_title: 'No contract, no minimum term',
  lp_early_3_desc: 'Try it free and decide for yourself.',
  lp_early_foot: 'A small, independent team — the people you talk to are the people writing the code.',

  // FAQ (6 questions — only what an owner actually asks before committing)
  lp_quick_contact: 'Questions? Email us',
  lp_faq_sub: 'Can\'t find your answer? Ask us directly.',
  lp_faq1_q: 'What does it cost?',
  lp_faq1_a: 'Starter is €9/month with a 7-day free trial, no card required. Or build your own package feature by feature, from €{price}/month. No contract — you cancel yourself from the panel any time.',
  lp_faq2_q: 'Do you take commission on my orders?',
  lp_faq2_a: 'No. The order goes through Assaggioo; the money doesn\'t. Guests pay you directly, and you pay only your monthly subscription — nothing per order, ever.',
  lp_faq_fiscal_q: 'Does it replace my POS or till?',
  lp_faq_fiscal_a: 'No, and it doesn\'t try to. It sits alongside whatever POS or cash register you use — the slips it prints are a non-fiscal reference for your staff. You ring the sale up on your own system exactly as you do now.',
  lp_faq3_q: 'Do I need to buy hardware or an app?',
  lp_faq3_a: 'No. Assaggioo runs in any browser, on a phone, tablet or computer — and guests need no app either. The only thing you print is the QR code, one sheet of paper per table.',
  lp_faq_setup_q: 'How do I get my menu in?',
  lp_faq_setup_a: 'Adding a dish is a short form; changing a price later is two taps. Short on time? Book a demo and we\'ll build the first menu with you from a photo of your printed one.',
  lp_faq4_q: 'Can I cancel? Is there a contract?',
  lp_faq4_a: 'No contract and no minimum term. You cancel yourself from the panel whenever you want; the service runs to the end of the period you already paid for, and nothing you built is deleted.',
  lp_faq5_q: 'Can I change or stop my plan?',
  lp_faq5_a: 'Yes to both, from the panel. You move up or down at any moment, and you cancel whenever you want — no contract, no penalty, and the service runs to the end of the period you already paid for.',
  lp_faq6_q: 'Which languages does it support?',
  lp_faq6_a: 'The whole interface — your panel and the menu your guests see — works in English and Albanian, and the guest switches between them with one tap. Dish names and descriptions stay exactly as you write them.',

  // Plan feature labels — consumed by src/lib/planDisplay.ts (admin plan-comparison
  // view), NOT by the landing page itself. Kept here with the other lp_* keys.
  lp_plan_everything_from: 'Everything from',
  lp_plan_unlimited_products: 'Unlimited products',
  lp_plan_up_to_products: 'Up to',
  lp_plan_products_suffix: 'products',
  lp_plan_unlimited_cats: 'Unlimited categories',
  lp_plan_up_to_cats: 'Up to',
  lp_plan_cats_suffix: 'categories',
  lp_plan_unlimited_orders: 'Unlimited online orders',
  lp_plan_up_to_orders: 'Up to',
  lp_plan_orders_suffix: 'orders/month',
  lp_plan_no_orders: 'Online orders',
  lp_plan_extras: 'Extra Products (add-ons)',
  lp_plan_analytics: 'Analytics panel',
  lp_plan_clients: 'Client management',
  lp_plan_reviews_likes: 'Customer reviews & likes',
  lp_plan_csv: 'CSV order export',
  lp_plan_support_starter: 'Email support',
  lp_plan_support_business: 'Live Support Chat',
  lp_plan_dish_comments: 'Public comments on every dish',
  lp_plan_delivery_rewards: 'Reward for repeat customers (discount or free item)',
  lp_plan_wheel_feature: 'Wheel of Fortune — pops up when the menu opens',
  lp_plan_courier_feature: 'Courier accounts — assign deliveries, live Google Maps location',
  lp_plan_dinein_feature: 'Table ordering — per-table & room QR codes',
  lp_plan_waiter_feature: 'Waiter ordering — waiter accounts & dashboard',
  lp_plan_email_feature: 'Win-back emails — automated, one email to write',
  lp_plan_daily_feature: 'Daily scratch card — a new thought for every guest, once a day',
  lp_plan_reservations_feature: 'Table reservations — customers request, you confirm',
  sa_plans_feat_reservations: 'Reservations',
  sa_plans_feat_reservations_sub: 'Customers request a table; you confirm or decline',
  sa_form_toggle_reservations: 'Reservations',
  sa_form_toggle_reservations_on: 'Customers can request a table',
  sa_form_toggle_reservations_off: 'Not available',
  sidebar_reservations: 'Reservations',
  reservations_title: 'Reservations',
  reservations_new_btn: '+ New reservation',
  reservations_form_title: 'Request a table',
  reservations_field_name: 'Name',
  reservations_field_phone: 'Phone',
  reservations_field_date: 'Date',
  reservations_field_time: 'Time',
  reservations_field_party: 'Number of guests',
  reservations_field_note: 'Note (optional)',
  reservations_field_note_ph: 'e.g. window table, birthday, allergy…',
  reservations_submit: 'Send request',
  reservations_submitting: 'Sending…',
  reservations_sent_title: 'Request sent!',
  reservations_sent_body: 'The restaurant will confirm or decline it shortly.',
  reservations_back_to_menu: 'Back to menu',
  reservations_empty: 'You have no reservations yet.',
  reservations_mine_title: 'My reservations',
  reservations_cancel_btn: 'Cancel',
  reservations_cancel_confirm: 'Cancel this reservation?',
  reservations_status_pending: 'Pending',
  reservations_status_confirmed: 'Confirmed',
  reservations_status_declined: 'Declined',
  reservations_status_cancelled: 'Cancelled',
  reservations_people_short: 'guests',
  reservations_validation_missing: 'Fill in your name, phone, date, time and party size.',
  reservations_validation_past: 'Pick a date and time in the future.',
  reservations_kind_pick_title: "What would you like to book?",
  reservations_kind_table: "A table",
  reservations_kind_table_sub: "Dine at the restaurant",
  reservations_kind_event: "A private event",
  reservations_kind_event_sub: "Birthday, family or company gathering",
  reservations_kind_room: "A room",
  reservations_kind_room_sub: "Book a stay with check-in and check-out",
  reservations_badge_table: "Table",
  reservations_badge_event: "Event",
  reservations_badge_room: "Room",
  reservations_form_title_event: "Request an event",
  reservations_form_title_room: "Request a room",
  reservations_field_event_type: "Type of event",
  reservations_event_birthday: "Birthday",
  reservations_event_family: "Family gathering",
  reservations_event_business: "Company event",
  reservations_event_other: "Other",
  reservations_field_checkin: "Check-in",
  reservations_field_checkout: "Check-out",
  reservations_field_guests: "Guests",
  reservations_field_room: "Choose a room",
  reservations_room_from: "from",
  reservations_room_per_night: "/ night",
  reservations_room_capacity: "up to {n} guests",
  reservations_room_none: "No room categories are published yet. Send your dates and the hotel will get back to you.",
  reservations_room_details: "Details",
  reservations_room_hide: "Hide",
  reservations_nights_one: "1 night",
  reservations_nights_many: "{n} nights",
  reservations_validation_checkout: "Check-out must be after check-in.",
  reservations_validation_capacity: "This room fits up to {n} guests.",
  amenity_wifi: "Wi-Fi",
  amenity_ac: "Air conditioning",
  amenity_balcony: "Balcony",
  amenity_sea_view: "Sea view",
  amenity_breakfast: "Breakfast included",
  amenity_tv: "TV",
  amenity_minibar: "Minibar",
  amenity_bathtub: "Bathtub",
  amenity_parking: "Parking",
  adm_rooms_title: "Room categories",
  adm_rooms_sub: "Describe each type of room: guests see it with its photos and price when they request a stay.",
  adm_rooms_add: "+ New room category",
  adm_rooms_empty: "No room categories yet. Add your first one: guests will pick from these when booking.",
  adm_rooms_edit: "Edit room category",
  adm_rooms_new: "New room category",
  adm_rooms_name: "Name (English)",
  adm_rooms_name_al: "Name (Albanian)",
  adm_rooms_desc: "Description (English)",
  adm_rooms_desc_al: "Description (Albanian)",
  adm_rooms_price: "Price per night",
  adm_rooms_capacity: "Max guests",
  adm_rooms_beds: "Beds",
  adm_rooms_beds_ph: "e.g. 1 king bed",
  adm_rooms_size: "Size (m²)",
  adm_rooms_amenities: "What the room includes",
  adm_rooms_photos: "Photos",
  adm_rooms_add_photo: "+ Add photo",
  adm_rooms_available: "Visible to guests",
  adm_rooms_hidden: "Hidden",
  adm_rooms_save: "Save",
  adm_rooms_cancel: "Cancel",
  adm_rooms_delete: "Delete",
  adm_rooms_delete_confirm: "Delete this room category?",
  adm_rooms_saved: "Room category saved",
  adm_rooms_need_migration: "The rooms table doesn't exist yet. Run the 2026-10-14 reservation migration in Supabase first.",
  adm_reservations_checkout: "Check-out",
  adm_reservations_room: "Room",
  adm_reservations_event: "Event type",
  adm_tab_reservations: 'Reservations',
  adm_reservations_empty: 'No reservation requests yet.',
  adm_reservations_confirm_btn: 'Confirm',
  adm_reservations_decline_btn: 'Decline',
  adm_reservations_cancelled_by_staff_btn: 'Cancel',
  adm_reservations_contact: 'Contact',
  adm_reservations_party: 'Guests',
  adm_reservations_note: 'Note',
  adm_reservations_filter_all: 'All',
  adm_reservations_filter_pending: 'Pending',
  adm_reservations_filter_upcoming: 'Upcoming',
  adm_reservations_search_ph: 'Search by name, phone or note…',
  daily_card_title: 'Card of the day',
  daily_card_scratch_hint: 'Scratch to reveal',
  daily_card_reveal: 'Reveal without scratching',
  daily_card_close: 'Close',
  daily_card_share: 'Share',
  daily_card_saved: 'Image saved',
  lp_footer_platform: 'Platform',
  lp_footer_support: 'Support chat inside your panel',
  lp_footer_tagline: 'Digital menu, ordering and rewards that bring guests back — for restaurants and cafés.',
  lp_footer_register: 'Sign up →',
  lp_footer_login_panel: 'Log in to Panel',
  lp_footer_qr: 'QR Digital Menu',
  lp_footer_orders: 'Online Orders',
  lp_footer_panel: 'Admin Panel',
  lp_footer_analytics: 'Business Analytics',
  lp_footer_product: 'Product',
  lp_footer_contact: 'Contact',
  lp_footer_copy: '© 2026 Assaggioo. All rights reserved.',
  lp_footer_who: 'Who it\'s for',
  lp_footer_legal: 'Legal',
  lp_footer_privacy: 'Privacy Policy',
  lp_footer_terms: 'Terms of Service',
  lp_footer_refund: 'Refund Policy',

  // ── Auth (customer) ──
  auth_welcome: 'Welcome!',
  auth_create_account: 'Create account',
  auth_login_sub: 'Sign in to your account',
  auth_register_sub: 'Register to place orders',
  auth_tab_login: 'Log in',
  auth_tab_register: 'Sign up',
  auth_google_btn: 'Continue with Google',
  auth_divider_or: 'or',
  auth_full_name: 'Full name',
  auth_full_name_ph: 'e.g. John Smith',
  auth_phone: 'Phone number',
  auth_phone_ph: '+355 69 000 0000',
  auth_phone_hint: 'Used for order confirmation',
  auth_email: 'Email',
  auth_email_ph: 'email@example.com',
  auth_password: 'Password',
  auth_password_ph: 'Minimum 6 characters',
  auth_login_btn: 'Log in',
  auth_register_btn: 'Create account',
  auth_back_menu: '← Back to menu',
  auth_success: 'Account created! Check your email for confirmation.',
  auth_name_required: 'Please enter your full name.',
  auth_phone_required: 'Please enter your phone number — the restaurant needs it to reach you about your order.',
  auth_restaurant_not_found: 'Restaurant not found.',
  auth_email_taken: 'This email already has an account. Try "Log in" instead, or use "Forgot password" if you don\'t remember it.',
  auth_clients_disabled_title: 'Customer login',
  auth_clients_disabled_msg: 'Registration and customer login is currently disabled for',
  auth_call: '📞',

  // ── Admin login ──
  admin_login_title: 'Admin Panel',
  admin_login_email: 'Admin Email',
  admin_login_email_ph: 'admin@restaurant.com',
  admin_login_password: 'Password',
  admin_login_btn: 'Log in as Admin',
  admin_login_back: '← Restaurant menu',
  admin_login_failed: 'Login failed.',
  login_admins_only: 'This page is for restaurant admins only.',
  admin_profile_not_found: 'Profile not found.',
  admin_no_access: 'This account does not have admin rights for this restaurant.',

  // ── Generic login page ──
  login_title: 'Welcome back',
  login_sub: 'Sign in to your admin panel',
  login_email_label: 'Email',
  login_password_label: 'Password',
  login_btn: 'Sign In',
  login_no_account: "Don't have an account?",
  login_register: 'Register for free',
  auth_forgot_title: 'Forgot password',
  auth_forgot_desc: 'Enter your email and you\'ll get a link to reset your password.',
  auth_forgot_email_ph: 'email@restaurant.com',
  auth_forgot_invalid_email: 'Enter a valid email.',
  auth_forgot_sent: 'Email sent! Check your inbox.',
  auth_forgot_send: 'Send reset link',
  auth_forgot_cancel: 'Cancel',
  auth_forgot_close: 'Close',
  login_no_admin: 'No admin account found for this email.',
  login_show_pwd: 'Show password',
  reset_pwd_title: 'Set a new password',
  reset_pwd_sub: 'Choose a new password for your account.',
  reset_pwd_new_label: 'New password',
  reset_pwd_confirm_label: 'Confirm password',
  reset_pwd_submit: 'Save new password',
  reset_pwd_too_short: 'Password must be at least 8 characters.',
  reset_pwd_mismatch: "Passwords don't match.",
  reset_pwd_invalid_title: 'This link has expired',
  reset_pwd_invalid_sub: 'Request a new password reset link and try again.',
  reset_pwd_back_to_login: 'Back to login',
  reset_pwd_success_title: 'Password updated!',
  reset_pwd_success_sub: "Redirecting you now — you're already signed in.",

  // ── Welcome modal (first open after setup) ──
  welcome_modal_title: 'Welcome to Assaggioo!',
  welcome_modal_sub: 'Your restaurant panel is ready. Let\'s grow your business together.',
  welcome_modal_body: 'We\'re here to help you deliver a real experience to your customers — not just words, but results. More engagement, more orders, more loyal clients.',
  welcome_modal_f1: 'A beautiful digital menu your customers will love',
  welcome_modal_f2: 'Online ordering, straight to your kitchen',
  welcome_modal_f3: 'Analytics to understand and grow your business',
  welcome_modal_btn: 'Let\'s get started →',
  adm_onboard_progress_prefix: 'Getting started',
  adm_onboard_progress_sub: 'Follow the steps to get started',
  adm_onboard_done_title: '🎉 Ready to go live!',
  adm_onboard_done_sub: 'All steps completed',
  adm_onboard_step_restaurant: 'Restaurant created',
  adm_onboard_step_product: 'Add your first product',
  adm_onboard_step_shop: 'Turn on the shop',
  adm_onboard_step_qr: 'Test the QR code',

  // ── Menu page ──
  menu_all_cats: 'All',
  menu_search_ph: 'Search dish...',
  menu_fav_empty: 'No favorites yet',
  menu_no_results: 'No dishes found',
  menu_cart_empty: 'Cart is empty',
  menu_extras_label: '✨ Extra Products',
  menu_delivery: '🚚 Delivery',
  menu_total: 'Total',
  menu_continue_order: 'Continue to order →',
  menu_detail_about: 'About this dish',
  menu_detail_desc_fallback: 'A delicious dish prepared with care.',
  menu_item_desc_fallback: 'A delicious dish from our kitchen',
  menu_similar: 'Similar',
  menu_add_btn: 'Add ·',
  menu_added_toast: '✓ Added to cart!',
  menu_offline: '📵 Offline — showing cached menu',
  menu_detail_empty: 'Select a dish to view details',

  // ── Bottom nav / sidebar ──
  bnav_menu: 'Menu',
  bnav_favorites: 'Favorites',
  bnav_cart: 'Cart',
  bnav_account: 'Account',
  bnav_reservations: 'Reserve',
  bnav_orders: 'Orders',

  // ── Profile panel ──
  profile_user_fallback: 'User',
  profile_guest_name: 'Guest',
  profile_guest_hint: 'Ordering from this table',
  profile_my_orders: 'My orders',
  profile_my_rewards: 'My rewards',
  profile_admin_panel: 'Admin Panel',
  profile_logout: 'Log out',
  google_review_fab_title: 'Rate us on Google',
  google_popup_title: 'Enjoying',
  google_popup_sub: 'A quick review on Google helps us a lot — takes 10 seconds.',
  google_popup_cta: '⭐ Leave a Google review',
  google_popup_later: 'Maybe later',
  dish_comments_title: 'Comments',
  dish_comment_yours: "You've already commented on this dish",
  dish_comment_ph: 'What do you think of this dish?',
  dish_comment_submit: 'Post',
  dish_comments_empty: 'No comments yet — be the first.',
  dish_comment_anon: 'A customer',

  // ── About panel ──
  about_maps_btn: 'Open in Google Maps',
  about_photo_credits: 'Photo credits',
  about_opens_new_tab: 'opens in a new tab',
  about_close: 'Close',

  // ── Sidebar tooltips ──
  sidebar_menu: 'Menu',
  sidebar_favorites: 'Favorites',
  sidebar_orders: 'Orders',
  sidebar_rewards: 'Rewards',
  sidebar_about: 'About restaurant',
  sidebar_cart: 'Cart',

  // ── Cart page ──
  cart_title: 'Cart',
  cart_empty: 'Cart is empty',
  cart_view_menu: 'View menu',
  cart_orders_disabled_title: 'Orders disabled',
  cart_orders_disabled_msg: 'does not accept orders at the moment.',
  cart_orders_contact: 'Contact:',
  cart_back_menu: 'Back to menu',
  cart_summary: 'Summary',
  cart_delivery: '🚚 Delivery',
  cart_delivery_hours: 'Delivery hours',
  cart_loyalty_discount_label: 'Loyalty reward',
  cart_gift_tag: 'Gift',
  cart_gift_locked_hint: 'Part of your free reward — you can add more, but not remove it',
  cart_min_order_msg: 'Minimum order is',
  cart_min_order_missing: 'you need',
  cart_outside_radius: 'This address is outside our delivery area',
  cart_delivery_closed_title: 'Delivery is closed right now',
  cart_delivery_closed_opens: 'Opens at',
  cart_dine_in_label: 'Ordering from',
  cart_dine_in_switch: 'Not at this table? Switch',
  cart_dine_in_switch_room: 'Not in this room? Switch',
  wheel_popup_spinning: 'Spinning...',
  wheel_popup_won_title: 'You won!',
  wheel_popup_share_hint_prefix: 'Share this with',
  wheel_popup_share_hint_suffix: 'friends on WhatsApp to claim it',
  wheel_popup_share_gmaps: 'Google Maps review',
  wheel_popup_share_instagram: 'Instagram page',
  wheel_popup_share_btn: 'Share on WhatsApp',
  wheel_popup_sharing: 'Opening WhatsApp...',
  wheel_popup_claim_ready: 'Prize unlocked!',
  wheel_popup_show_staff: 'Give this code to staff when you order.',
  wheel_popup_code_label: 'Your code',
  wheel_popup_close: 'Close',
  wheel_popup_all_products: 'On any item',
  wheel_login_banner: '{name} is live — log in to spin!',
  lucky_page_title: 'Lucky Bite',
  lucky_page_login_hint: 'One quick login and your spin is ready.',
  lucky_page_login_btn: 'Log in to spin',
  lucky_page_claiming: 'Checking your spin...',
  lucky_page_error: 'Something went wrong — try again.',
  lucky_page_retry: 'Try again',
  lucky_page_not_live: 'No Lucky Bite is running right now — ask staff.',
  lucky_page_not_live_hint: 'Ask your server, or check back on your next visit.',
  lucky_hero_kicker: '🎡 One Spin, Big Smile',
  lucky_hero_sub: 'No gimmicks here — a real dish, made fresh.',
  lucky_page_footer: 'Handed to you, warm from our kitchen.',
  wheel_lose_title: 'So close!',
  wheel_lose_hint: 'No luck this time — come back another visit and try again.',
  wheel_already_today_title: 'Already spun today',
  wheel_already_today_hint: 'One spin per day — come back tomorrow for another shot.',
  cart_extras_label: '✨ Extras',
  cart_total: 'Total',
  cart_location_label: 'Delivery Address',
  cart_location_required: '* Required',
  cart_location_confirmed: '✓ Confirmed',
  cart_write: 'Write',
  cart_gps: 'GPS',
  cart_map: 'Map',
  cart_manual_ph: 'e.g. 7 March St., Apt. 3, Tirana',
  cart_manual_hint: 'Enter your full address — street, number, city',
  cart_gps_btn: 'Get location (GPS)',
  cart_gps_loading: 'Getting location...',
  cart_gps_denied: 'GPS was denied. Paste a link or coordinates from Google Maps:',
  cart_or_paste: 'Or paste from Google Maps:',
  cart_paste_ph: 'Paste link or coordinates from Google Maps...',
  cart_get: 'Get',
  cart_maps_hint: 'Open Google Maps → navigate to location → right click → "What\'s here?" → copy the link',
  cart_verify_maps: 'Verify on Google Maps ↗',
  cart_change: '✕ Change',
  cart_open_map: 'Open map to select',
  cart_location_hint: 'Choose how to provide your delivery address',
  cart_gps_not_supported: 'GPS is not supported by this browser.',
  cart_gps_denied_msg: 'GPS permission denied. Choose another method or enable GPS in browser settings.',
  cart_gps_failed: 'Could not get GPS location. Try again or choose another option.',
  cart_maps_no_coords: 'Coordinates not found. Try with "right click → What\'s here?" in Google Maps.',
  cart_note: 'Note (optional)',
  cart_note_ph: 'e.g. no onion, 3rd floor...',
  cart_extras_section: '✨ Extras (optional)',
  cart_login_notice: 'You must log in to place an order.',
  cart_login_btn: 'Log in / Sign up',
  cart_cash_payment_note: 'Cash payment on delivery/pickup',
  cart_cash_payment_note_dinein: 'Cash payment when your order arrives',
  cart_place_order: 'Place order ·',
  cart_placing: 'Placing order...',
  cart_success_title: 'Order placed!',
  cart_success_sub: 'You will be notified when your order is ready.',
  cart_my_orders: 'View my orders',
  cart_guest_failed: "Couldn't start a guest session. Please try again.",
  cart_phone_modal_title: 'One more thing',
  cart_phone_modal_sub: 'Add your phone number so the restaurant can reach you about this order.',
  cart_phone_modal_continue: 'Continue',
  cart_phone_modal_cancel: 'Cancel',
  cart_phone_required: 'Please enter your phone number.',
  cart_address_required: 'Please enter your delivery address before continuing.',
  cart_month_limit: 'This restaurant has reached its monthly order limit. Please try again next month.',

  // ── Orders page ──
  orders_title: 'My orders',
  orders_empty: 'You have no orders yet',
  orders_view_menu: 'View menu',
  order_prefix: 'Order #',
  order_cancel_hint: 'Want to cancel this order? Call the restaurant:',
  my_rewards_title: 'Your rewards',
  rewards_spin_lucky_bite: 'Spin Lucky Bite',
  rewards_empty: "No rewards yet — spin Lucky Bite or keep visiting to earn your first one.",
  rewards_auto_apply_hint: 'Applies automatically to your next order',
  reward_status_active: 'Ready to use',
  reward_status_used: 'Used',
  reward_status_expired: 'Expired',
  reward_code_label: 'Your code',
  order_extras_label: '✨ Extras',
  review_prompt: 'How was your order? Leave a review:',
  review_thanks: 'Thanks for your review!',
  review_restaurant_btn: 'Comment on this restaurant',
  review_restaurant_title: 'How was',
  review_comment_ph: 'What did you think of this restaurant?',
  review_submit: 'Submit',
  review_pick_rating: 'Pick a rating',
  loyalty_ready_prefix: "You've earned:",
  loyalty_ready_suffix: 'Open the menu to claim it.',
  loyalty_reward_fallback: 'a reward',
  loyalty_progress_prefix: '',
  loyalty_progress_suffix: 'visits until',
  loyalty_popup_title: "You've earned a reward",
  loyalty_popup_off: 'off your order',
  loyalty_popup_hint: "No code, no waiting — it's ready for your next order.",
  loyalty_popup_claim_btn: 'Claim it',
  loyalty_popup_claiming: 'Claiming...',
  loyalty_popup_claimed_title: 'Reward claimed',
  loyalty_popup_claimed_hint: "It'll apply itself at checkout — nothing to show, nothing to type.",
  loyalty_popup_go_menu_btn: 'Start ordering',
  loyalty_popup_error: "Couldn't claim right now. Try again.",
  loyalty_active_claim_banner: 'You have an active reward:',
  loyalty_active_claim_expires: 'expires in',
  loyalty_active_claim_hours: 'h',

  // ── Order statuses ──
  status_pending: 'Pending',
  status_confirmed: 'Confirmed',
  status_preparing: 'Preparing',
  status_ready: 'Ready',
  status_on_the_way: 'Coming to you',
  status_delivered: 'Delivered',
  status_cancelled: 'Cancelled',

  // ── Register page ──
  reg_have_account: 'Already have an account?',
  reg_login_link: 'Log in',
  reg_step1: 'Account',
  reg_step2: 'Business',
  reg_step3: 'Plan',
  reg_step4: 'Payment',
  reg_step1_title: 'Create your account',
  reg_step1_sub: 'Your administrator account information',
  reg_full_name: 'Full name',
  reg_full_name_ph: 'John Smith',
  reg_email: 'Email',
  reg_email_ph: 'john@restaurant.com',
  reg_password: 'Password',
  reg_password_ph: 'Minimum 6 characters',
  reg_confirm_password: 'Confirm password',
  reg_confirm_ph: 'Password again',
  reg_name_required: 'Name is required',
  reg_email_invalid: 'Invalid email',
  reg_password_min: 'Minimum 6 characters',
  reg_passwords_mismatch: 'Passwords do not match',
  reg_step2_title: 'Business information',
  reg_step2_sub: 'Your restaurant details',
  reg_restaurant_name: 'Restaurant name',
  reg_restaurant_ph: 'Gjika Tavern',
  reg_nipt: 'Tax ID (NIPT)',
  reg_nipt_optional: '(optional)',
  reg_nipt_ph: 'K12345678A',
  reg_nipt_invalid: 'Invalid NIPT (e.g. K12345678A)',
  reg_phone: 'Phone',
  reg_phone_ph: '+355 69 123 4567',
  reg_currency: 'Currency',
  reg_address: 'Address',
  reg_address_ph: 'Myslym Shyri St., Tirana',
  reg_opening_hours: 'Opening hours',
  reg_opening_ph: 'Mon–Sat: 08:00–22:00',
  reg_restaurant_required: 'Restaurant name is required',
  reg_step3_title: 'Choose your plan',
  reg_step3_sub: 'You can change your plan anytime',
  reg_period_1m: '1 Month',
  reg_period_3m: '3 Months',
  reg_period_12m: 'Annual',
  reg_popular: '⭐ Most Popular',
  reg_per_month: '/month',
  reg_step4_title: 'Payment',
  reg_card: '💳 Credit / Debit Card',
  reg_paypal: '🅿 PayPal',
  reg_redirecting: 'Redirecting...',
  reg_change_plan: '← Change plan',
  reg_success_title: 'Account created!',
  reg_success_sub: 'Welcome to Assaggioo,',
  reg_plan_label: 'Plan:',
  reg_go_panel: 'Go to dashboard →',
  reg_creating: 'Creating your account...',
  reg_back: '← Back',
  reg_continue: 'Continue →',
  reg_start_free: 'Start for Free →',
  reg_continue_payment: 'Continue to Payment →',
  reg_unknown_error: 'Unknown error. Please try again.',
  reg_email_taken: 'This email already has an account. Log in — you can still create your business from there — or use "Forgot password" if you don\'t remember it.',
  reg_email_taken_title: 'This email already has an account',
  reg_email_taken_sub: 'Enter the password for',
  reg_email_taken_continue: 'Continue',
  reg_email_taken_forgot: "Forgot password?",
  reg_email_taken_different: 'Use a different email instead',
  reg_password_required: 'Please enter your password.',
  reg_wrong_password: 'Incorrect password. Try again or use "Forgot password?" below.',
  reg_connection_failed: 'Connection failed. Check your internet and try again.',
  reg_slug_taken: 'This name is already taken. Try a different one.',
  reg_contact_support: 'Contact support if payment was charged: info@assaggioo.com',
  reg_error_title: 'Error',
  reg_processing: 'Processing...',
  reg_first_name: 'First name',
  reg_first_name_ph: 'John',
  reg_first_name_required: 'First name is required',
  reg_last_name: 'Last name',
  reg_last_name_ph: 'Smith',
  reg_last_name_required: 'Last name is required',
  reg_step_account: 'Account',
  reg_step_email: 'Email',
  reg_account_title: 'Create your account',
  reg_account_sub: 'We will send a confirmation link to your email',

  // ── Setup page (post email confirmation) ──
  setup_title: 'Set up your restaurant',
  setup_sub: 'Almost there! Fill in your restaurant details.',
  setup_tagline_label: 'Tagline / Short description',
  setup_tagline_ph: 'e.g. The best pizza in Tirana',
  setup_need_confirm_title: 'Confirm your email first',
  setup_need_confirm_sub: 'Click the link we sent to your email, then come back to this page to complete setup.',
  setup_go_register: '← Back to Register',
  setup_creating: 'Creating your restaurant...',
  setup_success_title: 'You\'re all set!',
  setup_success_sub: 'Your restaurant panel is being opened...',
  setup_go_admin: 'Open Admin Panel →',
  setup_free_plan_note: '7-day free trial activated. Starter: digital menu, unlimited products & categories, product extras, statistics, CSV export, and customer accounts with reviews & likes — all free.',
  setup_paid_plan_note: 'No free trial for this plan — you\'ll be asked to subscribe to activate',
  setup_step_label: 'Restaurant Setup',

  // ── Admin Panel (shared) ──
  adm_role_admin: 'Administrator',
  adm_signout: 'Sign out ↗',
  adm_loading: 'Loading...',
  adm_verifying: 'Verifying role...',
  adm_no_rights: 'No admin rights',
  adm_no_rights_role: 'has role',
  adm_no_rights_action: 'Sign out and log in with another account',

  adm_branch_switch_label: 'Branch',
  adm_sidebar_collapse: 'Collapse sidebar',
  adm_sidebar_expand: 'Expand sidebar',
  adm_menu_shared_banner: 'This menu is shared across the whole chain — editing it here updates every branch.',
  adm_chain_locations_label: 'Locations',

  // Admin nav tabs
  adm_nav_group_daily: 'Daily',
  adm_nav_group_staff: 'Staff',
  adm_nav_group_engagement: 'Engagement',
  adm_nav_group_insights: 'Insights',
  adm_nav_group_settings: 'Settings',
  adm_nav_group_account: 'Account',
  adm_tab_orders: 'Orders',
  adm_orders_locked_title: 'No ordering feature purchased yet',
  adm_orders_locked_desc: 'Delivery, dine-in QR ordering and waiter ordering are all off for your account, so customers have no way to place an order — this list will stay empty until you add one.',
  adm_orders_locked_cta: 'See plans & add-ons',
  adm_tab_menu: 'Menu',
  adm_tab_profile: 'Profile',
  adm_tab_branding: 'Branding',
  adm_tab_loyalty: 'Loyalty Card',
  adm_tab_stats: 'Statistics',
  adm_tab_traffic: 'Traffic',
  adm_traffic_explainer: 'How many people opened your digital menu — from QR scans, shared links, or search.',
  adm_traffic_views: 'Menu views',
  adm_traffic_trend_title: 'Views over time',
  adm_tab_wheel: 'Wheel',
  adm_tab_redemptions: 'Rewards',
  adm_redeem_title: 'Rewards',
  adm_redeem_intro: "A customer at a table wins on the wheel or on their loyalty card and shows staff their code — enter it here to confirm it's real and check it off as claimed. This is not a bill or a till — ring up whatever they order separately, as always.",
  adm_redeem_active_title: 'Customers with a reward waiting',
  adm_redeem_active_empty: 'No unclaimed rewards right now.',
  adm_tab_reengagement: 'Win-back Emails',
  adm_reengage_intro: 'A message from your restaurant to customers who haven\'t ordered in a while — a new dish, an offer, or just a friendly reminder you exist.',
  adm_reengage_locked_title: 'Not included in your plan',
  adm_reengage_locked_desc: 'Win-back emails need customer visit tracking, which your current plan doesn\'t include — upgrade to a plan with client tracking to unlock this.',
  adm_reengage_days_label: 'Inactive for (days)',
  adm_reengage_days_hint: 'A customer becomes eligible once this many days pass since their last order.',
  adm_reengage_message_label: 'Message',
  adm_reengage_message_ph: 'Write what you want your customers to read...',
  adm_reengage_template_miss_btn: '"We miss you"',
  adm_reengage_template_miss: 'It\'s been a while since your last visit — we\'d love to see you again! Come check out what\'s new.',
  adm_reengage_template_new_btn: '"New dish"',
  adm_reengage_template_new: 'We just added something new to the menu, and we think you\'ll love it. Come give it a try!',
  adm_reengage_template_offer_btn: '"Special offer"',
  adm_reengage_template_offer: 'We have a special offer just for you this week — come by and treat yourself.',
  adm_reengage_dish_label: 'Link a dish (optional)',
  adm_reengage_dish_none: '— No dish —',
  adm_reengage_photo_label: 'Photo (optional)',
  adm_reengage_save_btn: 'Save message',
  adm_reengage_send_btn: 'Send now',
  adm_reengage_sending: 'Sending…',
  adm_reengage_sent_result: 'Sent to {sent} of {eligible} eligible customers.',
  adm_reengage_weekly_usage: '{used} of {limit} campaigns used this week.',
  adm_reengage_weekly_limit_hit: "You've used all {limit} campaigns for this week — the counter resets 7 days after your first send.",
  adm_reengage_stat_sent: 'Emails sent (all time)',
  adm_reengage_stat_returned: 'Customers who came back',
  adm_reengage_stat_rate: 'Return rate',
  adm_redeem_code_label: 'Redemption code',
  adm_redeem_code_ph: 'e.g. 482913',
  adm_redeem_search_btn: 'Search',
  adm_redeem_searching: 'Searching...',
  adm_redeem_customer_label: 'Customer',
  adm_redeem_reward_label: 'Reward',
  adm_redeem_expires_label: 'Expires',
  adm_redeem_add_items: 'Add what they ordered',
  adm_redeem_confirm_item_hint: "This is exactly what they won — just confirm and mark it redeemed, no need to re-select it.",
  adm_redeem_mark_used_btn: 'Mark as redeemed',
  adm_redeem_subtotal: 'Subtotal',
  adm_redeem_discount: 'Discount',
  adm_redeem_total_est: 'Estimated total',
  adm_redeem_create_btn: 'Create bill',
  adm_redeem_creating: 'Creating...',
  adm_redeem_done_title: 'Marked as redeemed',
  adm_redeem_done_hint: "This customer's ticket is now checked off — they've collected it.",
  adm_redeem_final_total: 'Amount to collect',
  adm_redeem_print_btn: 'Print receipt',
  adm_redeem_new_btn: 'New redemption',
  adm_rewards_history_title: 'History',
  adm_rewards_history_empty: 'No rewards won in this period.',
  adm_rewards_returning_only: 'Returning customers only',
  adm_rewards_total_known: 'Known value in this period',
  adm_rewards_col_email: 'Customer email',
  adm_rewards_col_reward: 'Reward',
  adm_rewards_col_type: 'Type',
  adm_rewards_col_value: 'Value',
  adm_rewards_col_date: 'Date',
  adm_redeem_not_fiscal: 'Not a fiscal invoice — ring this up on your own till/POS for the official receipt.',
  adm_redeem_no_items: 'Add at least one item first',
  adm_redeem_hours: 'h',
  adm_redeem_pending_dot_title: 'Not redeemed yet',
  adm_tab_tables: 'Tables',
  adm_tab_rooms: 'Rooms',
  adm_dinein_field_table: 'Table:',
  adm_dinein_field_room: 'Room:',
  adm_tab_couriers: 'Couriers',
  adm_courier_add: '+ Add Courier',
  adm_courier_desc: 'Couriers can see their assigned orders, the customer\'s phone and location on Google Maps, and mark deliveries as complete — they can\'t see your menu, stats, or other admin settings.',
  adm_courier_empty: 'No couriers yet — add one to start assigning deliveries.',
  adm_courier_active_orders: 'active deliveries',
  adm_courier_name: 'Full name',
  adm_courier_assign_label: 'Assign courier',
  adm_courier_none: '— unassigned —',
  adm_courier_form_required: 'Name, phone, email and a password (min. 6 characters) are required',
  adm_courier_created: 'Courier account created',
  adm_tab_waiters: 'Waiters',
  adm_waiter_add: '+ Add Waiter',
  adm_waiter_desc: 'Waiters get their own screen: they browse your menu, pick a table and send the order straight to the kitchen, plus a view of how many orders they took today per table. They can\'t see your menu editor, stats or other admin settings.',
  adm_waiter_empty: 'No waiters yet — add one to get started.',
  adm_waiter_name: 'Full name',
  adm_waiter_form_required: 'Name, phone, email and a password (min. 6 characters) are required',
  adm_waiter_created: 'Waiter account created',
  adm_tab_kitchen: 'Kitchen',
  adm_kitchen_add: '+ Add Kitchen Staff',
  adm_kitchen_desc: 'Kitchen staff see every open order for your restaurant with its items, and mark each one as ready when it leaves the pass — they can\'t see your menu editor, stats or other admin settings.',
  adm_kitchen_empty: 'No kitchen accounts yet — add one to get started.',
  adm_kitchen_name: 'Full name',
  adm_kitchen_form_required: 'Name, phone, email and a password (min. 6 characters) are required',
  adm_kitchen_created: 'Kitchen account created',
  adm_staff_login_link: 'Login link for this team',
  adm_staff_login_copy: 'Copy',
  adm_staff_login_copied: 'Copied',
  adm_creating: 'Creating…',
  adm_tab_reviews: 'Reviews',
  adm_reviews_sec_restaurant: 'Restaurant comments',
  adm_reviews_sec_dishes: 'Dish comments',
  adm_reviews_empty_restaurant: 'No restaurant comments yet.',
  adm_reviews_empty_dishes: 'No dish comments yet.',
  adm_reviews_delete_confirm: 'Delete this review?',
  adm_review_public: 'Public',
  adm_review_hidden: 'Hidden',
  adm_review_hide: 'Hide',
  adm_review_publish: 'Publish',
  adm_tab_qr: 'QR Code',
  adm_tab_support: 'Support',

  // Order status labels (admin)
  adm_status_pending: 'Pending',
  adm_status_confirmed: 'Confirmed',
  adm_status_preparing: 'Preparing',
  adm_status_ready: 'Ready',
  adm_status_on_the_way: 'Coming to you',
  adm_status_delivered: 'Delivered',
  adm_status_cancelled: 'Cancelled',
  adm_status_resolved: 'Resolved',
  adm_status_in_progress: 'In progress',

  // Courier app
  courier_login_title: 'Courier',
  courier_dash_title: 'My deliveries',
  courier_active: 'Active',
  courier_done: 'Done',
  courier_empty_active: 'No deliveries assigned right now.',
  courier_empty_done: 'No completed deliveries yet.',
  courier_open_maps: 'Open in Maps',
  courier_call_btn: 'Call customer',
  courier_mark_delivered: 'Mark delivered',

  // Kitchen app
  kitchen_login_title: 'Kitchen',
  kitchen_dash_title: 'Orders to prepare',
  kitchen_to_prepare: 'To prepare',
  kitchen_ready_tab: 'Ready',
  kitchen_empty_active: 'Nothing to prepare right now.',
  kitchen_empty_ready: 'No orders marked ready yet today.',
  kitchen_mark_ready: 'Mark ready',
  kitchen_mark_preparing: 'Start preparing',
  kitchen_table: 'Table',
  kitchen_room: 'Room',
  kitchen_delivery: 'Delivery',
  kitchen_min_ago: 'min ago',
  kitchen_autoprint: 'Auto-print new orders',
  kitchen_autoprint_hint: 'ticket with items and quantities, no prices',
  kitchen_print: 'Print',
  kitchen_paper_width: 'Paper roll width',
  kitchen_paper_default: 'Printer default',
  kitchen_ticket_note: 'Note',

  // Waiter app
  waiter_login_title: 'Waiter',
  waiter_dash_title: 'Waiter panel',
  waiter_tab_new_order: 'New order',
  waiter_tab_today: 'Today',
  waiter_tab_rewards: 'Rewards',
  waiter_rewards_intro: 'A customer at their table wins on the wheel or loyalty card and shows you their code — enter it to confirm it\'s real and check it off as claimed. This never creates an order or a bill; ring up whatever they order separately, as always.',
  waiter_pick_table: 'Pick a table',
  waiter_change_table: 'Change table',
  waiter_table: 'Table',
  waiter_room: 'Room',
  waiter_no_tables: 'No tables set up yet — the restaurant admin adds them in the Tables tab.',
  waiter_search_ph: 'Search the menu…',
  waiter_no_items: 'No items match.',
  waiter_order_summary: 'Order',
  waiter_cart_empty: 'No items added yet.',
  waiter_total_note: 'Reference total — not a fiscal receipt.',
  waiter_note_ph: 'Note for the kitchen (optional)',
  waiter_submit: 'Send order to the kitchen',
  waiter_order_sent: 'Order sent to the kitchen.',
  waiter_err_no_table: 'Pick a table first.',
  waiter_err_empty_cart: 'Add at least one item.',
  waiter_today_orders: 'Orders today',
  waiter_today_total: 'Total today',
  waiter_today_empty: 'You haven\'t placed any orders today.',
  waiter_orders_word: 'orders',

  adm_status_open: 'Open',

  // Orders tab
  adm_orders_filter_all_dates: 'All dates',
  adm_orders_filter_today: 'Today',
  adm_orders_filter_range: 'Range',
  adm_orders_until: 'until',
  adm_orders_clear: '✕ Clear',
  adm_orders_filter_all: 'All',
  adm_orders_filter_delivery: 'Delivery',
  adm_orders_filter_table: 'Table',
  adm_orders_filter_room: 'Room',
  adm_orders_empty_today: 'No orders today.',
  adm_orders_empty_range: 'No orders in this date range.',
  adm_orders_empty: 'No orders',
  adm_orders_empty_status: 'No orders with this status.',
  adm_client_fallback: 'Client',
  adm_open_location: '📍 Open location ↗',
  adm_cancel_order: 'Cancel',
  adm_invoice_btn: '🧾 Invoice',
  adm_invoice_paper_width: 'Paper width',
  adm_orders_note: 'Note',
  adm_orders_count: 'orders',
  adm_orders_label: 'orders',

  // Menu tab
  adm_menu_new_cat: '+ New category',
  adm_menu_new_extra: '➕ Extra',
  adm_menu_new_item: '+ New item',
  adm_menu_categories: '📂 Categories',
  adm_menu_no_cats: '⚠ No categories yet. Create the first category before adding items.',
  adm_menu_orphaned: '⚠ Items without category',
  adm_menu_unavailable: 'Unavailable',
  adm_menu_inactive_overlay: 'Inactive',
  adm_menu_edit_btn: '✏️ Edit',
  adm_menu_deactivate: '🚫 Deactivate',
  adm_menu_activate: '✅ Activate',
  adm_menu_delete_btn: '🗑️ Delete',
  adm_menu_set_cat: '✏️ Set category',
  adm_menu_no_extras: 'No extras yet. Add fries, sauces, etc.',
  adm_menu_extras_title: 'Extras',
  adm_menu_extras_sub: 'Optional add-ons that customers can choose during order',
  adm_menu_new_extra_btn: '➕ New extra',
  adm_plan_upgrade: '↑ Upgrade →',

  // Profile tab
  adm_profile_cover_empty: '📷 Restaurant cover photo',
  adm_profile_upload_cover: '📁 Upload cover photo',
  adm_profile_uploading: '⏳ Uploading...',
  adm_profile_name: 'Restaurant name',
  adm_profile_name_ph: 'e.g. Taverna Gjika',
  adm_profile_name_required: 'The restaurant name cannot be empty',
  adm_profile_tagline: 'Tagline (shown under the name)',
  adm_profile_tagline_ph: 'e.g. Real Albanian flavours',
  adm_profile_url_note: 'Your menu link (/r/{slug}) stays the same — contact support to change it.',
  adm_profile_getfound_title: 'Get found',
  adm_profile_getfound_hint: 'The strongest free way to be found: put your menu link on your Google Business Profile and Instagram bio.',
  adm_profile_getfound_label: 'Your menu link',
  adm_profile_getfound_copy: 'Copy',
  adm_profile_getfound_copied: 'Copied',
  adm_profile_short_desc: 'Short description (shown in menu)',
  adm_profile_short_desc_ph: 'e.g. Authentic Albanian cuisine since 1985',
  adm_profile_about: 'About us (full text)',
  adm_profile_about_ph: "Tell your restaurant's story...",
  adm_profile_location_title: '📍 Location on Google Maps',
  adm_profile_gps_btn: '📡 Get my location now (GPS)',
  adm_profile_gps_loading: '⏳ Getting location...',
  adm_profile_or_paste: 'or paste link from Maps',
  adm_profile_get: 'Get',
  adm_profile_maps_hint: 'Open Google Maps → right click on location → copy URL or coordinates',
  adm_profile_lat: 'Latitude',
  adm_profile_lng: 'Longitude',
  adm_profile_coords_set: '✓ Coordinates set',
  adm_profile_view_maps: '📍 View location on Google Maps ↗',
  adm_profile_delivery_title: '🚚 Delivery fee',
  adm_profile_delivery_free: '✓ Free delivery — shown as "Free" to customers',
  adm_profile_delivery_paid: 'Customers will pay',
  adm_profile_delivery_for: 'for delivery',
  adm_profile_min_order: 'Minimum order',
  adm_profile_min_order_hint: '0 = no minimum',
  adm_profile_radius: 'Delivery radius',
  adm_profile_radius_ph: 'no limit',
  adm_profile_radius_hint: 'Leave empty for no limit. Checked against GPS/map location only.',
  adm_profile_delivery_hours: 'Delivery hours',
  adm_profile_delivery_hours_ph: 'e.g. 11:00 - 22:00',
  adm_profile_delivery_hours_hint: 'Shown to customers if different from opening hours. Informational only.',
  adm_delivery_hours_limit_toggle: 'Only accept delivery orders during set hours',
  adm_delivery_hours_limit_hint: 'Outside this window, customers can still browse but can\'t place a delivery order. Dine-in/table orders are never affected.',
  adm_delivery_open_from: 'Opens at',
  adm_delivery_open_until: 'Closes at',
  adm_profile_google_review: '⭐ Google review link',
  adm_profile_google_review_hint: 'From Google Business Profile → "Ask for reviews". Shown to customers after a delivered order.',
  adm_loyalty_title: 'Loyalty Card',
  adm_loyalty_visits: 'Visits required',
  adm_loyalty_reward: 'Reward',
  adm_loyalty_reward_ph: 'e.g. Free coffee',
  adm_loyalty_hint: 'Customers see their progress on "Your rewards". A visit = a delivered order placed through delivery (table/waiter orders don\'t count).',
  adm_loyalty_items_hint: 'Add one row per dish — set a quantity for "2 of the same dish", or add several different dishes for a combo. All rows together are the whole reward.',
  adm_loyalty_window: 'Reset window (days)',
  adm_loyalty_window_hint: '0 = never resets. E.g. 7 = progress resets to zero if the customer doesn\'t return within a week.',
  adm_loyalty_min_amount: 'Min. order to count as a visit (ALL)',
  adm_loyalty_min_amount_hint: '0 = every order counts. E.g. 500 = only orders of 500 Lekë or more count as a visit.',
  adm_profile_upgrade: 'Upgrade ↑',
  adm_branding_desc: 'Your logo — how customers recognize your business.',
  adm_branding_logo_title: 'Logo',
  adm_branding_logo_sub: 'Shown in the admin sidebar, printed QR codes, and invoices. Square image works best.',
  adm_branding_logo_upload: 'Upload logo',
  adm_profile_save: '💾 Save profile',
  adm_profile_saving: 'Saving...',
  adm_profile_preview: 'Preview ↗',

  // Stats tab
  adm_stats_revenue: 'Revenue (delivered)',
  adm_stats_orders_total: 'Total orders',
  adm_stats_unique_clients: 'Unique clients',
  adm_stats_pending: 'Pending',
  adm_stats_delivered: 'Delivered',
  adm_stats_today: 'Today',
  adm_stats_active_items: 'Active items',
  adm_stats_by_status: 'By status',
  adm_stats_top5: 'Top 5 items',
  adm_stats_orders: 'orders',
  adm_stats_no_data: 'No data yet.',
  adm_stats_range_today: 'Today',
  adm_stats_range_7d: '7 days',
  adm_stats_range_30d: '30 days',
  adm_stats_range_month: 'This month',
  adm_stats_range_last_month: 'Last month',
  adm_stats_range_all: 'All time',
  adm_stats_vs_prev: 'vs previous period',
  adm_stats_aov: 'Average order value',
  adm_stats_cancel_rate: 'Cancelled',
  adm_stats_trend_title: 'Revenue & orders',
  adm_stats_peak_hours_title: 'Peak hours',
  adm_stats_customers_title: 'New vs returning customers',
  adm_stats_new_customers: 'New',
  adm_stats_returning_customers: 'Returning',
  adm_stats_tooltip_revenue: 'Revenue',
  adm_stats_tooltip_orders: 'Orders',

  // Wheel tab
  adm_wheel_desc: 'The wheel pops up the moment a customer opens the menu — not after an order. Set a time window, how many winners are allowed in it, and what they must share on WhatsApp to claim the prize.',
  adm_wheel_label_ph: 'e.g. Free Dessert',
  adm_wheel_weight_ph: 'Weight',
  adm_wheel_weight_hint: 'How often this dish is picked when someone wins, relative to your other prizes — e.g. weight 2 wins twice as often as weight 1.',
  adm_wheel_stock_ph: 'Quantity (blank = unlimited)',
  adm_wheel_stock_hint: 'Set a quantity to cap how many of this prize you give away in total — once reached, the wheel stops offering it.',
  adm_wheel_stock_sold_out: 'Sold out',
  adm_wheel_min_ph: 'Min. order',
  adm_wheel_min_hint: 'Only eligible above this order amount (0 = always)',
  adm_wheel_add: '➕ Add',
  adm_wheel_empty: 'No prizes yet — add at least one so the wheel has something to give.',
  adm_wheel_weight_short: 'weight',
  adm_wheel_min_short: 'min.',
  adm_wheel_qr_title: 'Lucky Bite link/QR',
  adm_wheel_qr_desc: 'Display this in the restaurant (poster, table tent, receipt) — reaching this link is what counts as being here, separate from your regular menu QR.',
  adm_wheel_qr_moved_hint: 'Get this wheel\'s link/QR to display in-store from the QR Codes tab.',
  adm_wheel_campaign_title: 'About',
  adm_wheel_winners_label: 'winners',
  adm_wheel_start_label: 'Starts',
  adm_wheel_end_label: 'Ends',
  adm_wheel_max_winners_label: 'Max winners',
  adm_wheel_max_winners_hint: 'Spread at random across the whole window',
  adm_wheel_share_required_label: 'Shares required',
  adm_wheel_share_type_label: 'What should winners share?',
  adm_wheel_share_hint: 'Winners must share this link with 3 people on WhatsApp to claim their prize — otherwise it passes to someone else.',
  adm_wheel_campaign_save: 'Save campaign',
  adm_wheel_saving: 'Saving...',
  adm_wheel_campaign_saved: 'Campaign saved ✓',
  adm_wheel_prizes_title: 'Prizes',
  adm_wheel_library_title: 'Your wheels',
  adm_wheel_new: 'New wheel',
  adm_wheel_name_ph: 'Wheel name (e.g. Weekend)',
  adm_wheel_name_label: 'Name',
  adm_wheel_availability_title: 'Availability',
  adm_wheel_mode_label: 'How does it turn on?',
  adm_wheel_mode_manual: 'Manual on/off',
  adm_wheel_mode_scheduled: 'Scheduled',
  adm_wheel_manual_on: 'Wheel is ON right now',
  adm_wheel_scheduled_armed: 'Armed (will run automatically during the window above)',
  adm_wheel_status_live: 'Live',
  adm_wheel_status_scheduled: 'Scheduled',
  adm_wheel_status_off: 'Off',
  adm_wheel_no_window: 'No window set yet',
  adm_wheel_no_prizes_hint: 'no prizes yet',
  adm_wheel_no_prizes_toast: "Add at least one prize before turning this wheel on.",
  adm_wheel_no_schedule_toast: "Set a start and end date and save the campaign before turning this wheel on — winning moments are scheduled across that window.",
  adm_wheel_delete: 'Delete',
  adm_wheel_end_before_start: 'End time must be after the start time.',
  adm_wheel_interval_hint: 'A winner lands roughly every {interval}',
  adm_reward_type_discount: '% Discount',
  adm_reward_type_item: 'Menu item',
  adm_reward_pick_item: 'Pick a menu item...',
  adm_reward_all_products: 'All products',
  adm_addon_wheel_title: 'Unlock the Wheel of Fortune',
  adm_addon_wheel_desc: 'Not included in your current plan. Add it as an extra service — cancel anytime.',
  adm_addon_delivery_title: 'Unlock Delivery',
  adm_addon_delivery_desc: 'Not included in your current plan. Add online ordering and delivery as an extra service.',
  adm_addon_unlock: 'Unlock',
  adm_addon_picker_title: 'Add features',
  adm_addon_picker_desc: 'Buy exactly what you need, one at a time or all together — on top of what you already have.',
  adm_addon_picker_total: 'Total',
  adm_addon_picker_buy: 'Buy selected',
  adm_addon_picker_owned: 'You already have this',
  adm_addon_free_with_delivery: 'Free with delivery',
  adm_addon_multiloc_title: 'Multiple Locations',
  adm_addon_multiloc_desc: 'One login for every branch, one shared menu, each with its own QR and orders.',
  adm_addon_multiloc_add_branch: '+ Add Location',
  adm_addon_multiloc_buy_slot: 'more location',
  adm_addon_multiloc_branch_created: 'Location added',
  adm_addon_multiloc_form_required: 'Name, phone and address are required',
  adm_addon_multiloc_modal_title: 'New Location',
  adm_addon_multiloc_no_slots: 'No slots left — buy another location to add more branches.',
  adm_addon_bundle_hint_1: 'as an add-on brings your total to',
  adm_addon_bundle_hint_2: '— upgrading to',
  adm_addon_bundle_hint_3: 'is cheaper and includes more.',

  // Tables (dine-in) tab
  adm_tables_desc: 'Each table gets its own QR — customers who scan it order directly to that table, no delivery address needed.',
  adm_tables_label_ph: 'e.g. Table 4',
  adm_tables_add: '➕ Add table',
  adm_tables_empty: 'No tables yet — add one to generate its QR code.',
  adm_tables_print: '🖨 Print',
  adm_tables_qr_hint: 'To get a table\'s QR code, go to the QR Codes tab and pick it there.',
  // Rooms (hotel room-service) tab — same feature as Tables above, relabeled for
  // restaurants whose business_type is 'hotel' (see isHotel() in src/lib/dineIn.ts)
  adm_tables_desc_hotel: 'Each room gets its own QR — guests who scan it order directly to that room, no delivery address needed.',
  adm_tables_label_ph_hotel: 'e.g. Room 4',
  adm_tables_add_hotel: '➕ Add room',
  adm_tables_empty_hotel: 'No rooms yet — add one to generate its QR code.',
  // A restaurant can now mix tables and rooms (e.g. a hotel's own restaurant with both
  // public dine-in tables and room-service rooms), so each entry picks its own kind.
  adm_tables_kind_label: 'Type',
  adm_tables_kind_table: 'Table',
  adm_tables_kind_room: 'Room',

  // QR tab
  adm_qr_menu_title: 'Menu QR Code',
  adm_qr_menu_desc: 'Place on tables — customers scan and view the menu',
  adm_qr_type_menu: 'Menu',
  adm_qr_type_table: 'Table',
  adm_qr_type_room: 'Room',
  adm_qr_type_lucky: 'Lucky Bite',
  adm_qr_table_desc: 'Pick a table below — customers scanning this exact code order straight to it.',
  adm_qr_lucky_desc: 'Display this one in-store — scanning it is what counts as being here, no location permission needed.',
  adm_qr_no_tables: 'No tables yet — add one above first.',
  adm_qr_not_purchased_title: '{feature} isn\'t included in your plan',
  adm_qr_not_purchased_desc: 'Contact support to add this feature to your account.',
  adm_qr_contact_support: 'Contact support',
  adm_qr_offline: '✓ Works offline — customers can scan even without WiFi if opened before',
  adm_qr_print: '🖨️ Print',
  adm_qr_open: 'Open ↗',
  adm_qr_how: 'How to use',
  adm_qr_menu_url: '🔗 Menu URL:',
  adm_qr_step1_t: 'Download QR Code',
  adm_qr_step1_d: 'Click PNG for printing or SVG for a designer.',
  adm_qr_step2_t: 'Print and place on tables',
  adm_qr_step2_d: 'You can laminate it or use table stands.',
  adm_qr_step3_t: 'Customer scans',
  adm_qr_step3_d: 'The menu opens immediately with the phone camera.',
  adm_qr_step4_t: 'Orders without WiFi',
  adm_qr_step4_d: 'Menu is saved on phone — works offline.',

  // Support tab (admin side)
  adm_support_sub: 'Send a request or issue to our team',
  adm_support_new: '📩 New request',
  adm_support_subject: 'Subject *',
  adm_support_subject_ph: 'e.g. Order issue, Invoice not opening...',
  adm_support_msg: 'Problem description *',
  adm_support_msg_ph: 'Write your problem or request in detail...',
  adm_support_photo: 'Photo (optional)',
  adm_support_upload_photo: '📁 Upload photo',
  adm_support_send: '📤 Send request',
  adm_support_sending: 'Sending...',
  adm_support_my_requests: 'My requests',
  adm_support_no_requests: 'No requests yet.',
  adm_support_new_reply: '↩ New reply',
  adm_support_write_msg: 'Write message...',
  adm_support_write_reply: 'Write reply...',
  adm_support_send_btn: '↩ Send',
  adm_support_ticket_resolved: '✓ This request has been resolved',
  adm_our_team: '⚡ Our team',
  adm_you: '(You)',

  // Modals (admin)
  adm_modal_edit_extra: '✏️ Edit extra',
  adm_modal_new_extra: '✨ New extra',
  adm_modal_name: 'Name *',
  adm_modal_name_ph_extra: 'e.g. Extra fries, Ketchup sauce...',
  adm_modal_desc: 'Description (optional)',
  adm_modal_desc_ph_extra: 'e.g. Crispy fried potatoes...',
  adm_modal_price: 'Price',
  adm_modal_sort: 'Sort order',
  adm_modal_status: 'Status',
  adm_modal_status_active: '✅ Active — shown to customers',
  adm_modal_status_inactive: '🚫 Inactive — hidden',
  adm_modal_photo: 'Photo (optional)',
  adm_modal_add: '➕ Add',
  adm_modal_create: '➕ Create',
  adm_modal_edit_cat: 'Edit category',
  adm_modal_new_cat: 'New category',
  adm_modal_name_sq: 'Albanian name *',
  adm_modal_name_en: 'English name',
  adm_modal_icon: 'Icon (emoji)',
  adm_modal_edit_item: 'Edit item',
  adm_modal_new_item: 'New item',
  adm_modal_img_url: 'Photo URL',
  adm_modal_or: 'or',
  adm_modal_upload_pc: '📁 Upload from computer',
  adm_modal_photos: 'Photos',
  adm_modal_cover: 'Cover',
  adm_modal_add_photo: '+ Add',
  adm_modal_remove_photo: 'Remove photo',
  adm_modal_photos_hint: 'Up to 5 photos. The first one is the cover shown in the menu list.',
  adm_modal_max_photos: 'You can add up to 5 photos per product.',
  adm_modal_category: 'Category *',
  adm_modal_select: 'Select...',
  adm_modal_badge: 'Badge (optional)',
  adm_modal_badge_ph: 'e.g. New, Bestseller',
  adm_modal_featured: 'Featured?',
  adm_modal_featured_no: 'No',
  adm_modal_featured_yes: 'Yes — shown at top',
  adm_modal_item_active: '✅ Active — shown in menu',
  adm_modal_item_inactive: '🚫 Inactive — hidden from menu',
  adm_modal_save_changes: '💾 Save changes',
  adm_modal_add_item: '➕ Add item',
  adm_plan_limit_close: 'Close',
  adm_plan_limit_upgrade: '📩 Send Upgrade Request',

  // Toast messages (admin)
  adm_toast_status_changed: 'Status updated ✓',
  adm_toast_item_deleted: 'Item deleted ✓',
  adm_toast_item_name_req: 'Enter item name',
  adm_toast_item_cat_req: 'Select category',
  adm_toast_item_price_req: 'Enter price',
  adm_toast_item_saved: 'Item updated ✓',
  adm_toast_item_added: 'Item added ✓',
  adm_toast_cat_name_req: 'Enter category name',
  adm_toast_cat_saved: 'Category updated ✓',
  adm_toast_cat_added: 'Category added ✓',
  adm_toast_cat_deleted: 'Category deleted ✓',
  adm_toast_extra_name_req: 'Enter extra name',
  adm_toast_extra_saved: 'Extra updated ✓',
  adm_toast_extra_added: 'Extra added ✓',
  adm_toast_extra_deleted: 'Extra deleted ✓',
  adm_toast_photo_uploaded: 'Photo uploaded ✓',
  adm_toast_gps_not_supported: 'GPS not supported by this browser',
  adm_toast_location_set: 'Location set ✓',
  adm_toast_coords_set: 'Coordinates set ✓',
  adm_toast_coords_not_found: 'Coordinates not found. Try another URL.',
  adm_toast_profile_saved: 'Profile saved ✓',
  adm_toast_ticket_sent: 'Request sent ✓',
  adm_toast_error: 'Error: ',
  adm_toast_upload_error: 'Upload error: ',
  adm_toast_location_error: 'Could not get location: ',
  adm_toast_activated: 'Activated ✓',
  adm_toast_deactivated: 'Deactivated ✓',

  // Confirm dialogs (admin)
  adm_confirm_delete_item: 'This action cannot be undone.',
  adm_confirm_delete_extra: 'Delete',
  adm_confirm_delete_cat_items: 'has items. Deleting will leave them without a category. Continue?',
  adm_confirm_delete_cat: 'Delete category',

  // Plan limit modal
  adm_plan_pkg: 'Package',
  adm_plan_products: 'Products:',
  adm_plan_categories: 'Categories:',

  // ── Super Admin Panel ──
  sa_super_admin: 'Super Admin',
  sa_signout: '↗ Sign out',

  // SA nav tabs
  sa_tab_dashboard: 'Overview',
  sa_tab_restaurants: 'Restaurants',
  sa_tab_clients: 'Clients',
  sa_tab_notes: 'Notes',
  sa_tab_support: 'Support',
  sa_tab_plans: 'Plans',
  sa_tab_finance: 'Snapshot',
  sa_fin_total_mrr: 'Total MRR',
  sa_fin_autorenew_mrr: 'Auto-renew MRR',
  sa_fin_active_subs: 'Active subscriptions',
  sa_fin_collected: 'Collected (recent)',
  sa_fin_churn_30d: 'Cancellations (30d)',
  sa_fin_recent_payments: 'Recent payments',
  sa_fin_no_payments: 'No payments yet.',
  sa_fin_cancellations: 'Cancellations',
  sa_fin_no_cancellations: 'No cancellations yet.',
  sa_fin_chain_payment: 'Chain',
  sa_fin_type_cafe: 'Cafe',
  sa_fin_type_restaurant: 'Restaurant',
  sa_fin_type_fastfood: 'Fast-food',
  sa_fin_type_hotel: 'Hotel',
  sa_fin_type_mensa: 'Canteen',
  sa_fin_type_other: 'Other',
  sa_fin_mrr_by_type: 'MRR by business type',
  sa_fin_search_ph: 'Search by restaurant...',
  sa_fin_th_date: 'Date',
  sa_fin_th_restaurant: 'Restaurant',
  sa_fin_th_plan: 'Plan',
  sa_fin_th_type: 'Type',
  sa_fin_th_amount: 'Amount',
  sa_fin_th_status: 'Status',
  sa_fin_th_mrr_lost: 'MRR lost',
  sa_fin_business_health: 'Business health',
  sa_fin_views_not_started: 'Menu-view tracking starts collecting data from today — conversion will show once visits come in.',
  sa_fin_conversion_note: 'Conversion = orders ÷ menu views, since tracking started.',
  sa_fin_th_business_type: 'Type',
  sa_fin_th_orders_month: 'Orders/month',
  sa_fin_th_repeat_rate: 'Repeat rate',
  sa_fin_th_last_order: 'Last order',
  sa_fin_th_conversion: 'Conversion',
  sa_fin_today: 'Today',
  sa_fin_inactive: 'Inactive 30d+',
  sa_fin_at_risk: 'Revenue at risk (14d)',
  sa_fin_ltv: 'Avg. LTV (churned)',
  sa_fin_forecast_title: 'Revenue forecast',
  sa_fin_forecast_30: 'Next 30 days',
  sa_fin_forecast_60: 'Next 60 days',
  sa_fin_forecast_90: 'Next 90 days',
  sa_fin_forecast_note: 'Based on current auto-renew MRR, assuming no cancellations.',
  sa_fin_th_expires: 'Expires',
  sa_fin_mrr_by_city: 'MRR by city',
  sa_fin_city_unknown: 'Unknown',
  sa_fin_refund_btn: 'Mark refunded',
  sa_fin_refunded_badge: 'Refunded',
  sa_fin_refunded_amount_label: 'Refunded:',
  sa_fin_refund_within_window: 'Within the refund window',
  sa_fin_refund_past_window: 'Past the refund window',
  sa_fin_refund_days_left: 'days left',
  sa_fin_refund_days_over: 'days over',
  sa_fin_refund_prompt_title: 'Mark refunded',
  sa_fin_refund_prompt_amount: "Refund amount (€) — this only records it here, it doesn't trigger a real refund on PayPal. Reduce it to account for the processor fee if you're not refunding in full:",
  sa_fin_refund_invalid_amount: 'Enter a valid amount between 0 and the original payment.',
  sa_refund_window_title: 'Refund window',
  sa_refund_window_intro: "How many days after payment a client can still request a refund. Past this, the button still works but flags it as expired — it's not a hard block.",
  sa_refund_window_1m: 'Monthly plan',
  sa_refund_window_3m: '3-month plan',
  sa_refund_window_12m: 'Yearly plan',
  sa_refund_window_days_suffix: 'days',

  // Growth tab
  sa_tab_growth: 'Growth',
  sa_growth_funnel_title: 'Activation funnel',
  sa_growth_stage_signed_up: 'Signed up',
  sa_growth_stage_has_menu: 'Added a menu',
  sa_growth_stage_live: 'Live (got an order)',
  sa_growth_stage_paying: 'Paying',
  sa_growth_source_title: 'Signup source',
  sa_growth_source_referral: 'Referral',
  sa_growth_source_organic: 'Organic',
  sa_growth_source_ad: 'Ad',
  sa_growth_source_direct: 'Direct',
  sa_growth_source_other: 'Other',
  sa_growth_source_unknown: 'Not recorded',
  sa_growth_stuck_title: 'Stuck in onboarding',
  sa_growth_stuck_empty: 'No restaurant is stuck — everyone who signed up more than 7 days ago has added at least one menu item.',
  sa_growth_th_signed_up: 'Signed up',
  sa_growth_th_days: 'Days since',
  sa_growth_referral_note: 'Referral program — needs a decision: what does a restaurant get for inviting another one?',

  // Retention tab
  sa_tab_retention: 'Retention',
  sa_ret_cohort_title: 'Retention curve — % still active by age',
  sa_ret_month_short: 'mo',
  sa_ret_reasons_title: 'Cancellation reasons',
  sa_ret_reasons_empty: 'No cancellations yet.',
  sa_ret_reason_payment_failed: 'Payment failed',
  sa_ret_reason_expired: 'Expired',
  sa_ret_reason_price: 'Price',
  sa_ret_reason_missing_feature: 'Missing feature',
  sa_ret_reason_closed: 'Business closed',
  sa_ret_reason_payment_method_removed: 'Removed saved payment method',
  sa_ret_reason_other: 'Other',
  sa_ret_health_title: 'Health score',
  sa_ret_th_score: 'Score',

  // SA nav groups
  sa_nav_group_overview: 'Overview',
  sa_nav_group_business: 'Business',
  sa_nav_group_people: 'Clients & Support',
  sa_nav_group_finance: 'Finance',
  sa_nav_group_stats: 'Statistics',
  sa_nav_group_developer: 'Developer',
  sa_nav_group_marketing: 'Marketing',

  // SA metric pages — shared chrome
  sa_metric_range_7d: '7D',
  sa_metric_range_30d: '30D',
  sa_metric_range_90d: '90D',
  sa_metric_range_12m: '12M',
  sa_metric_range_custom: 'Custom',
  sa_metric_export_csv: 'Export CSV',
  sa_metric_vs_previous: 'vs previous period',
  sa_metric_plain_language: 'Plain language',
  sa_metric_show_chart: 'Show chart',
  sa_metric_manual_entry_title: "This month's actual",
  sa_metric_manual_entry_hint: "No automatic tracking exists for this yet — enter the real number each month.",
  sa_metric_manual_note_placeholder: 'Note (optional)',
  sa_metric_manual_save: 'Save',
  sa_metric_toast_saved: 'Saved',
  sa_metric_low_sample_prefix: 'Based on only',
  sa_metric_low_sample_suffix: 'cases so far — interpret with care.',
  sa_metric_incomplete_title: "This page isn't finished yet",
  sa_metric_incomplete_body: "There's no automatic tracking for this metric yet — what you see is built from the numbers you enter manually below. Log real values each month for it to become accurate.",
  sa_metric_incomplete_dismiss: 'Got it',
  sa_metric_demo_excluded_prefix: 'Demo data excluded:',
  sa_metric_demo_excluded_restaurants: 'demo restaurants',
  sa_metric_demo_excluded_and: 'and',
  sa_metric_demo_excluded_rows: 'synthetic billing rows are not counted in this figure.',

  // SA metric pages — Finance
  sa_tab_fin_mrr: 'MRR',
  sa_metric_fin_mrr_explainer: 'Monthly recurring revenue — the clearest single signal of financial health. Steady growth means the business is compounding.',
  sa_metric_fin_mrr_breakdown: 'MRR movements',
  sa_tab_fin_arr: 'ARR',
  sa_metric_fin_arr_explainer: 'Annual run-rate of recurring revenue — MRR × 12. Useful for yearly planning and comparing against costs.',
  sa_tab_fin_gross_margin: 'Gross Margin',
  sa_metric_fin_gross_margin_explainer: 'Share of revenue left after direct costs (payment fees, hosting). Higher means more of every euro stays as profit.',
  sa_tab_fin_burn_rate: 'Burn Rate',
  sa_metric_fin_burn_rate_explainer: 'How much cash the business is losing (or gaining) per month, after expenses. Negative means the business is profitable.',
  sa_tab_fin_churn: 'Churn Rate',
  sa_metric_fin_churn_explainer: 'Share of active restaurants that cancel each period. Lower is healthier — it means customers are sticking around.',
  sa_metric_fin_churn_breakdown: 'Cancellation reasons',
  sa_tab_fin_ltv: 'LTV',
  sa_metric_fin_ltv_explainer: 'Average lifetime revenue from a restaurant before it churns. Compare it to CAC to see if growth is worth its cost.',
  sa_metric_fin_ltv_breakdown: 'Highest lifetime value',
  sa_tab_fin_cac: 'CAC',
  sa_metric_fin_cac_explainer: 'Average cost to acquire one new paying restaurant, from the marketing spend you log each month.',
  sa_tab_fin_ltv_cac: 'LTV:CAC Ratio',
  sa_metric_fin_ltv_cac_explainer: 'How many euros a customer is worth compared to what it costs to acquire them. Above 3x is generally considered healthy.',
  sa_tab_fin_cash_balance: 'Cash Balance',
  sa_metric_fin_cash_balance_explainer: "How much money is actually in the account right now — a manual snapshot you update, not derived from other numbers.",
  sa_tab_fin_paypal_balance: 'PayPal Balance',
  sa_paypal_balance_intro: "The real, live balance in your PayPal account, pulled directly from PayPal — not derived from what's recorded in this app, so it already accounts for PayPal's own fees.",
  sa_paypal_balance_refresh: 'Refresh',
  sa_paypal_balance_error_title: "Can't reach PayPal balance yet",
  sa_paypal_balance_total: 'Total balance',
  sa_paypal_balance_available: 'Available balance',
  sa_paypal_balance_withheld: 'Withheld',
  sa_paypal_balance_as_of: 'As of',
  sa_tab_fin_refunds_client: 'Client Refunds',
  sa_tab_fin_refunds_influencer: 'Influencer Refunds',

  // SA metric pages — Marketing
  sa_tab_mkt_traffic: 'Website Traffic',
  sa_metric_mkt_traffic_explainer: 'Visits to assaggioo.com — tracked automatically going forward; older months you can still log by hand below.',
  sa_tab_mkt_menu_traffic: 'Menu Traffic',
  sa_metric_mkt_menu_traffic_explainer: 'Real, automatically-tracked views of restaurants\' digital menus across the whole platform — from QR scans and shared links.',
  sa_metric_mkt_menu_traffic_breakdown: 'Top restaurants by views',
  sa_tab_mkt_signups: 'Signups',
  sa_metric_mkt_signups_explainer: 'New restaurants that create an account. The very top of the funnel — everything else starts here.',
  sa_metric_mkt_signups_breakdown: 'By source',
  sa_tab_mkt_conversion: 'Trial → Paid Conversion',
  sa_metric_mkt_conversion_explainer: 'Share of signups that become paying customers. This is where product and pricing prove themselves.',
  sa_tab_mkt_channels: 'Channels & ROI',
  sa_metric_mkt_channels_explainer: 'Signups by acquisition channel (real), compared against what you spend on each (logged manually) — shows which channel is actually worth it.',
  sa_metric_mkt_channels_breakdown: 'By channel',
  sa_metric_channels_spend_title: "Log this month's spend per channel",
  sa_metric_channels_spend_referral: 'Referral spend (€)',
  sa_metric_channels_spend_organic: 'Organic/content spend (€)',
  sa_metric_channels_spend_ad: 'Ads spend (€)',
  sa_metric_channels_spend_direct: 'Direct/other spend (€)',

  // SA Promo Codes
  sa_tab_promos: 'Promo Codes',
  sa_promo_intro: 'Codes give an influencer\'s referrals a discount on their first payment only, and track a commission for you to pay them manually.',
  sa_promo_new: 'New promo code',
  sa_promo_cancel: 'Cancel',
  sa_promo_field_code: 'Code',
  sa_promo_field_influencer: 'Influencer name',
  sa_promo_field_contact: 'Contact',
  sa_promo_field_plan: 'Plans',
  sa_promo_plan_wizard: 'Wizard (custom package)',
  sa_promo_plans_hint: 'Leave all unchecked to allow every plan (legacy + wizard).',
  sa_promo_plans_all: 'All plans',
  sa_promo_field_periods: 'Billing periods',
  sa_promo_periods_hint: 'Leave all unchecked to allow every period.',
  sa_promo_period_1m: '1 month',
  sa_promo_period_3m: '3 months',
  sa_promo_period_12m: '12 months',
  sa_promo_period_1m_short: '1m',
  sa_promo_period_3m_short: '3m',
  sa_promo_period_12m_short: '12m',
  sa_promo_periods_all: 'All periods',
  sa_promo_field_discount: 'Discount',
  sa_promo_field_commission: 'Commission %',
  sa_promo_create: 'Create code',
  sa_promo_codes_title: 'Codes',
  sa_promo_none: 'Nothing yet.',
  sa_promo_status: 'Status',
  sa_promo_active: 'Active',
  sa_promo_inactive: 'Inactive',
  sa_promo_provision: 'Provision on PayPal',
  sa_promo_toast_provisioned_partial: 'Provisioned with errors for:',
  sa_promo_redemptions_title: 'Redemptions & commission owed',
  sa_promo_owed_prefix: 'Currently owed to influencers:',
  sa_promo_owed_suffix: '(unpaid).',
  sa_promo_paid: 'Amount paid',
  sa_promo_commission: 'Commission',
  sa_promo_paid_status: 'Paid',
  sa_promo_unpaid_status: 'Unpaid',
  sa_promo_mark_paid: 'Mark paid',
  sa_promo_toast_missing: 'Enter a code and influencer name',
  sa_promo_toast_provisioned: 'PayPal discount plan ready',
  sa_promo_delete: 'Delete',
  sa_promo_delete_confirm: 'Delete promo code',
  sa_promo_delete_blocked: "This code has recorded redemptions — deactivate it instead of deleting, so the commission history stays intact.",
  sa_promo_deleted: 'Promo code deleted',
  sa_promo_account_col: 'Portal account',
  sa_promo_account_active: 'Has account',
  sa_promo_account_create: 'Create account',
  sa_promo_account_email_prompt: "Influencer's login email:",

  // Influencer portal
  influencer_login_title: 'Influencer Portal',
  influencer_login_email: 'Email',
  influencer_login_btn: 'Log in',
  influencer_no_access: "This isn't an influencer account. If your account was just created, ask the Assaggioo team to check it.",
  influencer_bad_credentials: 'Wrong email or password.',
  influencer_portal_title: 'My Dashboard',
  influencer_stat_total_earned: 'Total earned',
  influencer_stat_pending: 'Pending payout',
  influencer_stat_paid: 'Paid out to you',
  influencer_stat_businesses: 'Businesses referred',
  influencer_codes_title: 'Your promo codes',
  influencer_code_expires: 'expires',
  influencer_code_expired: 'Expired',
  influencer_code_no_expiry: 'No expiry',
  influencer_table_title: 'Referrals',
  influencer_empty: "No referrals yet — share your code to get started.",
  influencer_th_date: 'Date',
  influencer_th_business: 'Business',
  influencer_th_commission: 'Commission',
  influencer_th_status: 'Status',
  influencer_status_paid: 'Paid',
  influencer_status_pending: 'Pending',
  influencer_status_clawed_back: 'Reversed (refunded)',
  sa_promo_search_placeholder: 'Search by code or influencer...',
  sa_promo_influencer_earnings_title: 'Influencer earnings',
  sa_promo_influencer_th_earned: 'Total earned',
  sa_promo_influencer_th_businesses: 'Businesses referred',
  sa_promo_no_influencers: 'No influencers yet — add one first.',
  sa_promo_select_influencer: 'Select an influencer...',
  sa_promo_field_expires: 'Expires',
  sa_promo_expired: 'Expired',
  sa_promo_no_expiry: 'No expiry',

  // SA Influencers
  sa_tab_influencers: 'Influencers',
  sa_inf_intro: 'Create an influencer first, then give them one or more promo codes. Once you create their portal account, they can log in and see their own referrals and earnings.',
  sa_inf_login_link: 'Influencer portal login link',
  sa_inf_new: 'New influencer',
  sa_inf_create: 'Create influencer',
  sa_inf_toast_missing_name: "Enter the influencer's name",
  sa_inf_delete_confirm: 'Delete influencer',
  sa_inf_delete_blocked: 'This influencer still has promo codes tied to them — delete or reassign those codes first.',
  sa_inf_deleted: 'Influencer deleted',
  sa_inf_email_invalid: 'Enter a valid email address.',
  sa_inf_account_broken: 'Broken account',
  sa_inf_account_repair: 'Repair',
  sa_inf_account_repaired: 'Influencer account repaired — they can log in now.',
  sa_inf_login_not_deleted: 'but the portal login was not removed:',

  // SA Refunds
  sa_refunds_client_intro: 'Payments that were refunded to a restaurant.',
  sa_refunds_client_none: 'Nothing yet.',
  sa_refunds_th_date: 'Date',
  sa_refunds_th_restaurant: 'Restaurant',
  sa_refunds_th_plan: 'Plan',
  sa_refunds_th_amount: 'Amount',
  sa_refunds_influencer_intro: 'Promo redemptions whose underlying payment was later refunded — if the commission was already paid out, it needs to be clawed back from the influencer.',
  sa_refunds_influencer_needs_clawback: 'Needs clawback',
  sa_refunds_influencer_no_clawback_needed: 'Not paid yet — nothing to claw back',
  sa_refunds_influencer_clawed_back: 'Clawed back',
  sa_refunds_influencer_mark_clawback: 'Mark clawed back',

  // SA Cash ledger
  sa_ledger_title: 'Money movements',
  sa_ledger_intro: 'Every euro in and out — earnings from businesses, refunds to businesses, and influencer commissions reversed after a refund.',
  sa_ledger_filter_all: 'All',
  sa_ledger_filter_earning: 'Earnings',
  sa_ledger_filter_refund: 'Refunds',
  sa_ledger_total_in: 'Total in',
  sa_ledger_total_out: 'Total out',
  sa_ledger_net: 'Net',
  sa_ledger_th_type: 'Type',
  sa_ledger_kind_earning: 'Earning',
  sa_ledger_kind_refund_client: 'Refund — Client',
  sa_ledger_kind_refund_influencer: 'Refund — Influencer',

  // Operations tab
  sa_tab_operations: 'Operations',
  sa_ops_avg_response: 'Avg. first response',
  sa_ops_avg_resolution: 'Avg. resolution time',
  sa_ops_audit_title: 'Audit trail',
  sa_ops_audit_empty: 'No recorded actions yet.',
  sa_ops_th_actor: 'Who',
  sa_ops_th_action: 'Action',
  sa_ops_errors_24h: 'Errors (24h)',
  sa_ops_errors_title: 'Errors',
  sa_ops_errors_empty: 'No errors logged — all clear.',
  sa_ops_th_last_seen: 'Last seen',
  sa_ops_th_source: 'Source',
  sa_ops_th_message: 'Message',
  sa_ops_th_count: 'Count',
  sa_ops_plan_change: 'Plan change',
  sa_ops_decision_note: 'Referral program and customer email campaigns — both need a business decision (reward structure, content, opt-out) before they can be built as real features.',

  sa_tab_reviews: 'Reviews',
  sa_reviews_empty: 'No reviews yet.',
  sa_reviews_empty_search: 'No review found.',
  sa_reviews_search_ph: 'Search by restaurant or comment...',
  sa_reviews_filter_low: '3 stars or below',
  sa_reviews_avg: 'Average rating',
  sa_reviews_delete_confirm: 'Delete this review?',

  // SA status labels
  sa_status_active: 'Active',
  sa_status_inactive: 'Inactive',
  sa_status_expired: 'Expired',
  sa_status_starts: 'Starts',

  // Dashboard
  sa_dash_total_restaurants: 'Total Restaurants',
  sa_dash_active: 'Active',
  sa_dash_inactive: 'Inactive',
  sa_dash_clients: 'Clients',
  sa_dash_notes: 'Notes',
  sa_dash_currencies: 'Active currencies',
  sa_dash_status_chart: 'Restaurant status',
  sa_needs_title: 'Needs attention',
  sa_needs_at_risk: 'Expiring soon',
  sa_needs_suspended: 'Payment failed',
  sa_needs_stuck: 'Stuck in onboarding',
  sa_dash_shop_active: 'Shop active',
  sa_dash_login_active: 'Login active',
  sa_dash_admin_pending: '⏳ Admin pending',
  sa_dash_recent: 'Recently added',
  sa_dash_view_all: 'View all restaurants →',
  sa_dash_by_currency: 'By currency',
  sa_dash_quick_actions: 'Quick actions',
  sa_dash_create_rest: 'Create new restaurant',
  sa_dash_view_clients: 'View clients',
  sa_dash_new_note: 'New note',
  sa_dash_mrr: 'Monthly recurring revenue',
  sa_dash_expiring_title: '⏰ Expiring within 7 days',
  sa_dash_expiring_empty: 'Nothing expiring soon',
  sa_dash_expiring_expired: 'Expired',
  sa_dash_expiring_today: 'Expires today',
  sa_dash_expiring_days: 'days left',
  sa_dash_trial_conversion: 'Trial conversion',
  sa_dash_trial_no_data: 'No completed trials yet',
  sa_dash_trial_breakdown: 'converted · churned',
  sa_dash_growth_title: 'Platform growth',
  sa_dash_signups_chart_title: 'New restaurants / week',
  sa_dash_platform_orders_title: 'Platform-wide orders & GMV / week',
  sa_dash_plan_mix_title: 'Restaurants & MRR by plan',
  sa_dash_tooltip_signups: 'New restaurants',
  sa_dash_tooltip_orders: 'Orders',
  sa_dash_tooltip_gmv: 'GMV',
  sa_dash_tooltip_mrr: 'MRR',
  sa_dash_tooltip_count: 'Restaurants',

  sa_op_edit: 'Edit',
  sa_op_delete: 'Delete',
  sa_op_cancel: 'Cancel',

  sa_tab_chains: 'Chains',
  sa_chain_add: '+ New chain',
  sa_chain_empty: 'No chains yet — add the first one.',
  sa_chain_locations: 'locations',
  sa_chain_expires: 'expires',
  sa_chain_mrr_total: 'total MRR',
  sa_chain_branches: 'Branches',
  sa_chain_no_branches: 'No branches assigned yet.',
  sa_chain_menu_source: 'Set as shared menu source',
  sa_chain_select_restaurant: 'Select a restaurant...',
  sa_chain_add_branch: 'Add branch',
  sa_chain_admins: 'Admin access',
  sa_chain_no_admins: 'No admin granted access yet.',
  sa_chain_admin_email_ph: 'admin email...',
  sa_chain_grant_access: 'Grant access',
  sa_chain_name_required: 'Name is required',
  sa_chain_save: 'Save',
  sa_chain_delete_confirm: 'Delete this chain? Branches stay — just unlinked.',
  sa_chain_max_reached: 'Location limit reached for this chain plan.',
  sa_chain_remove_branch_confirm: 'Remove this branch from the chain?',
  sa_chain_admin_not_found: 'No admin account found with that email.',
  sa_chain_access_granted: 'Access granted',
  sa_chain_edit_title: 'Edit chain',
  sa_chain_new_title: 'New chain',
  sa_chain_name: 'Chain name',
  sa_chain_name_ph: 'e.g. Teddy Grill Group',
  sa_chain_max_locations: 'Max locations',
  sa_chain_price: 'Price (€ / period)',
  sa_chain_expires_label: 'Expires on',

  // Restaurants tab (SA)
  sa_rest_total: 'Total:',
  sa_rest_restaurants: 'restaurants',
  sa_rest_filtered: 'filtered from',
  sa_rest_new: 'New restaurant',
  sa_rest_search_ph: 'Search by name or slug...',
  sa_rest_filter_all: 'All',
  sa_rest_selected: 'selected',
  sa_rest_delete_selected: 'Delete selected',
  sa_rest_th_name: 'Restaurant',
  sa_rest_th_slug: 'Slug / URL',
  sa_rest_th_status: 'Status',
  sa_rest_th_currency: 'Currency',
  sa_rest_th_phone: 'Phone',
  sa_rest_th_actions: 'Actions',
  sa_rest_empty_search: 'No restaurant found with this search.',
  sa_rest_empty: 'No restaurants yet.',
  sa_rest_action_open: 'Open menu',
  sa_rest_action_edit: 'Edit restaurant',
  sa_rest_action_create_admin: 'Create admin account',
  sa_rest_action_fix_admin: 'Make existing profile admin',
  sa_rest_shop_disabled: 'Shop disabled',
  sa_rest_login_disabled: 'Login disabled',
  sa_rest_autorenew_badge: 'Auto',
  sa_rest_autorenew_title: 'This restaurant auto-renews monthly via PayPal',
  sa_plan_modal_autorenew_active: 'Auto-renew subscription active',
  sa_plans_sync_paypal: 'Sync with PayPal',
  sa_plans_sync_paypal_success: 'PayPal plans synced',
  sa_plans_sync_paypal_partial: 'Some plans synced, others failed:',

  // Pagination (SA)
  sa_page_of: 'of',

  // Clients tab (SA)
  sa_clients_label: 'clients',
  sa_clients_search_ph: 'Search by name, email, phone or zone...',
  sa_clients_refresh: 'Refresh',
  sa_clients_th_client: 'Client',
  sa_clients_th_email: 'Email',
  sa_clients_th_phone: 'Phone',
  sa_clients_th_zone: 'Zone',
  sa_clients_th_restaurant: 'Restaurant',
  sa_clients_th_orders: 'Orders',
  sa_clients_th_spent: 'Spent',
  sa_clients_th_registered: 'Registered',
  sa_clients_th_actions: 'Actions',
  sa_clients_filter_ordered: 'Ordered',
  sa_clients_filter_no_orders: 'No orders',
  sa_clients_empty_search: 'No client found.',
  sa_clients_empty: 'No registered clients yet. (Anonymous guest sessions — Wheel of Fortune, guest browsing — are excluded from this list.)',
  sa_clients_pwd_btn: 'Reset password',

  // Notes tab (SA)
  sa_notes_sub: 'Ideas and plans for program development. Saved locally.',
  sa_notes_new: '+ New note',
  sa_notes_edit_title: '️ Edit note',
  sa_notes_new_title: 'New note',
  sa_notes_title_ph: 'Title (optional)...',
  sa_notes_text_ph: 'Write your idea or plan here...',
  sa_notes_color: 'Color:',
  sa_notes_empty: 'No notes yet. Add your first idea!',

  // Support tab (SA)
  sa_support_open: 'open',
  sa_support_resolved: 'resolved',
  sa_support_refresh: '↻ Refresh',
  sa_support_search_ph: 'Search business...',
  sa_support_filter_all: 'All',
  sa_support_filter_open: 'Open',
  sa_support_filter_resolved: 'Resolved',
  sa_support_empty: 'No support requests.',
  sa_support_status_resolved: 'Resolved',
  sa_support_status_in_progress: 'In progress',
  sa_support_status_open: 'Open',
  sa_support_resolve_btn: 'Resolve',
  sa_support_you: 'You (Team)',
  sa_support_write_reply: 'Write reply...',
  sa_support_send: '↩ Send',
  sa_support_ticket_resolved: 'This request has been resolved',
  sa_support_restaurant_fallback: 'Restaurant',

  // Plans tab (SA)
  sa_plans_sub: 'Change limits, prices and features · Changes are reflected automatically on the website',
  sa_plans_by_plan: 'Restaurants by plan',
  sa_features_title: 'À la carte features',
  sa_features_desc: 'The same catalog the wizard and the admin panel\'s "Add features" picker sell individually — editing a price here changes it everywhere immediately. Features can\'t be deleted from here, only priced.',
  sa_plans_prices: 'Prices (EUR total for period)',
  sa_plans_limits: 'Limits',
  sa_plans_features: 'Features',
  sa_plans_no_limit: 'empty = no limit',
  sa_plans_max_products: 'Max Products',
  sa_plans_max_cats: 'Max Categories',
  sa_plans_max_orders: 'Max Orders/Month',
  sa_plans_feat_shop: 'Delivery & online ordering',
  sa_plans_feat_shop_sub: 'Orders screen (delivery) · Courier dashboard · online shop',
  sa_plans_feat_dinein: 'Table ordering',
  sa_plans_feat_dinein_sub: 'Orders screen (table / room) · per-table QR codes · room QR',
  sa_plans_feat_waiter: 'Waiter ordering',
  sa_plans_feat_waiter_sub: 'Orders screen (waiter) · Waiter dashboard · waiter accounts',
  sa_plans_feat_wheel_sub: 'Rewards tab · geofenced prize wheel, share-to-claim',
  sa_plans_feat_loyalty_sub: 'Rewards tab · visit-based loyalty card',
  sa_plans_feat_email: 'Win-back emails',
  sa_plans_feat_daily: 'Daily scratch card',
  sa_plans_feat_daily_sub: 'A daily thought guests scratch to reveal when they open the menu',

  sa_plans_feat_email_sub: 'Automated email to guests who\'ve gone quiet',
  sa_plans_feat_kitchen_note: 'Any ordering channel also shows the Kitchen dashboard.',
  sa_plans_feat_support: 'Live Support Chat (always active)',
  sa_plans_base_note: 'Free with every plan (not editable): digital menu · dishes & prices · product extras · business statistics · CSV export · customer accounts with reviews & likes.',
  sa_plans_price_1m: '1 Month (€/month)',
  sa_plans_price_3m: '3 Months total (€)',
  sa_plans_price_12m: 'Annual / 12 Months (€)',
  sa_plans_save_btn: 'Save',
  sa_plans_new_plan_btn: '+ New Plan',
  sa_plans_new_plan_slug_label: 'Plan ID (unique, lowercase, no spaces)',
  sa_plans_new_plan_slug_ph: 'e.g. premium',
  sa_plans_catalog: 'Catalog & display',
  sa_plans_label: 'Display name',
  sa_plans_icon: 'Icon (emoji)',
  sa_plans_color: 'Color',
  sa_plans_sort_order: 'Sort order',
  sa_plans_is_popular: 'Mark as "Most Popular"',
  sa_plans_is_public: 'Show on public pricing page',
  sa_plans_public_toggle_on: 'Visible — shown on the pricing page and the admin upgrade screen',
  sa_plans_public_toggle_off: 'Hidden — not shown on the pricing page or the admin upgrade screen',
  sa_plans_delete_btn: 'Delete plan',
  sa_plans_delete_confirm: 'Delete the "{name}" plan? This can\'t be undone.',
  sa_plans_delete_in_use: 'Can\'t delete — {count} restaurant(s) are still on this plan.',
  sa_plans_show_price: 'Show price (off = "Contact us" card)',
  sa_plans_trial_days: 'Free trial (days, 0 = none)',
  sa_plans_wheel_enabled: 'Includes Wheel of Fortune',
  sa_plans_loyalty_enabled: 'Includes repeat-customer rewards',
  sa_plans_loyalty_free_with_delivery: 'Free — included automatically with delivery',
  sa_plans_includes_plan: '"Everything from" plan',
  sa_plans_includes_plan_none: '— none —',
  sa_plans_description: 'Description (English)',
  sa_plans_description_al: 'Description (Albanian)',

  // Plan modal (SA)
  sa_plan_modal_plan: 'Plan',
  sa_plan_custom_price: 'Custom price',
  sa_plan_custom_price_hint: 'Negotiated monthly price for this restaurant — not shown publicly, only used for their own renewal (incl. PayPal).',
  sa_plan_modal_expires: 'Expiry Date (leave empty = no expiry)',
  sa_plan_modal_notes: 'Payment Notes',
  sa_plan_modal_notes_ph: 'e.g. Paid cash €29 for June 2025',
  sa_plan_modal_history: 'History',
  sa_plan_modal_by: 'by',
  sa_plan_modal_save: 'Save Plan',
  sa_plan_modal_saving: 'Saving...',
  sa_plan_option_month: '/month',
  sa_plan_modal_payments: 'Payments',
  sa_plan_modal_cancellations: 'Cancellations',
  sa_cancel_status_cancelled: 'Cancelled',
  sa_cancel_status_suspended: 'Suspended (payment failure)',
  sa_cancel_status_expired: 'Expired',
  sa_cancel_months_active: 'months active',

  // Password modal (SA)
  sa_pwd_modal_title: 'Change password',
  sa_pwd_modal_client_label: 'Client',
  sa_pwd_modal_field: 'New password (min 6 characters)',
  sa_pwd_modal_min: 'Minimum 6 characters',
  sa_pwd_modal_valid: 'Password is valid',
  sa_pwd_modal_no_key: 'Service key missing — change not possible.',
  sa_pwd_modal_save: 'Change',
  sa_pwd_modal_saving: 'Changing...',

  // SA toast messages
  sa_toast_config_saved: 'Configuration saved',
  sa_toast_plan_changed: 'Plan changed to',
  sa_toast_pwd_changed: 'Password changed',
  sa_toast_rest_deleted: 'deleted',
  sa_toast_rests_deleted: 'restaurants deleted',
  sa_toast_rests_deleted_partial: '{ok} deleted, {failed} could not be deleted',
  sa_toast_account_created: 'Account for',
  sa_toast_account_created2: 'created',
  sa_toast_profile_fixed: 'Profile set as admin',
  sa_toast_note_error: 'Error: ',
  sa_toast_upload_error: 'Upload error: ',
  sa_toast_error: 'Error: ',
  sa_toast_service_key_missing: 'Service key missing',
  sa_toast_pwd_min_chars: 'Password must be at least 6 characters',
  sa_toast_account_failed: 'Account creation failed:',
  sa_toast_profile_not_found: 'Profile not found — create the account with the button',
  sa_toast_ticket_resolved: 'Request marked as resolved',
  sa_toast_unknown: 'unknown',
  sa_support_resolve_msg: 'This request was marked as resolved. If you have other questions, open a new request.',
  sa_confirm_delete_rest_prefix: 'Delete',
  sa_confirm_delete_rest: 'This deletes everything (menu, categories, orders). Irreversible!',
  sa_confirm_delete_selected_prefix: 'Delete',
  sa_confirm_delete_selected: 'selected restaurants? Irreversible!',
  sa_pwd_prompt: 'Password for (min 6 characters)',
  sa_page_label: 'Page',

  // ── SA Restaurant Form ──
  sa_form_back: '← Back',
  sa_form_edit_title: 'Edit:',
  sa_form_new_title: 'New Restaurant',
  sa_form_saving: 'Saving...',
  sa_form_save: 'Save changes',
  sa_form_create: 'Create restaurant',
  sa_form_sec_identity: '️ Restaurant identity',
  sa_form_logo_label: 'Logo / Icon',
  sa_form_uploading: '⏳ Uploading...',
  sa_form_upload_btn: 'Upload image',
  sa_form_upload_hint: 'PNG or JPG · Recommended 256×256 px',
  sa_form_upload_error: 'Upload error: ',
  sa_form_name_label: 'Restaurant name *',
  sa_form_slug_label: 'Slug / URL *',
  sa_form_public_address: 'Public URL: ',
  sa_form_tagline_label: 'Tagline / Motto',
  sa_form_sec_business: 'Business details',
  sa_form_nipt_label: 'Tax ID (NIPT)',
  sa_form_nipt_hint: 'Tax identification number',
  sa_form_currency_label: 'Currency',
  sa_form_phone_label: 'Phone number',
  sa_form_hours_label: 'Opening hours',
  sa_form_address_label: 'Address',
  sa_form_sec_account: 'Restaurant admin account',
  sa_form_admin_email_label: 'Admin email',
  sa_form_admin_pwd_label: 'Password (min 6 characters)',
  sa_form_account_auto_create: 'Account will be created automatically when the restaurant is saved.',
  sa_form_account_hint: 'Enter email and password to create the admin account.',
  sa_form_account_exists: 'Admin account exists',
  sa_form_change_email: '️ Change email',
  sa_form_new_email_placeholder: 'New email (current: ',
  sa_form_change_btn: 'Change',
  sa_form_change_pwd: 'Change password',
  sa_form_new_pwd_placeholder: 'New password (min 6 characters)',
  sa_form_min_chars: 'Minimum 6 characters',
  sa_form_account_not_created: '⏳ Admin account not yet created',
  sa_form_account_create_hint: 'After saving, create the account with the button from the restaurant list.',
  sa_form_name_slug_required: 'Name and slug are required',
  sa_form_rest_updated: 'Restaurant updated',
  sa_form_rest_created: 'Restaurant created',
  sa_form_rest_created_with_account: 'Restaurant created and admin account set up',
  sa_form_account_failed: 'Account creation failed: ',
  sa_form_pwd_changed: 'Password changed',
  sa_form_email_changed: 'Email changed',
  sa_form_sec_payment: '💳 Payment received',
  sa_form_payment_hint: "What the client actually paid to get this plan — most manually-added clients will be free, so this defaults to 0. This is recorded in Finance either way, so free signups stay visible instead of disappearing from the books.",
  sa_form_payment_amount: 'Amount paid (€)',
  sa_form_payment_amount_hint: 'Leave at 0 if this client is free.',
  sa_form_payment_period: 'Billing period',
  sa_form_sec_activation: '⏰ Timed activation',
  sa_form_optional: '— optional',
  sa_form_active_from: 'Active from date',
  sa_form_active_until: 'Active until date',
  sa_form_starts: '▶ Starts: ',
  sa_form_ends: '⏹ Ends: ',
  sa_form_clear_dates: 'Clear dates',
  sa_form_sec_access: 'Access & settings',
  sa_form_toggle_active: 'Restaurant active',
  sa_form_toggle_active_on: 'Customers can see the menu and place orders',
  sa_form_toggle_active_off: 'Restaurant is not visible to customers',
  sa_form_sec_chain: 'Chain',
  sa_form_chain_shared_menu: 'uses the chain\'s shared menu',
  sa_form_chain_hint: 'Manage branches, shared menu, and admin access from the Chains tab.',
  sa_form_sec_plan: 'Plan & extras',
  sa_form_business_type: 'Business type',
  sa_form_business_type_unset: 'Not set',
  sa_form_business_type_hint: 'Each type gets its own feature set (e.g. no delivery for Cafe/Restaurant/Hotel) and is used to break down MRR and QR-menu conversion in Finance. "Other" / "Not set" removes the limits — use it for a deliberate exception.',
  sa_form_not_for_type: 'Not offered for this business type (pick "Other" to override)',
  sa_form_signup_source: 'Signup source',
  sa_form_signup_source_hint: 'How this restaurant found Assaggioo — shown in Growth.',
  sa_form_extras_hint: 'Picking a plan pre-fills what it includes — toggle any extra on or off individually below.',
  sa_form_toggle_wheel: 'Wheel of Fortune',
  sa_form_toggle_wheel_on: 'Customers can spin for a prize',
  sa_form_toggle_wheel_off: 'Not available to customers',
  sa_form_toggle_dinein: 'Dine-in / table QR codes',
  sa_form_toggle_dinein_on: 'Table QR codes enabled',
  sa_form_toggle_dinein_off: 'Not available',
  sa_form_toggle_waiter: 'Waiter ordering',
  sa_form_toggle_waiter_on: 'Waiter accounts can be created',
  sa_form_toggle_waiter_off: 'Not available',
  sa_form_toggle_listed: 'Listed on /discover',
  sa_form_toggle_listed_on: 'Visible in the public directory',
  sa_form_toggle_listed_off: 'Hidden from the public directory',
  sa_form_toggle_loyalty: 'Loyalty Card',
  sa_form_toggle_loyalty_on: 'Visit-based rewards are active',
  sa_form_toggle_loyalty_off: 'Not available to customers',
  sa_form_loyalty_free_with_delivery: 'Included free — comes with delivery',
  sa_form_plan_history_created: 'Restaurant created with this plan',
  sa_form_base_includes: 'Free with every plan',
  sa_form_plan_adds: 'This plan also adds',
  sa_form_plan_custom_hint: 'Custom plan — turn on exactly the add-ons this restaurant is paying for.',
  sa_form_inc_menu: 'Digital menu · dishes & prices',
  sa_form_inc_extras: 'Product extras',
  sa_form_inc_stats: 'Business statistics',
  sa_form_inc_csv: 'CSV export',
  sa_form_inc_reviews_likes: 'Reviews + likes',
  sa_form_inc_branding: 'Custom branding',
  sa_form_bundled_note: 'An ordering channel also turns on the Orders screen and the matching staff dashboard (courier / waiter). Customer accounts, reviews, likes and CSV export are free with every plan.',
  sa_form_toggle_delivery: 'Delivery & online ordering',
  sa_form_toggle_delivery_on: 'Delivery orders + courier dashboard',
  sa_form_toggle_delivery_off: 'No delivery',
  sa_form_toggle_accounts: 'Customer accounts',
  sa_form_toggle_email: 'Win-back emails',
  sa_form_toggle_email_on: 'Automated win-back campaigns',
  sa_form_toggle_email_off: 'Not available',
  sa_form_toggle_daily: 'Daily scratch card',
  sa_form_toggle_daily_on: 'Guests see a scratch card once a day',
  sa_form_toggle_daily_off: 'Not available',


  // ── Trial / subscription ──
  trial_soon_title: "We're updating!",
  trial_soon_sub: "This restaurant is temporarily updating to offer you an even better experience. We'll be back soon!",
  trial_expired_overlay_title: 'Your free trial has ended',
  trial_expired_overlay_sub: 'Your 7-day trial period has expired. Choose a plan to keep your restaurant online and continue using Assaggioo.',
  trial_expired_upgrade_btn: 'View Plans',
  trial_expired_contact: 'Need help? Contact us at assaggioo1@gmail.com',
  trial_days_left: 'day(s) left in your free trial',
  trial_upgrade_now: 'Upgrade Now',
  reg_check_email_title: 'Check your inbox!',
  reg_check_email_sub: 'We sent a confirmation link to',
  reg_check_email_hint: 'After clicking the link, you will be taken to complete your restaurant setup.',
  reg_check_email_spam: "Didn't receive the email? Check your spam folder.",
  reg_go_login: 'Go to Login',
  reg_phone_required: 'Phone number is required',
  reg_address_required: 'Address is required',
  reg_create_account: 'Create Account',
  reg_terms_prefix: 'By continuing, you accept our',
  reg_terms_and: 'and',
  reg_wizard_hint: 'Not sure what features you need?',
  reg_wizard_link: 'Try the Package Builder, step by step →',
  reg_trial_note: '7-day free trial · No payment required',

  // ── Admin Plans tab ──
  adm_tab_plans: 'Plans',
  adm_tab_bills: 'Bills',
  adm_bills_header: 'Billing history',
  adm_bills_autorenew_active: 'Auto-renew active',
  adm_bills_autorenew_desc: 'Your plan renews automatically every month.',
  adm_bills_autorenew_cancel: 'Cancel auto-renew',
  adm_paymethod_title: 'Payment method',
  adm_paymethod_desc: 'Saved so you don\'t have to reconnect it every time you renew or add something.',
  adm_paymethod_connected: 'Connected',
  adm_paymethod_remove: 'Remove',
  adm_paymethod_add: 'Add payment method',
  adm_paymethod_empty: 'No payment method saved yet. Click "Add payment method" to connect PayPal with auto-renew.',
  adm_paymethod_add_notice: 'Connecting with auto-renew on saves this as your payment method.',
  adm_paymethod_replace_notice: 'You already have a connected payment method — completing this will replace it with the new one and cancel the old PayPal subscription. Your paid access isn\'t affected.',
  adm_paymethod_remove_confirm: 'Remove this payment method from your account? This only removes it from view here — it does not cancel an active subscription (use "Cancel auto-renew" for that).',
  adm_paymethod_remove_confirm_active: 'This is the payment method your auto-renew is currently using — removing it will also cancel auto-renew (PayPal won\'t charge you again). You\'ll keep access until your current period ends. Continue?',
  adm_paymethod_removed_toast: 'Payment method removed.',
  adm_paymethod_removed_autorenew_toast: 'Payment method removed and auto-renew cancelled.',
  adm_bills_cancel_confirm: 'Cancel auto-renewal? You\'ll keep access until your current period ends, then you\'ll need to renew manually.',
  adm_bills_cancel_reason_label: 'Mind telling us why? (optional, helps us improve)',
  adm_bills_cancel_reason_ph: 'Select a reason...',
  adm_bills_cancel_success: 'Auto-renew cancelled.',
  adm_bills_empty: 'No payments yet.',
  adm_bills_download: 'Download invoice',
  adm_bills_type_one_time: 'Payment',
  adm_bills_type_sub_start: 'Subscription start',
  adm_bills_type_sub_renewal: 'Auto-renewal',
  adm_plans_header: 'Plans & Subscription',
  adm_plans_status_trial: 'Free trial',
  adm_plans_days_left: 'days left',
  adm_plans_status_active: 'Active plan',
  adm_plans_status_expired: 'Trial expired',
  adm_plans_active_until: 'Active until',
  adm_plans_popular: 'Most popular',
  adm_plans_current_badge: 'Current',
  adm_plans_upgrade_to: 'Upgrade to',
  adm_plans_renew: 'Renew now',
  adm_plans_processing: 'Redirecting...',
  adm_plans_activated: 'Plan activated! ✓',
  adm_plans_contact: 'Questions? Contact us at assaggioo1@gmail.com',
  pay_modal_title: 'Complete Your Upgrade',
  pay_modal_summary: 'Plan summary',
  pay_modal_promo_placeholder: 'Promo code',
  pay_modal_promo_apply: 'Apply',
  pay_modal_promo_applied: 'Code',
  pay_modal_promo_off: 'off',
  pay_modal_autorenew: 'Auto-renew monthly',
  pay_modal_autorenew_on: 'PayPal will charge you automatically each month — cancel anytime.',
  pay_modal_autorenew_off: 'One-time payment — you\'ll need to renew manually.',
  pay_modal_paypal: 'Pay with PayPal',
  pay_modal_not_configured: 'Payments not configured yet. Contact support.',
  pay_modal_success: '✓ Plan activated successfully!',
  pay_modal_error: 'Payment failed. Please try again.',
  adm_plans_select_period: 'Billing period',
  adm_plans_per_month: '/mo',
  adm_plans_total: 'total',
  adm_plans_save: 'Save',

  // ── À la carte onboarding wizard (/register/wizard) ──
  // Note: the feature cards' own name/description come from the feature_catalog table
  // (label_al/description_al — Albanian-only columns), so those stay Albanian in both
  // languages. Everything else in the wizard lives here.
  wiz_loading: 'Loading…',
  wiz_catalog_error: 'We couldn\'t load the features. {msg}',
  wiz_shortcut_link: "Don't need to customize? Start free with the classic form →",
  // Phone-width variant of the line above: at 375px the full sentence wraps to three
  // lines inside the sticky header and eats ~50px of vertical space on every step.
  wiz_shortcut_link_short: 'Classic signup →',
  wiz_continue: 'Continue',
  wiz_step_of: 'Step {n} of {total}',
  wiz_finishing: 'We\'re finishing your signup…',
  wiz_unexpected_error: 'An unexpected error occurred.',
  wiz_free: 'Free',
  wiz_free_lower: 'free',
  wiz_per_month_long: '/ month',
  wiz_per_month: '/month',

  // business types
  wiz_bt_cafe: 'Cafe',
  wiz_bt_restaurant: 'Restaurant',
  wiz_bt_fastfood: 'Fast-Food',
  wiz_bt_mensa: 'Canteen',
  wiz_bt_hotel: 'Hotel',

  // step 1 — business type
  wiz_type_title: 'What kind of business do you have?',
  wiz_type_sub: 'Each type of business gets its own set of features — we only show the ones that fit yours.',
  bp_summary_cafe: 'Cafe: QR menu, table ordering, waiters, Lucky Bite and daily card. No delivery.',
  bp_summary_mensa: 'Canteen: QR menu and delivery, with the loyalty card included free.',
  bp_summary_fastfood: 'Fast-Food: QR menu and delivery, with the loyalty card included free, plus win-back emails.',
  bp_summary_restaurant: 'Restaurant: QR menu, table ordering, waiters and table reservations, plus Lucky Bite, win-back emails and daily card. No delivery.',
  bp_summary_hotel: 'Hotel: QR menu, room service from the guest\'s phone, waiters and restaurant reservations.',
  bp_hotel_roomservice_label: 'Room service by QR',
  bp_hotel_roomservice_desc: 'Guests scan the code in their room, order from their phone and the kitchen sends it up. Rooms and restaurant tables can live side by side.',
  wiz_feat_reco: '★ Recommended for your business',
  setup_biz_type: 'Type of business',
  setup_biz_type_ph: 'Choose…',
  setup_biz_type_required: 'Choose what kind of business you run',
  setup_plan_mismatch: 'The {plan} package includes features that don\'t exist for a {type} business. Start with the free Starter plan and add exactly what fits you.',
  setup_plan_use_starter: 'Use Starter instead',
  adm_addon_not_for_type: 'Not available for your type of business',

  // step 2 — problems
  wiz_problem_title: 'What problems are you facing?',
  wiz_problem_sub: 'Pick one or more — it helps us understand what you need.',
  wiz_problem_queue: 'Customers wait in long queues',
  wiz_problem_printed: 'My menu is printed/outdated',
  wiz_problem_online: 'I want more online orders',
  wiz_problem_know: 'I want to know my customers',
  wiz_problem_churn: 'I lose customers who never come back',
  wiz_problem_other: 'Other',
  wiz_problem_freetext: 'Tell us in a few words...',

  // feature steps
  wiz_feat_step_of: 'Feature {n} of {total}',
  wiz_feat_placeholder_tag: 'illustration placeholder',
  wiz_feat_always: '✓ Always included',
  wiz_feat_bundle_included: 'Included free:',
  wiz_feat_subtotal: 'So far',

  // bill
  wiz_bill_topbar: 'Bill',
  wiz_bill_title: 'Your bill',
  wiz_period_1m: 'Monthly',
  wiz_period_3m: '3 Months',
  wiz_period_12m: 'Yearly',
  wiz_cycle_1m: 'every month',
  wiz_cycle_3m: 'every 3 months',
  wiz_cycle_12m: 'every year',
  wiz_billed: 'billed {cycle}',
  wiz_total_discount: ' · total discount ~{pct}%',
  wiz_go_to_payment: 'Continue to Payment',
  wiz_go_to_account: 'Create Your Account',
  wiz_session_banner_1: 'Continuing as ',
  wiz_session_banner_2: '. Not you?',
  wiz_session_banner_fallback: 'your existing account',
  wiz_session_logout_link: 'Log out and start fresh',
  wiz_session_logout_busy: 'Logging out…',

  // promo code
  wiz_promo_placeholder: 'Promo code',
  wiz_promo_apply: 'Apply',
  wiz_promo_invalid: 'Invalid code.',
  wiz_promo_inactive: 'This code is no longer active.',
  wiz_promo_expired: 'This code has expired.',
  wiz_promo_plan_only: 'This code is only valid for: {plans}.',
  wiz_promo_period_only: 'This code is only valid for a {periods} commitment.',
  wiz_promo_applied: '✓ Code applied — extra −{pct}%',
  wiz_promo_or: 'or',
  wiz_promo_plan_wizard: 'custom package (wizard)',
  wiz_commit_1m: '1-month',
  wiz_commit_3m: '3-month',
  wiz_commit_12m: '12-month',

  // account step
  wiz_acc_topbar: 'Account',
  wiz_acc_topbar_yours: 'Your account',
  wiz_acc_title: 'Create your account',
  wiz_acc_sub: 'Create your account — the next step is the payment that activates your restaurant.',
  wiz_acc_biz_name: 'Business name *',
  wiz_acc_first: 'First name *',
  wiz_acc_last: 'Last name *',
  wiz_acc_phone: 'Phone *',
  wiz_acc_email: 'Email *',
  wiz_acc_password: 'Password *',
  wiz_acc_password2: 'Confirm password *',
  wiz_acc_busy: 'Continuing...',
  wiz_acc_going_payment: 'Taking you to the payment…',
  wiz_acc_check_email_title: 'Check your email',
  wiz_acc_check_email_body_1: 'We sent a confirmation link to ',
  wiz_acc_check_email_body_2: '. As soon as you confirm it you\'ll come back here automatically and the restaurant will be created.',
  wiz_acc_taken_title: 'This email already exists',
  wiz_acc_taken_sub_1: 'There is already an account with ',
  wiz_acc_taken_sub_2: '. Enter the password to continue.',
  wiz_acc_taken_continue: 'Continue and create the restaurant',
  wiz_acc_taken_other: 'Use a different email',
  wiz_acc_wrong_password: 'Wrong password.',
  wiz_err_biz_required: 'Business name is required',
  wiz_err_first_required: 'First name is required',
  wiz_err_last_required: 'Last name is required',
  wiz_err_phone_required: 'Phone is required',
  wiz_err_email_invalid: 'Invalid email',
  wiz_err_password_short: 'Password must be at least 6 characters',
  wiz_err_password_mismatch: 'Passwords do not match',
  wiz_err_password_required: 'Password is required',

  // payment step
  wiz_pay_topbar: 'Payment',
  wiz_pay_title: 'Pay and Activate',
  wiz_pay_sub: 'This is a real purchase — not a free trial. The subscription is billed {cycle} until you cancel.',
  wiz_pay_cycle_note: 'billed {cycle} · renews automatically',
  wiz_pay_preparing: 'Preparing the payment…',
  wiz_pay_retry: 'Try Again',
  wiz_pay_paypal_error: 'Something went wrong with PayPal. Please try again.',
  wiz_pay_not_configured: 'Payments are not configured yet. Contact support.',
  wiz_pay_creating: 'Payment confirmed — we\'re creating your restaurant…',
  wiz_pay_session_expired: 'Your session has expired. Please sign in again.',
  wiz_pay_provision_failed: 'We couldn\'t prepare the payment.',
  wiz_pay_not_confirmed: 'The payment was not confirmed.',
  wiz_pay_partial_failure: 'PayPal confirmed the payment, but something went wrong while creating the restaurant. Contact support ({email}) with this subscription reference: {ref}',

  // done
  wiz_done_title: 'Welcome to Assaggioo!',
  wiz_done_sub: 'Payment confirmed — your restaurant is live. Taking you to your admin panel.',
};

const sq: typeof en = {
  lang_btn: 'EN',
  lang_label: 'English',

  // ── Common ──
  save: 'Ruaj',
  cancel: 'Anulo',
  back: 'Kthehu',
  close: 'Mbyll',
  loading: 'Duke ngarkuar...',
  free: 'Falas',
  error: 'Gabim',
  unknown_error: 'Gabim i panjohur. Provo sërish.',
  per_piece: '/ copë',
  notfound_title: 'Kjo faqe s\'është në menu',
  notfound_sub: 'Linku që ndoqe s\'ekziston ose është zhvendosur.',
  notfound_home_btn: '← Kthehu në faqen kryesore',

  // ── Nav / Landing ──
  nav_features: 'Funksionet',
  nav_how: 'Si Funksionon',
  nav_pricing: 'Çmimet',
  nav_register: 'Regjistrohu',
  nav_login: 'Kyçu',
  nav_open_admin: 'Paneli admin',
  nav_discover: 'Zbulo',
  nav_blog: 'Blog',

  // Discover page (drejtoria publike për klientë)
  discover_title: 'Diçka e re për të eksploruar',
  discover_subtitle: 'Shiko ambientin, ushqimin dhe menunë e plotë të vendeve pranë teje — pastaj vendos ku të shkosh.',
  discover_all_cities: 'Të gjitha qytetet',
  discover_all_types: 'Të gjitha',
  discover_type_cafe: 'Kafe',
  discover_type_restaurant: 'Restorant',
  discover_type_fastfood: 'Fast-Food',
  discover_type_mensa: 'Mensë',
  discover_type_other: 'Tjetër',
  discover_empty_none: 'Ende s\'ka restorante të listuara — kthehu së shpejti.',
  discover_empty_filtered: 'Asnjë përputhje për këtë filtër — provo një qytet ose lloj tjetër.',
  discover_geo_title: 'Të hahet diçka e re?',
  discover_geo_body: 'Na lejo të tregojmë çfarë ka afër teje — vendet më të afërta, drejt e në krye.',
  discover_geo_cta: 'Gjej vendet afër meje',
  discover_geo_loading: 'Duke gjetur vendndodhjen…',
  discover_geo_dismiss: 'Jo tani',
  discover_geo_sorted: 'Po shfaqim më të afërtat tek ti',
  discover_geo_clear: 'Pastro',

  // Profili — çelësi i listimit publik
  adm_profile_publicly_listed: 'Shfaqu në drejtorinë publike të Assaggioo-s',
  adm_profile_publicly_listed_hint: 'I lejon klientët të të zbulojnë te assaggioo.com/discover. Çaktivizoje për të mbetur i arritshëm vetëm përmes linkut/QR-it tënd.',

  // ── Landing hero ──
  hero_title: 'Menu dixhitale për restorantin tënd',
  hero_subtitle: 'QR menu, porosi online dhe panel administrimi — gjithçka në një platformë të vetme.',
  hero_cta: 'Fillo falas, 7 ditë',
  hero_demo: 'Shiko demo',

  // ── Landing features section ──
  feat_title: 'Gjithçka që i nevojitet restorantit tuaj',
  feat_subtitle: 'Mjete të fuqishme për të dixhitalizuar restorantin tuaj brenda 5 minutave',

  // ── Landing how-it-works ──
  how_title: 'Si funksionon',
  how_sub: 'Tre hapa të thjeshtë',
  how_step1_title: 'Krijo llogarinë',
  how_step1_desc: 'Regjistrohu brenda 2 minutave. Nuk kërkohet kartë krediti.',
  how_step2_title: 'Shto menunë',
  how_step2_desc: 'Shto kategoritë, pjatat, fotot dhe çmimet.',
  how_step3_title: 'Printo QR kodin',
  how_step3_desc: 'Vendos QR kodin në tavolina. Klientët skanojnë dhe porosisin.',

  // ── Landing pricing ──
  pricing_title: 'Çmime transparente',
  pricing_sub: 'Pa tarifa të fshehura, pa kontratë dhe pa komision mbi porositë e tua.',
  period_1m: '1 Muaj',
  period_3m: '3 Muaj',
  period_12m: 'Vjetore',
  per_month: '/muaj',
  billed_as: 'Faturuar',
  most_popular: '⭐ Më i Popullarizuar',
  save_pct: 'Kursej',
  plan_cta_trial_prefix: 'Fillo',
  plan_cta_trial_suffix: 'ditë provë',
  plan_cta_get_started: 'Fillo Tani',
  plan_cta_contact: 'Na kontakto',

  // ── Landing FAQ ──
  faq_title: 'Pyetje të shpeshta.',

  // ── Landing CTA section ──
  cta_title: 'Gati ta provosh?',
  cta_sub: 'Ngrije sot menunë tënde me QR, falas për 7 ditë. Pa kartë krediti, pa kontratë — dhe pastaj vendos vetë.',
  cta_btn: 'Fillo falas →',
  cta_hint: 'Pa kartë krediti · Anulo kur të duash · Ndihmë me ngritjen',

  // ── Lead capture form (primary conversion) ──
  nav_talk: 'Kërko demo',
  lead_cta_primary: 'Kërko një demo falas',
  lead_cta_hero_reassure: 'Pa kartë krediti · pa kontratë · menunë e parë e ndërtojmë ne',
  lead_form_title: 'Na trego për lokalin tënd. Menunë e parë e ndërtojmë ne për ty.',
  lead_form_sub: 'Një telefonatë e shkurtër, pa asnjë detyrim. E ndërtojmë menunë nga një foto e menusë tënde me letër dhe të dërgojmë linkun e restorantit tënd, live.',
  lead_form_name: 'Emri yt',
  lead_form_restaurant: 'Emri i restorantit / kafenesë',
  lead_form_phone: 'Telefoni (WhatsApp)',
  lead_form_city: 'Qyteti',
  lead_form_type: 'Lloji i lokalit (opsional)',
  lead_form_type_cafe: 'Kafene / bar',
  lead_form_type_rest: 'Restorant',
  lead_form_type_fast: 'Fast-food',
  lead_form_type_mensa: 'Mensë',
  lead_form_type_hotel: 'Hotel',
  lead_form_message: 'Diçka që do të na thuash (opsionale)',
  lead_form_submit: 'Kërko demo',
  lead_form_sending: 'Po dërgohet…',
  lead_form_wa: 'Ose na kontakto me WhatsApp ose email — si të jetë më e lehtë për ty.',
  lead_form_self: 'Preferon ta shohësh vetë? Fillo falas, 7 ditë →',
  lead_form_success_title: 'U mor, {name} 🎉',
  lead_form_success_body: 'Do të të kontaktojmë brenda pak orësh për menunë e {restaurant}.',
  lead_form_success_wa: 'Na shkruaj tani në WhatsApp →',
  lead_form_err_required: 'Emri, restoranti, telefoni dhe qyteti janë të gjitha të detyrueshme.',
  lead_form_err_generic: 'Diçka shkoi keq. Provo sërish ose na shkruaj në WhatsApp.',
  lead_band_title: 'Duket mirë? Kërko një demo dhe ta ndërtojmë për restorantin tënd.',
  lead_band_cta: 'Kërko një demo falas',

  // ── Landing footer ──
  footer_product: 'Produkti',
  footer_contact: 'Kontakti',
  footer_rights: 'Të gjitha të drejtat të rezervuara.',

  // ── Faqja hyrëse ──
  // Hero
  lp_hero_pill: 'Menu me QR · porosi online · shpërblime që i kthejnë klientët',
  lp_hero_title_1: 'Klientët që ke tashmë —',
  lp_hero_title_2: 'të kthehen më shpesh.',
  lp_hero_sub: 'Menu me QR, porosi pa komision, dhe Rrota e Fatit e karta e besnikërisë që i kthejnë klientët e rastit në klientë të rregullt.',
  lp_hero_cta_demo: 'Kërko një demo falas',
  lp_hero_see_menu: 'Shiko një menu të vërtetë →',
  // Karta Starter (zëvendëson bandën e vjetër nën features)
  lp_starter_tag: 'Çmim i thjeshtë',
  lp_starter_name: 'Starter',
  lp_starter_price_note: '/muaj',
  lp_starter_free: '7 ditë falas — pa kartë',
  lp_starter_li1: 'Menuja jote me QR, live në linkun e vet',
  lp_starter_li2: 'Pjata, foto dhe kategori pa limit',
  lp_starter_li3: 'Gjithçka e ndryshon vetë, kur të duash',
  lp_starter_cta: 'Fillo falas',
  lp_starter_wizard: 'ose ndërto vetë paketën tënde, veçori pas veçorie →',
  lp_starter_scan: 'Skano për të parë një menu live',
  lp_hero_watch: 'Shiko si funksionon',
  lp_trust_text: 'Platformë e re, e pavarur — pa kontratë, e ndal kur të duash.',
  lp_stat_noapp: 'Pa aplikacion për klientin',
  lp_stat_fiscal: 'Punon krah POS-it tënd',
  lp_stat_commission: 'Zero komision në çdo porosi',
  lp_stat_setup: 'Online po atë ditë',

  // Për kë është
  lp_who_tag: 'Për kë është',
  lp_who_title_1: 'Nëse te ti hahet,',
  lp_who_title_2: 'të bën punë.',
  lp_who_sub: 'Pesë lloje biznesi, një platformë — ndez vetëm atë që i duhet sallës sate dhe nuk paguan për asgjë tjetër.',
  lp_who_cafe_title: 'Kafene',
  lp_who_cafe_desc: 'Menu me QR mbi tavolinë, dhe klientë të rregullt që kthehen.',
  lp_who_rest_title: 'Restorant',
  lp_who_rest_desc: 'Klienti porosit nga tavolina; kamarierët dhe kuzhina qëndrojnë në sinkron.',
  lp_who_fast_title: 'Fast-food',
  lp_who_fast_desc: 'Porosi për dërgesë nga menuja, të gjurmuara deri te dera — pa komision platforme.',
  lp_who_mensa_title: 'Mensë',
  lp_who_mensa_desc: 'Menuja e ditës e përditësuar në sekonda — pa printuar e riprintuar.',
  lp_who_hotel_title: 'Hotel',
  lp_who_hotel_desc: 'Room service me të njëjtin QR — numri i dhomës zë vendin e tavolinës.',

  // Problemi — mbahet vetëm për kopjen e krahasimit të paketave në AdminPage; faqja
  // hyrëse s'e rendon më si seksion (u fut te nën-titulli i seksionit të mëposhtëm).
  lp_prob_tag: 'Problemi i vërtetë',
  lp_prob_title_1: 'Problemi s\'është menuja.',
  lp_prob_title_2: 'Është që klienti të harron.',
  lp_prob_sub: 'Menuja vetëm tregon çmimet. Pasi klienti del, asgjë s\'e kthen.',
  lp_prob_1_title: 'Një mbrëmje plot, tjetrën bosh',
  lp_prob_1_desc: 'Askush s\'ka arsye ta mbajë mend lokalin mes një vizite dhe tjetrës.',
  lp_prob_2_title: 'S\'e di kush është klient i rregullt',
  lp_prob_2_desc: 'Fytyrat i njeh, jo sa herë erdhi secili — as cili nuk erdhi më.',
  lp_prob_3_title: 'Reklama kushton më shumë se sa kthen',
  lp_prob_3_desc: 'Ata që kaluan tashmë nga dera s\'të kushtojnë pothuajse asgjë për t\'i rikthyer.',

  // Ndryshimi — mekanikat që i kthejnë klientët (Problemi futet te lp_ret_sub)
  lp_ret_tag: 'Ndryshimi',
  lp_ret_title_1: 'Menuja s\'ishte kurrë problemi.',
  lp_ret_title_2: 'Të harrojnë — kjo është.',
  lp_ret_sub: 'E printuar apo dixhitale, menuja vetëm tregon çmimet. Pasi klienti del nga dera, asgjë s\'e kthen — veç nëse i jep një arsye.',
  lp_ret_wheel_title: 'Lucky Bite — Rrota e Fatit',
  lp_ret_wheel_desc: 'Shfaqet vetëm për klientët brenda lokalit tënd — rrotullojnë dhe dalin me një kod real që e shfrytëzojnë te arka.',
  lp_ret_loyalty_title: 'Karta e besnikërisë',
  lp_ret_loyalty_desc: 'Ti cakton vizitat dhe shpërblimin; kur klienti e arrin, merr një kod që stafi yt e verifikon te arka.',
  lp_ret_email_title: 'Emaile ri-kthimi',
  lp_ret_email_desc: 'Klienti që s\'ka porositur prej dy javësh merr një email nga ti — një pjatë e re, një ofertë, ose thjesht "na ke munguar" — dhe ti sheh sa u kthyen.',
  lp_ret_wof_lead: 'Lucky Bite, hap pas hapi:',
  lp_wof_step1_title: 'Del rrota',
  lp_wof_step1_desc: 'Klienti hap menunë brenda lokalit dhe rrota shfaqet.',
  lp_wof_step2_title: 'Rrotullon, pastaj ndan',
  lp_wof_step2_desc: 'Rrotullon për një çmim dhe e ndan për ta zhbllokuar.',
  lp_wof_step3_title: 'E shfrytëzon në lokal',
  lp_wof_step3_desc: 'Çmimi bëhet një kod real që stafi yt e verifikon te arka.',
  lp_feat_learn_more: 'Shiko si funksionon →',

  // Funksionet — tani seksioni kryesor i faqes
  lp_sec_features_tag: 'Çfarë bën',
  lp_sec_features_title_1: 'Gjithçka që bën,',
  lp_sec_features_title_2: 'dhe si e rrit biznesin.',
  lp_sec_features_sub: 'Kliko cilindo për të parë si funksionon në detaje.',
  lp_feat_qr_title: 'Menu me kod QR',
  lp_feat_qr_desc: 'Klienti skanon dhe e sheh menjëherë; fotot, çmimet dhe tekstin i ndryshon në sekonda.',
  lp_feat_orders_title: 'Porosi nga tavolina & online',
  lp_feat_orders_desc: 'Klienti porosit me QR-in e tavolinës ose për dërgesë — çdo porosi hyn live në panel.',
  lp_feat_staff_title: 'Kamarierët & kuzhina',
  lp_feat_staff_desc: 'Kamarierët dërgojnë porositë nga telefoni; kuzhina i shënon "gati" në ekranin e saj.',
  lp_feat_courier_title: 'Korrierët & gjurmimi',
  lp_feat_courier_desc: 'Korrierin e cakton nga paneli; merr hartën, dhe klienti e ndjek dërgesën live.',
  lp_feat_clients_title: 'Llogari klientësh & vlerësime',
  lp_feat_clients_desc: 'Klientët marrin llogari, historikun e porosive, dhe vlerësojnë ose komentojnë pjatat.',
  lp_feat_stats_title: 'Statistika',
  lp_feat_stats_desc: 'Sheh cilat pjata shiten, në cilat orë, dhe të ardhurat ditore — eksportohen në CSV.',

  // Rreshti i besimit nën hero
  lp_trust_new: 'I ri — ndërtuar me restorantet e para që e përdorin. Pa numra të fryrë këtu.',

  // Problemi (3 pika, para se të flitet fare për produktin)
  lp_problem_tag: 'Problemi real',
  lp_problem_title: 'Plot të premten. Bosh të martën.',
  lp_problem_sub: 'Ushqimi rrallë është problemi. Problemi është që kur klienti largohet, asgjë s\'e sjell ndër mend sërish.',
  lp_problem_1_q: '"I pëlqeu — dhe s\'u kthye kurrë."',
  lp_problem_1_p: 'Një vizitë e mirë e parë pa arsye të dytë është thjesht një mostër e shtrenjtë.',
  lp_problem_2_q: '"Klientët e rregullt i njoh nga fytyra."',
  lp_problem_2_p: 'Dhe asgjë tjetër. Jo sa shpesh vijnë, jo sa shpenzojnë, jo kush ka pushuar së ardhuri pa zhurmë.',
  lp_problem_3_q: '"Çdo klient i ri kushton më shumë se i fundit."',
  lp_problem_3_p: 'Reklamat dhe aplikacionet e dërgesës të japin me qira të huaj. Klienti më i lirë për ta rifituar është ai që tashmë e pëlqeu vendin tënd.',

  // Diferenca — mbështjellësi i seksionit (kartat përdorin lp_ret_*)
  lp_diff_tag: 'Çfarë e bën Assaggioo ndryshe',
  lp_diff_title: 'Tre mekanizma që kthejnë një vizitë në zakon.',
  lp_diff_sub: 'Çdo mjet restoranti dixhitalizon menunë. Këto janë pjesët që ndryshojnë sa shpesh kthehet klienti.',

  // Gjithçka tjetër — listë kompakte (përdor titujt/përshkrimet e lp_feat_*)
  lp_rest_tag: 'Dhe bazat, si duhet',
  lp_rest_title: 'Po, mbulon edhe gjithçka tjetër.',

  // Çmimi — 4 pako me çmim fiks nga plan_configs (Menu/Counter/Regulars/Everything)
  lp_pricing_tag: 'Çmimi',
  lp_pricing_title: 'Fillo me menunë. Shto çka e rrit biznesin.',
  lp_pricing_sub: '7 ditë provë falas për të filluar. Pa kartë për të filluar. Anulo vetë nga paneli, kur të duash.',
  lp_pricing_monthly: 'Mujore',
  lp_pricing_3m: '3 muaj',
  lp_pricing_annual: 'Vjetore',
  lp_bundle_menu_for: 'Do vetëm një menu online, gjithmonë e përditësuar.',
  lp_bundle_menu_li1: 'Menu QR, pjata & foto pa limit',
  lp_bundle_menu_li2: 'Menu në anglisht & shqip, ndërro me një prekje',
  lp_bundle_menu_li3: 'Llogari klientësh, vlerësime & pëlqime',
  lp_bundle_menu_li4: 'Statistika & eksport CSV',
  lp_bundle_counter_for: 'Fast-food & delivery-first. Zotëro çdo porosi.',
  lp_bundle_counter_li1: 'Gjithçka te Menu',
  lp_bundle_counter_li2: 'Delivery & marrje, 0% komision',
  lp_bundle_counter_li3: 'Porosi nga tavolina & kamarieri',
  lp_bundle_counter_li4: 'Kartë besnikërie, falas me delivery-n',
  lp_bundle_regulars_for: 'Kafene, bare & restorante që duan klientë të kthyer.',
  lp_bundle_regulars_li1: 'Gjithçka te Menu',
  lp_bundle_regulars_li2: 'Rrota Lucky Bite',
  lp_bundle_regulars_li3: 'Kartë besnikërie që shpërblen klientët e kthyer',
  lp_bundle_regulars_li4: 'Emaile rikthimi për t\'i sjellë klientët përsëri',
  lp_bundle_everything_for: 'Lokale të konsoliduara që punojnë çdo kanal.',
  lp_bundle_everything_li1: 'Gjithçka te Counter & Regulars',
  lp_bundle_everything_li2: 'Delivery, tavolinë & kamarier — çdo kanal',
  lp_bundle_everything_li3: 'Rrota Lucky Bite + kartë besnikërie',
  lp_bundle_everything_li4: 'Fushata emaili për rikthimin e klientëve',
  lp_bundle_popular: 'Shumica e restoranteve zgjedhin këtë',
  lp_bundle_start_free: 'Fillo falas',
  lp_bundle_start_trial: 'Fillo provën 7-ditore',
  lp_bundle_billed: 'faturuar',
  lp_bundle_yr: 'vit',
  lp_bundle_3mo: '3muaj',
  lp_bundle_customize: 'Asnjëra s\'të përshtatet?',
  lp_bundle_customize_link: 'Ndërtoje veçori pas veçorie →',

  // Si funksionon (përdor lp_golive_lead/1/2/3 poshtë)
  lp_how_tag: 'Si fillon',
  lp_how_title: 'Live sonte. Pa hardware, pa kontratë.',

  // CTA final — direkt, pa formë lead-i
  lp_final_title: 'Menuja jote mund të jetë live sonte.',
  lp_final_sub: 'Lucky Bite duke u rrotulluar për klientët e tu nesër.',
  lp_final_whatsapp: 'Na shkruaj në WhatsApp',

  // "3 hapa për online" — shiriti brenda seksionit të funksioneve
  lp_golive_lead: '3 hapa për online:',
  lp_golive_1: 'Shto lokalin — emri, adresa, logoja.',
  lp_golive_2: 'Ndërto menunë — pjata, foto, çmime, kategori.',
  lp_golive_3: 'Printo QR-in — një fletë për çdo tavolinë, dhe je live.',

  // Çelësa të vjetër hapash — përdoren ende nga AdminPage / faqe të tjera.
  lp_sec_how_tag: 'Si funksionon',
  lp_sec_how_title_1: 'Online brenda',
  lp_sec_how_title_2: '5 minutave',
  lp_sec_how_sub: 'Tre hapa deri te menuja jote e parë me QR.',
  lp_how_tab_start: 'Fillimi',
  lp_how_tab_delivery: 'Dërgesa',
  lp_step1_title: 'Regjistro lokalin',
  lp_step1_desc: 'Krijo llogarinë dhe plotëso bazën: emri, logoja, orari, qyteti.',
  lp_step2_title: 'Shto menunë',
  lp_step2_desc: 'Kategori dhe pjata me foto, çmime e përshkrime. Ndryshimi i një çmimi më vonë është dy prekje.',
  lp_step3_title: 'Printo kodin QR',
  lp_step3_desc: 'Shkarko QR-in — një për gjithë menunë, një për çdo tavolinë — printoje dhe vendose.',
  lp_delivery_title: 'Si funksionon dërgesa',
  lp_delivery_sub: 'Nga prekja te dera, në katër hapa.',
  lp_delivery_step1_title: 'Bëhet porosia',
  lp_delivery_step1_desc: 'Klienti shfleton menunë dhe bën porosinë nga telefoni, me adresën në hartë.',
  lp_delivery_step2_title: 'Caktohet korrieri',
  lp_delivery_step2_desc: 'Korrierin e cakton direkt nga paneli admin — pa telefonatë, pa letra.',
  lp_delivery_step3_title: 'Gjurmim live',
  lp_delivery_step3_desc: 'Korrieri sheh vendndodhjen në hartë dhe e merr klientin në telefon me një prekje.',
  lp_delivery_step4_title: 'Dorëzuar',
  lp_delivery_step4_desc: 'Korrieri e shënon si të dorëzuar dhe klienti njoftohet menjëherë.',

  // Paneli
  lp_sec_preview_tag: 'Paneli',
  lp_sec_preview_title_1: 'Një ekran',
  lp_sec_preview_title_2: 'e drejton gjithë turnin.',
  lp_sec_preview_sub: 'I njëjti panel në telefon ose laptop që stafi yt përdor çdo shërbim.',
  lp_preview_li1: 'Porositë live, me njoftim sapo hyn një e re',
  lp_preview_li2: 'Pjata, kategori, foto e çmime — i ndryshon vetë',
  lp_preview_li3: 'Një QR për menunë dhe një për çdo tavolinë, gati për printim',
  lp_preview_li4: 'Llogari për kamarierë, kuzhinë dhe korrierë',
  lp_preview_li5: 'Shitjet, pjatat më të kërkuara dhe orët e pikut',
  lp_preview_li6: 'Kodet e shpërblimeve verifikohen te arka, me faturë referencë jo-fiskale',

  // Hapur (faza e hershme, në vend të dëshmive të sajuara)
  lp_early_tag: 'Hapur',
  lp_early_title_1: 'Assaggioo është i ri.',
  lp_early_title_2: 'Dhe këtë nuk e fshehim.',
  lp_early_sub: 'Këtu s\'do të gjesh logo klientësh të mëdhenj apo numra të fryrë. Do të gjesh një platformë që punon sot, dhe njerëzit që e ndërtojnë çdo javë.',
  lp_early_1_title: 'Ndër të parët',
  lp_early_1_desc: 'Kështu produkti ndërtohet sipas mënyrës si punon ti.',
  lp_early_2_title: 'Flet me ata që e ndërtuan',
  lp_early_2_desc: 'Jo një qendër thirrjesh.',
  lp_early_3_title: 'Pa kontratë, pa afat minimal',
  lp_early_3_desc: 'Provoje falas dhe vendos vetë.',
  lp_early_foot: 'Një ekip i vogël dhe i pavarur — ata me të cilët flet janë ata që shkruajnë kodin.',

  // FAQ (6 pyetje — vetëm ato që një pronar i bën vërtet para se të vendosë)
  lp_quick_contact: 'Pyetje? Na shkruaj',
  lp_faq_sub: 'S\'e gjen përgjigjen? Na pyet direkt.',
  lp_faq1_q: 'Sa kushton?',
  lp_faq1_a: 'Starter është €9/muaj me 7 ditë provë falas, pa kartë. Ose ndërto vetë paketën tënde veçori pas veçorie, nga €{price}/muaj. Pa kontratë — e anulon vetë nga paneli kur të duash.',
  lp_faq2_q: 'A merrni komision mbi porositë?',
  lp_faq2_a: 'Jo. Porosia kalon nga Assaggioo; paratë jo. Klientët të paguajnë direkt, dhe ti paguan vetëm abonimin mujor — asgjë për porosi, kurrë.',
  lp_faq_fiscal_q: 'A e zëvendëson POS-in ose arkën?',
  lp_faq_fiscal_a: 'Jo, dhe as nuk përpiqet. Punon krah çdo POS-i ose arke që përdor — faturat që printon janë referencë jo-fiskale për stafin. Shitjen e kalon te sistemi yt saktësisht si sot.',
  lp_faq3_q: 'A më duhet të blej pajisje ose aplikacion?',
  lp_faq3_a: 'Jo. Assaggioo punon nga çdo shfletues, në telefon, tablet ose kompjuter — dhe as klientët s\'kanë nevojë për aplikacion. E vetmja gjë që printon është kodi QR, një fletë për çdo tavolinë.',
  lp_faq_setup_q: 'Si e fus menunë time?',
  lp_faq_setup_a: 'Shtimi i një pjate është një formë e shkurtër; ndryshimi i çmimit më vonë është dy prekje. S\'ke kohë? Kërko një demo dhe menunë e parë e ngremë bashkë nga një foto e menusë tënde me letër.',
  lp_faq4_q: 'A mund ta anuloj? A ka kontratë?',
  lp_faq4_a: 'Pa kontratë dhe pa afat minimal. E anulon vetë nga paneli kur të duash; shërbimi vazhdon deri në fund të periudhës që ke paguar, dhe asgjë nga puna jote nuk fshihet.',
  lp_faq5_q: 'A mund ta ndryshoj ose ta ndal paketën?',
  lp_faq5_a: 'Po të dyja, nga paneli. Kalon lart ose poshtë në çdo moment, dhe e anulon kur të duash — pa kontratë, pa penalitet, dhe shërbimi vazhdon deri në fund të periudhës që ke paguar.',
  lp_faq6_q: 'Cilat gjuhë mbështet?',
  lp_faq6_a: 'E gjithë ndërfaqja — paneli yt dhe menuja që sheh klienti — punon në anglisht dhe shqip, dhe klienti kalon nga njëra te tjetra me një prekje. Emrat dhe përshkrimet e pjatave mbeten si i shkruan ti.',

  // Etiketat e veçorive të paketave — përdoren nga src/lib/planDisplay.ts (pamja e
  // krahasimit të paketave në panel), JO nga faqja hyrëse.
  lp_plan_everything_from: 'Gjithçka nga',
  lp_plan_unlimited_products: 'Produkte pa limit',
  lp_plan_up_to_products: 'Deri',
  lp_plan_products_suffix: 'produkte',
  lp_plan_unlimited_cats: 'Kategori pa limit',
  lp_plan_up_to_cats: 'Deri',
  lp_plan_cats_suffix: 'kategori',
  lp_plan_unlimited_orders: 'Porosi online pa limit',
  lp_plan_up_to_orders: 'Deri',
  lp_plan_orders_suffix: 'porosi/muaj',
  lp_plan_no_orders: 'Porosi online',
  lp_plan_extras: 'Produkte Extra (shtesa)',
  lp_plan_analytics: 'Panel analitike',
  lp_plan_clients: 'Menaxhim klientësh',
  lp_plan_reviews_likes: 'Vlerësime & pëlqime nga klientët',
  lp_plan_csv: 'Eksport CSV porosish',
  lp_plan_support_starter: 'Mbështetje me email',
  lp_plan_support_business: 'Support Chat Live',
  lp_plan_dish_comments: 'Komente publike për çdo pjatë',
  lp_plan_delivery_rewards: 'Shpërblim për klientë të përsëritur (zbritje ose artikull falas)',
  lp_plan_wheel_feature: 'Rrota e Fatit — shfaqet kur hapet menuja',
  lp_plan_courier_feature: 'Llogari korrierësh — cakto dërgesat, vendndodhje live me Google Maps',
  lp_plan_dinein_feature: 'Porosi nga tavolina — kode QR për tavolinë & dhomë',
  lp_plan_waiter_feature: 'Porosi nga kamarieri — llogari & panel kamarieri',
  lp_plan_email_feature: 'Emaile rikthimi — automatike, një email për të shkruar',
  lp_plan_daily_feature: 'Karta e ditës me gërvishtje — një mendim i ri për çdo klient, një herë në ditë',
  lp_plan_reservations_feature: 'Rezervime tavolinash — klienti kërkon, ti konfirmon',
  sa_plans_feat_reservations: 'Rezervime',
  sa_plans_feat_reservations_sub: 'Klienti kërkon tavolinë; ti konfirmon ose refuzon',
  sa_form_toggle_reservations: 'Rezervime',
  sa_form_toggle_reservations_on: 'Klientët mund të kërkojnë tavolinë',
  sa_form_toggle_reservations_off: 'Jo e disponueshme',
  sidebar_reservations: 'Rezervime',
  reservations_title: 'Rezervime',
  reservations_new_btn: '+ Rezervim i ri',
  reservations_form_title: 'Kërko tavolinë',
  reservations_field_name: 'Emri',
  reservations_field_phone: 'Telefoni',
  reservations_field_date: 'Data',
  reservations_field_time: 'Ora',
  reservations_field_party: 'Numri i personave',
  reservations_field_note: 'Shënim (opsionale)',
  reservations_field_note_ph: 'p.sh. tavolinë te dritarja, ditëlindje, alergji…',
  reservations_submit: 'Dërgo kërkesën',
  reservations_submitting: 'Duke dërguar…',
  reservations_sent_title: 'Kërkesa u dërgua!',
  reservations_sent_body: 'Restoranti do ta konfirmojë ose refuzojë së shpejti.',
  reservations_back_to_menu: 'Kthehu te menuja',
  reservations_empty: 'Nuk ke ende asnjë rezervim.',
  reservations_mine_title: 'Rezervimet e mia',
  reservations_cancel_btn: 'Anulo',
  reservations_cancel_confirm: 'Ta anulosh këtë rezervim?',
  reservations_status_pending: 'Në pritje',
  reservations_status_confirmed: 'Konfirmuar',
  reservations_status_declined: 'Refuzuar',
  reservations_status_cancelled: 'Anuluar',
  reservations_people_short: 'persona',
  reservations_validation_missing: 'Plotëso emrin, telefonin, datën, orën dhe numrin e personave.',
  reservations_validation_past: 'Zgjidh një datë dhe orë në të ardhmen.',
  reservations_kind_pick_title: "Çfarë dëshiron të rezervosh?",
  reservations_kind_table: "Një tavolinë",
  reservations_kind_table_sub: "Ha në restorant",
  reservations_kind_event: "Një event privat",
  reservations_kind_event_sub: "Ditëlindje, familje apo kolegë",
  reservations_kind_room: "Një dhomë",
  reservations_kind_room_sub: "Rezervo një qëndrim me ardhje dhe ikje",
  reservations_badge_table: "Tavolinë",
  reservations_badge_event: "Event",
  reservations_badge_room: "Dhomë",
  reservations_form_title_event: "Kërko një event",
  reservations_form_title_room: "Kërko një dhomë",
  reservations_field_event_type: "Lloji i eventit",
  reservations_event_birthday: "Ditëlindje",
  reservations_event_family: "Mbledhje familjare",
  reservations_event_business: "Event kompanie",
  reservations_event_other: "Tjetër",
  reservations_field_checkin: "Ardhja",
  reservations_field_checkout: "Ikja",
  reservations_field_guests: "Mysafirë",
  reservations_field_room: "Zgjidh një dhomë",
  reservations_room_from: "nga",
  reservations_room_per_night: "/ natë",
  reservations_room_capacity: "deri në {n} mysafirë",
  reservations_room_none: "Ende nuk ka kategori dhomash të publikuara. Dërgo datat dhe hoteli do të të kontaktojë.",
  reservations_room_details: "Detaje",
  reservations_room_hide: "Fshih",
  reservations_nights_one: "1 natë",
  reservations_nights_many: "{n} net",
  reservations_validation_checkout: "Ikja duhet të jetë pas ardhjes.",
  reservations_validation_capacity: "Kjo dhomë strehon deri në {n} mysafirë.",
  amenity_wifi: "Wi-Fi",
  amenity_ac: "Kondicioner",
  amenity_balcony: "Ballkon",
  amenity_sea_view: "Pamje nga deti",
  amenity_breakfast: "Mëngjesi i përfshirë",
  amenity_tv: "TV",
  amenity_minibar: "Minibar",
  amenity_bathtub: "Vaskë",
  amenity_parking: "Parkim",
  adm_rooms_title: "Kategoritë e dhomave",
  adm_rooms_sub: "Përshkruaj çdo lloj dhome: mysafirët e shohin me foto dhe çmim kur kërkojnë një qëndrim.",
  adm_rooms_add: "+ Kategori e re dhome",
  adm_rooms_empty: "Ende nuk ke kategori dhomash. Shto të parën: mysafirët do të zgjedhin nga këto kur rezervojnë.",
  adm_rooms_edit: "Ndrysho kategorinë e dhomës",
  adm_rooms_new: "Kategori e re dhome",
  adm_rooms_name: "Emri (anglisht)",
  adm_rooms_name_al: "Emri (shqip)",
  adm_rooms_desc: "Përshkrimi (anglisht)",
  adm_rooms_desc_al: "Përshkrimi (shqip)",
  adm_rooms_price: "Çmimi për natë",
  adm_rooms_capacity: "Mysafirë maksimumi",
  adm_rooms_beds: "Shtretër",
  adm_rooms_beds_ph: "p.sh. 1 shtrat dopio",
  adm_rooms_size: "Sipërfaqja (m²)",
  adm_rooms_amenities: "Çfarë përfshin dhoma",
  adm_rooms_photos: "Foto",
  adm_rooms_add_photo: "+ Shto foto",
  adm_rooms_available: "E dukshme për mysafirët",
  adm_rooms_hidden: "E fshehur",
  adm_rooms_save: "Ruaj",
  adm_rooms_cancel: "Anulo",
  adm_rooms_delete: "Fshi",
  adm_rooms_delete_confirm: "Ta fshij këtë kategori dhome?",
  adm_rooms_saved: "Kategoria e dhomës u ruajt",
  adm_rooms_need_migration: "Tabela e dhomave nuk ekziston ende. Ekzekuto fillimisht migrimin e rezervimeve 2026-10-14 në Supabase.",
  adm_reservations_checkout: "Ikja",
  adm_reservations_room: "Dhoma",
  adm_reservations_event: "Lloji i eventit",
  adm_tab_reservations: 'Rezervime',
  adm_reservations_empty: 'Nuk ka ende kërkesa për rezervim.',
  adm_reservations_confirm_btn: 'Konfirmo',
  adm_reservations_decline_btn: 'Refuzo',
  adm_reservations_cancelled_by_staff_btn: 'Anulo',
  adm_reservations_contact: 'Kontakt',
  adm_reservations_party: 'Persona',
  adm_reservations_note: 'Shënim',
  adm_reservations_filter_all: 'Të gjitha',
  adm_reservations_filter_pending: 'Në pritje',
  adm_reservations_filter_upcoming: 'Të ardhshme',
  adm_reservations_search_ph: 'Kërko me emër, telefon ose shënim…',
  daily_card_title: 'Karta e ditës',
  daily_card_scratch_hint: 'Gërvish për të zbuluar',
  daily_card_reveal: 'Zbulo pa gërvishtur',
  daily_card_close: 'Mbyll',
  daily_card_share: 'Ndaj',
  daily_card_saved: 'Imazhi u ruajt',
  lp_footer_platform: 'Platforma',
  lp_footer_support: 'Chat mbështetjeje brenda panelit',
  lp_footer_tagline: 'Menu dixhitale, porosi dhe shpërblime që i kthejnë klientët — për restorante dhe kafene.',
  lp_footer_register: 'Regjistrohu →',
  lp_footer_login_panel: 'Kyçu në Panel',
  lp_footer_qr: 'Menu Dixhitale QR',
  lp_footer_orders: 'Porosi Online',
  lp_footer_panel: 'Panel Admin',
  lp_footer_analytics: 'Analitikë Biznesi',
  lp_footer_product: 'Produkti',
  lp_footer_contact: 'Kontakt',
  lp_footer_copy: '© 2026 Assaggioo. Të gjitha të drejtat të rezervuara.',
  lp_footer_who: 'Për kë është',
  lp_footer_legal: 'Ligjore',
  lp_footer_privacy: 'Politika e Privatësisë',
  lp_footer_terms: 'Kushtet e Shërbimit',
  lp_footer_refund: 'Politika e Rimbursimit',

  // ── Auth (customer) ──
  auth_welcome: 'Mirë se vjen!',
  auth_create_account: 'Krijo llogari',
  auth_login_sub: 'Hyr në llogarinë tënde',
  auth_register_sub: 'Regjistrohu për të bërë porosi',
  auth_tab_login: 'Hyr',
  auth_tab_register: 'Regjistrohu',
  auth_google_btn: 'Vazhdo me Google',
  auth_divider_or: 'ose',
  auth_full_name: 'Emri i plotë',
  auth_full_name_ph: 'p.sh. Arben Hoxha',
  auth_phone: 'Numri i telefonit',
  auth_phone_ph: '+355 69 000 0000',
  auth_phone_hint: 'Përdoret për konfirmim porosie',
  auth_email: 'Email',
  auth_email_ph: 'email@shembull.com',
  auth_password: 'Fjalëkalimi',
  auth_password_ph: 'Minimum 6 karaktere',
  auth_login_btn: 'Hyr',
  auth_register_btn: 'Krijo llogari',
  auth_back_menu: '← Kthehu te menuja',
  auth_success: 'Llogaria u krijua! Kontrollo emailin për konfirmim.',
  auth_name_required: 'Vendos emrin e plotë.',
  auth_phone_required: 'Vendos numrin e telefonit — restoranti e ka nevojë për të të kontaktuar për porosinë.',
  auth_restaurant_not_found: 'Restoranti nuk u gjet.',
  auth_email_taken: 'Ky email ka tashmë një llogari. Provo "Log in" në vend të regjistrimit, ose "Harrova fjalëkalimin" nëse s\'e mban mend.',
  auth_clients_disabled_title: 'Hyrja e klientëve',
  auth_clients_disabled_msg: 'Regjistrimi dhe hyrja e klientëve është momentalisht e çaktivizuar për',
  auth_call: '📞',

  // ── Admin login ──
  admin_login_title: 'Paneli Admin',
  admin_login_email: 'Email Admin',
  admin_login_email_ph: 'admin@restoranti.com',
  admin_login_password: 'Fjalëkalimi',
  admin_login_btn: 'Hyr si Admin',
  admin_login_back: '← Menuja e restorantit',
  admin_login_failed: 'Hyrja dështoi.',
  login_admins_only: 'Kjo faqe është vetëm për administratorë restorantesh.',
  admin_profile_not_found: 'Profili nuk u gjet.',
  admin_no_access: 'Ky llogari nuk ka të drejta admin për këtë restorant.',

  // ── Generic login page ──
  login_title: 'Mirë se u kthyet',
  login_sub: 'Hyni në panelin tuaj admin',
  login_email_label: 'Email',
  login_password_label: 'Fjalëkalimi',
  login_btn: 'Hyr',
  login_no_account: 'Nuk keni llogari?',
  login_register: 'Regjistrohu falas',
  auth_forgot_title: 'Harrova fjalëkalimin',
  auth_forgot_desc: 'Shkruaj email-in tënd dhe do të marrësh një link për të rivendosur fjalëkalimin.',
  auth_forgot_email_ph: 'email@restoranti.com',
  auth_forgot_invalid_email: 'Shkruaj një email të vlefshëm.',
  auth_forgot_sent: 'Email-i u dërgua! Kontrollo kutinë tënde.',
  auth_forgot_send: 'Dërgo link reset',
  auth_forgot_cancel: 'Anulo',
  auth_forgot_close: 'Mbyll',
  login_no_admin: 'Nuk u gjet llogari admin për këtë email.',
  login_show_pwd: 'Shfaq fjalëkalimin',
  reset_pwd_title: 'Vendos fjalëkalim të ri',
  reset_pwd_sub: 'Zgjidh një fjalëkalim të ri për llogarinë tënde.',
  reset_pwd_new_label: 'Fjalëkalimi i ri',
  reset_pwd_confirm_label: 'Konfirmo fjalëkalimin',
  reset_pwd_submit: 'Ruaj fjalëkalimin e ri',
  reset_pwd_too_short: 'Fjalëkalimi duhet të ketë të paktën 8 shkronja/shifra.',
  reset_pwd_mismatch: 'Fjalëkalimet nuk përputhen.',
  reset_pwd_invalid_title: 'Ky link ka skaduar',
  reset_pwd_invalid_sub: 'Kërko një link të ri për rivendosjen e fjalëkalimit dhe provo sërish.',
  reset_pwd_back_to_login: 'Kthehu te hyrja',
  reset_pwd_success_title: 'Fjalëkalimi u përditësua!',
  reset_pwd_success_sub: 'Po të ridrejtojmë — tashmë je i loguar.',

  // ── Welcome modal (first open after setup) ──
  welcome_modal_title: 'Mirë se vini në Assaggioo!',
  welcome_modal_sub: 'Paneli juaj është gati. Të rrisim biznesin tuaj së bashku.',
  welcome_modal_body: 'Jemi këtu për t\'ju ndihmuar të ofroni një përvojë reale për klientët tuaj — jo vetëm fjalë, por rezultate. Më shumë angazhim, më shumë porosi, më shumë klientë besnikë.',
  welcome_modal_f1: 'Menu dixhitale e bukur që klientët do ta duan',
  welcome_modal_f2: 'Porosi online, direkt në kuzhën tuaj',
  welcome_modal_f3: 'Analitikë për të kuptuar dhe rritur biznesin',
  welcome_modal_btn: 'Të fillojmë →',
  adm_onboard_progress_prefix: 'Fillimi',
  adm_onboard_progress_sub: 'Ndiq hapat për të filluar',
  adm_onboard_done_title: '🎉 Gati për live!',
  adm_onboard_done_sub: 'Të gjitha hapat u kryen',
  adm_onboard_step_restaurant: 'Restorant u krijua',
  adm_onboard_step_product: 'Shto produktin e parë',
  adm_onboard_step_shop: 'Aktivizo shop-in',
  adm_onboard_step_qr: 'Testo QR kodin',

  // ── Menu page ──
  menu_all_cats: 'Të Gjitha',
  menu_search_ph: 'Kërko pjatë...',
  menu_fav_empty: 'Nuk ke favorites ende',
  menu_no_results: 'Nuk u gjet asnjë pjatë',
  menu_cart_empty: 'Shporta është bosh',
  menu_extras_label: '✨ Produkte Ekstra',
  menu_delivery: '🚚 Transport',
  menu_total: 'Total',
  menu_continue_order: 'Vazhdo me porosinë →',
  menu_detail_about: 'Rreth pjatës',
  menu_detail_desc_fallback: 'Pjatë e shijshme e gatuar me kujdes.',
  menu_item_desc_fallback: 'Pjatë e shijshme e kuzhinës sonë',
  menu_similar: 'Të ngjashme',
  menu_add_btn: 'Shto ·',
  menu_added_toast: '✓ U shtua në shportë!',
  menu_offline: '📵 Offline — duke shfaqur menunë e ruajtur',
  menu_detail_empty: 'Zgjidh një pjatë për të parë detajet',

  // ── Bottom nav / sidebar ──
  bnav_menu: 'Menu',
  bnav_favorites: 'Favorite',
  bnav_cart: 'Shporta',
  bnav_account: 'Llogaria',
  bnav_reservations: 'Rezervo',
  bnav_orders: 'Porositë',

  // ── Profile panel ──
  profile_user_fallback: 'Përdorues',
  profile_guest_name: 'Vizitor',
  profile_guest_hint: 'Duke porositur nga kjo tavolinë',
  profile_my_orders: 'Porositë e mia',
  profile_my_rewards: 'Shpërblimet e mia',
  profile_admin_panel: 'Paneli i Admin',
  profile_logout: 'Dilni',
  google_review_fab_title: 'Na vlerëso në Google',
  google_popup_title: 'Po e shijon',
  google_popup_sub: 'Një vlerësim i shpejtë në Google na ndihmon shumë — merr 10 sekonda.',
  google_popup_cta: '⭐ Lër një vlerësim në Google',
  google_popup_later: 'Ndoshta më vonë',
  dish_comments_title: 'Komente',
  dish_comment_yours: 'Ke lënë tashmë një koment për këtë pjatë',
  dish_comment_ph: 'Çfarë mendon për këtë pjatë?',
  dish_comment_submit: 'Posto',
  dish_comments_empty: 'Ende s\'ka komente — bëhu i pari.',
  dish_comment_anon: 'Një klient',

  // ── About panel ──
  about_maps_btn: 'Hap në Google Maps',
  about_photo_credits: 'Kreditet e fotove',
  about_opens_new_tab: 'hapet në skedë të re',
  about_close: 'Mbyll',

  // ── Sidebar tooltips ──
  sidebar_menu: 'Menu',
  sidebar_favorites: 'Favorite',
  sidebar_orders: 'Porositë',
  sidebar_rewards: 'Shpërblimet',
  sidebar_about: 'Rreth restorantit',
  sidebar_cart: 'Shporta',

  // ── Cart page ──
  cart_title: 'Shporta',
  cart_empty: 'Shporta është bosh',
  cart_view_menu: 'Shiko menunë',
  cart_orders_disabled_title: 'Porosia e çaktivizuar',
  cart_orders_disabled_msg: 'nuk pranon porosi tani.',
  cart_orders_contact: 'Kontakto:',
  cart_back_menu: 'Kthehu te menuja',
  cart_summary: 'Përmbledhja',
  cart_delivery: '🚚 Transport',
  cart_delivery_hours: 'Orari i dorëzimit',
  cart_loyalty_discount_label: 'Shpërblim besnikërie',
  cart_gift_tag: 'Dhuratë',
  cart_gift_locked_hint: 'Pjesë e shpërblimit tuaj falas — mund të shtoni më shumë, por s\'mund ta hiqni',
  cart_min_order_msg: 'Minimumi i porosisë është',
  cart_min_order_missing: 'të mungojnë',
  cart_outside_radius: 'Kjo adresë është jashtë zonës sonë të dorëzimit',
  cart_delivery_closed_title: 'Dorëzimi është i mbyllur tani',
  cart_delivery_closed_opens: 'Hapet në',
  cart_dine_in_label: 'Duke porositur nga',
  cart_dine_in_switch: 'Jo në këtë tavolinë? Ndrysho',
  cart_dine_in_switch_room: 'Jo në këtë dhomë? Ndrysho',
  wheel_popup_spinning: 'Duke rrotulluar...',
  wheel_popup_won_title: 'Fitove!',
  wheel_popup_share_hint_prefix: 'Shpërndaje këtë tek',
  wheel_popup_share_hint_suffix: 'shokë në WhatsApp për ta marrë',
  wheel_popup_share_gmaps: 'vlerësim Google Maps',
  wheel_popup_share_instagram: 'faqja e Instagramit',
  wheel_popup_share_btn: 'Shpërnda në WhatsApp',
  wheel_popup_sharing: 'Duke hapur WhatsApp...',
  wheel_popup_claim_ready: 'Çmimi u zhbllokua!',
  wheel_popup_show_staff: 'Jepja këtë kod stafit kur porosit.',
  wheel_popup_code_label: 'Kodi yt',
  wheel_popup_close: 'Mbyll',
  wheel_popup_all_products: 'Në çdo artikull',
  wheel_login_banner: '{name} është aktive — kyçu për të rrotulluar!',
  lucky_page_title: 'Lucky Bite',
  lucky_page_login_hint: 'Vetëm një kyçje e shpejtë dhe rrotullimi yt është gati.',
  lucky_page_login_btn: 'Kyçu për të rrotulluar',
  lucky_page_claiming: 'Duke kontrolluar rrotullimin tënd...',
  lucky_page_error: 'Diçka shkoi keq — provo sërish.',
  lucky_page_retry: 'Provo sërish',
  lucky_page_not_live: 'Asnjë Lucky Bite s\'është aktive tani — pyet stafin.',
  lucky_page_not_live_hint: 'Pyet kamarierin, ose kontrollo përsëri herën tjetër që vjen.',
  lucky_hero_kicker: '🎡 Rrotullo, Fito Buzëqeshje',
  lucky_hero_sub: 'Pa gjepura — një pjatë reale, e gatuar freskët.',
  lucky_page_footer: 'Të dorëzuar ngrohtë, direkt nga kuzhina jonë.',
  wheel_lose_title: 'Për pak!',
  wheel_lose_hint: 'Kësaj here s\'pate fat — kthehu një herë tjetër dhe provo sërish.',
  wheel_already_today_title: 'E ke provuar sot tashmë',
  wheel_already_today_hint: 'Një rrotullim në ditë — kthehu nesër për një shans tjetër.',
  cart_extras_label: '✨ Ekstra',
  cart_total: 'Total',
  cart_location_label: 'Vendodhja',
  cart_location_required: '* E detyrueshme',
  cart_location_confirmed: '✓ E konfirmuar',
  cart_write: 'Shkruaj',
  cart_gps: 'GPS',
  cart_map: 'Hartë',
  cart_manual_ph: 'p.sh. Rruga 7 Marsi, Ap. 3, Tiranë',
  cart_manual_hint: 'Shkruaj adresën tuaj të plotë — rruga, numri, qyteti',
  cart_gps_btn: 'Merr vendodhjen tani (GPS)',
  cart_gps_loading: 'Duke marrë vendodhjen...',
  cart_gps_denied: 'GPS u refuzua. Paste linkun ose koordinatat nga Google Maps:',
  cart_or_paste: 'Ose paste nga Google Maps:',
  cart_paste_ph: 'Paste link ose koordinata nga Google Maps...',
  cart_get: 'Merr',
  cart_maps_hint: 'Hap Google Maps → navigo te vendodhja → klik djathtas → "What\'s here?" → kopjo linkun',
  cart_verify_maps: 'Verifiko në Google Maps ↗',
  cart_change: '✕ Ndrysho',
  cart_open_map: 'Hap hartën për të zgjedhur',
  cart_location_hint: 'Zgjidh si dëshiron të japësh adresën e dorëzimit',
  cart_gps_not_supported: 'GPS nuk mbështetet nga ky shfletues.',
  cart_gps_denied_msg: 'Leja GPS u refuzua. Zgjidh një mënyrë tjetër ose aktivizo GPS në cilësimet e shfletuesit.',
  cart_gps_failed: 'GPS nuk u mor. Provo sërish ose zgjidh opsion tjetër.',
  cart_maps_no_coords: 'Koordinatat nuk u gjetën. Provo me "klik djathtas → What\'s here?" në Google Maps.',
  cart_note: 'Shënim (opsional)',
  cart_note_ph: 'p.sh. pa qepë, kati 3...',
  cart_extras_section: '✨ Ekstra (opsionale)',
  cart_login_notice: 'Duhet të hysh për të bërë porosi.',
  cart_login_btn: 'Hyr / Regjistrohu',
  cart_cash_payment_note: 'Pagesë cash në dorëzim/marrje',
  cart_cash_payment_note_dinein: 'Pagesë kur t\'ju vijë porosia',
  cart_place_order: 'Dërgo porosinë ·',
  cart_placing: 'Duke dërguar...',
  cart_success_title: 'Porosia u dërgua!',
  cart_success_sub: 'Do njoftoheni kur porosia të jetë gati.',
  cart_my_orders: 'Shiko porositë e mia',
  cart_guest_failed: 'S\'u arrit të fillonte seanca e vizitorit. Provo përsëri.',
  cart_phone_modal_title: 'Edhe një gjë',
  cart_phone_modal_sub: 'Shto numrin e telefonit që restoranti të mund të të kontaktojë për këtë porosi.',
  cart_phone_modal_continue: 'Vazhdo',
  cart_phone_modal_cancel: 'Anulo',
  cart_phone_required: 'Vendos numrin e telefonit.',
  cart_address_required: 'Vendos adresën e dorëzimit para se të vazhdosh.',
  cart_month_limit: 'Ky restorant ka arritur limitin e porosive për këtë muaj. Provo përsëri muajin tjetër.',

  // ── Orders page ──
  orders_title: 'Porosite e mia',
  orders_empty: 'Nuk ke bërë asnjë porosi ende',
  orders_view_menu: 'Shiko menunë',
  order_prefix: 'Porosi #',
  order_cancel_hint: 'Dëshiron ta anulosh këtë porosi? Telefono restorantin:',
  my_rewards_title: 'Shpërblimet e tua',
  rewards_spin_lucky_bite: 'Rrotullo Lucky Bite',
  rewards_empty: 'Ende pa shpërblime — rrotullo Lucky Bite ose vazhdo të vizitosh për ta fituar të parin.',
  rewards_auto_apply_hint: 'Aplikohet automatikisht te porosia jote e ardhshme',
  reward_status_active: 'Gati për t\'u përdorur',
  reward_status_used: 'Përdorur',
  reward_status_expired: 'Skaduar',
  reward_code_label: 'Kodi yt',
  order_extras_label: '✨ Ekstra',
  review_prompt: 'Si ishte porosia jote? Lër një vlerësim:',
  review_thanks: 'Faleminderit për vlerësimin!',
  review_restaurant_btn: 'Koment për restorantin',
  review_restaurant_title: 'Si ishte',
  review_comment_ph: 'Çfarë mendon për këtë restorant?',
  review_submit: 'Dërgo',
  review_pick_rating: 'Zgjidh vlerësimin',
  loyalty_ready_prefix: 'Fitove:',
  loyalty_ready_suffix: 'Hap menunë për ta marrë.',
  loyalty_reward_fallback: 'një shpërblim',
  loyalty_progress_prefix: '',
  loyalty_progress_suffix: 'vizita deri te',
  loyalty_popup_title: 'Ke fituar një shpërblim',
  loyalty_popup_off: 'ulje mbi porosinë',
  loyalty_popup_claim_btn: 'Merre',
  loyalty_popup_hint: 'Pa kod, pa pritje — gati për porosinë tënde të radhës.',
  loyalty_popup_claiming: 'Duke marrë...',
  loyalty_popup_claimed_title: 'Shpërblimi u mor',
  loyalty_popup_claimed_hint: 'Do të aplikohet vetvetiu në arkë — asgjë për të treguar, asgjë për të shkruar.',
  loyalty_popup_go_menu_btn: 'Fillo porosinë',
  loyalty_popup_error: 'S\'u arrit ta marrim tani. Provo përsëri.',
  loyalty_active_claim_banner: 'Ke një shpërblim aktiv:',
  loyalty_active_claim_expires: 'skadon për',
  loyalty_active_claim_hours: 'orë',

  // ── Order statuses ──
  status_pending: 'Në pritje',
  status_confirmed: 'Konfirmuar',
  status_preparing: 'Duke gatuar',
  status_ready: 'Gati',
  status_on_the_way: 'Po vjen te ju',
  status_delivered: 'Dorëzuar',
  status_cancelled: 'Anuluar',

  // ── Register page ──
  reg_have_account: 'Keni llogari?',
  reg_login_link: 'Kyçuni',
  reg_step1: 'Llogaria',
  reg_step2: 'Biznesi',
  reg_step3: 'Paketa',
  reg_step4: 'Pagesa',
  reg_step1_title: 'Krijo llogarinë',
  reg_step1_sub: 'Informacioni i llogarisë tuaj të administratorit',
  reg_full_name: 'Emri i plotë',
  reg_full_name_ph: 'Arben Gjika',
  reg_email: 'Email',
  reg_email_ph: 'arben@restoranti.al',
  reg_password: 'Fjalëkalimi',
  reg_password_ph: 'Minimum 6 karaktere',
  reg_confirm_password: 'Konfirmo fjalëkalimin',
  reg_confirm_ph: 'Fjalëkalimi sërish',
  reg_name_required: 'Kërkohet emri',
  reg_email_invalid: 'Email i pavlefshëm',
  reg_password_min: 'Minimum 6 karaktere',
  reg_passwords_mismatch: 'Fjalëkalimet nuk përputhen',
  reg_step2_title: 'Informacioni i biznesit',
  reg_step2_sub: 'Të dhënat e restorantit tuaj',
  reg_restaurant_name: 'Emri i restorantit',
  reg_restaurant_ph: 'Taverna Gjika',
  reg_nipt: 'NIPT',
  reg_nipt_optional: '(opsional)',
  reg_nipt_ph: 'K12345678A',
  reg_nipt_invalid: 'NIPT i pavlefshëm (p.sh. K12345678A)',
  reg_phone: 'Telefoni',
  reg_phone_ph: '+355 69 123 4567',
  reg_currency: 'Monedha',
  reg_address: 'Adresa',
  reg_address_ph: 'Rruga Myslym Shyri, Tiranë',
  reg_opening_hours: 'Orari i hapjes',
  reg_opening_ph: 'E Hënë-E Shtunë: 08:00-22:00',
  reg_restaurant_required: 'Kërkohet emri i biznesit',
  reg_step3_title: 'Zgjidh paketën',
  reg_step3_sub: 'Mund ta ndërrosh paketën në çdo kohë',
  reg_period_1m: '1 Muaj',
  reg_period_3m: '3 Muaj',
  reg_period_12m: 'Vjetore',
  reg_popular: '⭐ Më i Popullarizuar',
  reg_per_month: '/muaj',
  reg_step4_title: 'Pagesa',
  reg_card: '💳 Kartë Krediti / Debiti',
  reg_paypal: '🅿 PayPal',
  reg_redirecting: 'Duke ridrejtuar...',
  reg_change_plan: '← Ndrysho paketën',
  reg_success_title: 'Llogaria u krijua!',
  reg_success_sub: 'Mirë se vini në Assaggioo,',
  reg_plan_label: 'Paketa:',
  reg_go_panel: 'Hyr në panel →',
  reg_creating: 'Duke krijuar llogarinë tuaj...',
  reg_back: '← Kthehu',
  reg_continue: 'Vazhdo →',
  reg_start_free: 'Fillo Falas →',
  reg_continue_payment: 'Vazhdo me Pagesën →',
  reg_unknown_error: 'Gabim i panjohur. Provo sërish.',
  reg_email_taken: 'Ky email ka tashmë një llogari. Hyr (Log in) — mund ta krijosh biznesin edhe nga aty — ose "Harrova fjalëkalimin" nëse s\'e mban mend.',
  reg_email_taken_title: 'Ky email ka tashmë një llogari',
  reg_email_taken_sub: 'Fut fjalëkalimin për',
  reg_email_taken_continue: 'Vazhdo',
  reg_email_taken_forgot: 'Harrove fjalëkalimin?',
  reg_email_taken_different: 'Përdor email tjetër',
  reg_password_required: 'Vendos fjalëkalimin.',
  reg_wrong_password: 'Fjalëkalim i gabuar. Provo sërish ose përdor "Harrove fjalëkalimin?" më poshtë.',
  reg_connection_failed: 'Lidhja dështoi. Kontrollo internetin dhe provo sërish.',
  reg_slug_taken: 'Ky emër është i zënë. Provo një emër tjetër.',
  reg_contact_support: 'Kontakto mbështetjen nëse pagesa u krye: info@assaggioo.com',
  reg_error_title: 'Gabim',
  reg_processing: 'Duke procesuar...',

  // ── Admin Panel (shared) ──
  adm_role_admin: 'Administrator',
  adm_signout: 'Dilni ↗',
  adm_loading: 'Duke ngarkuar...',
  adm_verifying: 'Duke verifikuar rolin...',
  adm_no_rights: 'Nuk ke të drejta admin',
  adm_no_rights_role: 'ka rolin',
  adm_no_rights_action: 'Dil dhe hyr me llogari tjetër',

  adm_branch_switch_label: 'Dega',
  adm_sidebar_collapse: 'Ngushto sidebar-in',
  adm_sidebar_expand: 'Zgjero sidebar-in',
  adm_menu_shared_banner: 'Kjo menu është e përbashkët për gjithë zinxhirin — redaktimi këtu përditëson çdo degë.',
  adm_chain_locations_label: 'Lokacione',

  // Admin nav tabs
  adm_nav_group_daily: 'Përditshme',
  adm_nav_group_staff: 'Stafi',
  adm_nav_group_engagement: 'Angazhim',
  adm_nav_group_insights: 'Statistika',
  adm_nav_group_settings: 'Cilësime',
  adm_nav_group_account: 'Llogaria',
  adm_tab_orders: 'Porositë',
  adm_orders_locked_title: 'Ende s\'ke blerë asnjë mënyrë porosie',
  adm_orders_locked_desc: 'Delivery, porosia me QR në tavolinë dhe porosia nga kamarieri janë të gjitha të fikura për llogarinë tënde — klientët s\'kanë asnjë mënyrë të bëjnë porosi, kështu që kjo listë do mbetet bosh derisa të shtosh një.',
  adm_orders_locked_cta: 'Shiko paketat & shtesat',
  adm_tab_menu: 'Menuja',
  adm_tab_profile: 'Profili',
  adm_tab_branding: 'Branding',
  adm_tab_loyalty: 'Karta e Besnikërisë',
  adm_tab_stats: 'Statistika',
  adm_tab_traffic: 'Trafiku',
  adm_traffic_explainer: 'Sa persona kanë hapur menunë tuaj dixhitale — nga skanimi i QR-it, linqe të ndara, ose kërkim.',
  adm_traffic_views: 'Hapje të menusë',
  adm_traffic_trend_title: 'Hapjet me kalimin e kohës',
  adm_tab_wheel: 'Rrota',
  adm_tab_redemptions: 'Shpërblimet',
  adm_redeem_title: 'Shpërblimet',
  adm_redeem_intro: 'Kur një klient në tavolinë fiton në rrotë ose në kartën e loyalty-t, ai i tregon stafit kodin e tij — futeni këtu për të konfirmuar që është i vërtetë dhe për ta shënuar si të marrë. Kjo s\'është faturë apo arkë — atë që porosit e regjistroni si gjithmonë, veç.',
  adm_redeem_active_title: 'Klientë me shpërblim në pritje',
  adm_redeem_active_empty: 'S\'ka shpërblime të pashlyera për momentin.',
  adm_tab_reengagement: 'Email Rikthimi',
  adm_reengage_intro: 'Një mesazh nga restoranti juaj për klientët që s\'kanë porositur prej kohësh — një pjatë e re, një ofertë, ose thjesht një kujtesë e ngrohtë.',
  adm_reengage_locked_title: 'S\'përfshihet në paketën tënde',
  adm_reengage_locked_desc: 'Email-et e rikthimit kërkojnë ndjekjen e vizitave të klientëve, gjë që paketa jote aktuale s\'e përfshin — kalo në një paketë me gjurmim klientësh për ta shkyçur.',
  adm_reengage_days_label: 'Joaktiv për (ditë)',
  adm_reengage_days_hint: 'Klienti bëhet i pranueshëm kur kalojnë kaq ditë nga porosia e tij e fundit.',
  adm_reengage_message_label: 'Mesazhi',
  adm_reengage_message_ph: 'Shkruaj çfarë do të lexojnë klientët e tu...',
  adm_reengage_template_miss_btn: '"Na mungove"',
  adm_reengage_template_miss: 'Ka kohë që s\'të kemi parë — do të donim shumë të vije sërish! Eja shiko çfarë kemi të re.',
  adm_reengage_template_new_btn: '"Pjatë e re"',
  adm_reengage_template_new: 'Sapo shtuam diçka të re në menu, dhe mendojmë se do ta duash. Eja provoje!',
  adm_reengage_template_offer_btn: '"Ofertë speciale"',
  adm_reengage_template_offer: 'Kemi një ofertë speciale për ty këtë javë — eja dhe shpërblehu.',
  adm_reengage_dish_label: 'Lidh një pjatë (opsionale)',
  adm_reengage_dish_none: '— Pa pjatë —',
  adm_reengage_photo_label: 'Foto (opsionale)',
  adm_reengage_save_btn: 'Ruaj mesazhin',
  adm_reengage_send_btn: 'Dërgo tani',
  adm_reengage_sending: 'Duke dërguar…',
  adm_reengage_sent_result: 'U dërgua te {sent} nga {eligible} klientë të pranueshëm.',
  adm_reengage_weekly_usage: '{used} nga {limit} fushata të përdorura këtë javë.',
  adm_reengage_weekly_limit_hit: 'I ke përdorur të gjitha {limit} fushatat për këtë javë — numëruesi rivendoset 7 ditë pas dërgimit tënd të parë.',
  adm_reengage_stat_sent: 'Email të dërguar (gjithsej)',
  adm_reengage_stat_returned: 'Klientë që u kthyen',
  adm_reengage_stat_rate: 'Përqindja e kthimit',
  adm_redeem_code_label: 'Kodi i shlyerjes',
  adm_redeem_code_ph: 'p.sh. 482913',
  adm_redeem_search_btn: 'Kërko',
  adm_redeem_searching: 'Duke kërkuar...',
  adm_redeem_customer_label: 'Klienti',
  adm_redeem_reward_label: 'Shpërblimi',
  adm_redeem_expires_label: 'Skadon',
  adm_redeem_add_items: 'Shto çfarë porositi',
  adm_redeem_confirm_item_hint: 'Kjo është pikërisht ajo që fitoi — thjesht konfirmoje dhe shënoje si të marrë, s\'ka nevojë ta rizgjedhësh.',
  adm_redeem_mark_used_btn: 'Shëno si të marrë',
  adm_redeem_subtotal: 'Nëntotali',
  adm_redeem_discount: 'Ulje',
  adm_redeem_total_est: 'Total i vlerësuar',
  adm_redeem_create_btn: 'Krijo faturën',
  adm_redeem_creating: 'Duke krijuar...',
  adm_redeem_done_title: 'U shënua si e marrë',
  adm_redeem_done_hint: 'Bileta e këtij klienti tani është e konfirmuar — e ka marrë.',
  adm_redeem_final_total: 'Shuma për të marrë',
  adm_redeem_print_btn: 'Printo faturën',
  adm_redeem_new_btn: 'Shlyerje e re',
  adm_rewards_history_title: 'Historiku',
  adm_rewards_history_empty: 'Asnjë shpërblim i fituar në këtë periudhë.',
  adm_rewards_returning_only: 'Vetëm klientë të kthyer',
  adm_rewards_total_known: 'Vlera e njohur në këtë periudhë',
  adm_rewards_col_email: 'Email i klientit',
  adm_rewards_col_reward: 'Shpërblimi',
  adm_rewards_col_type: 'Lloji',
  adm_rewards_col_value: 'Vlera',
  adm_rewards_col_date: 'Data',
  adm_redeem_not_fiscal: 'Jo faturë fiskale — regjistroje te arka/POS-i juaj për faturën zyrtare.',
  adm_redeem_no_items: 'Shto të paktën një artikull',
  adm_redeem_hours: 'orë',
  adm_redeem_pending_dot_title: 'Ende e pashfrytëzuar',
  adm_tab_tables: 'Tavolinat',
  adm_tab_rooms: 'Dhomat',
  adm_dinein_field_table: 'Tavolina:',
  adm_dinein_field_room: 'Dhoma:',
  adm_tab_couriers: 'Korrierët',
  adm_courier_add: '+ Shto Korrier',
  adm_courier_desc: 'Korrierët shohin vetëm porositë e caktuara për ta, telefonin dhe vendndodhjen e klientit në Google Maps, dhe mund t\'i shënojnë porositë si të dorëzuara — s\'shohin menunë, statistikat apo cilësime të tjera admin.',
  adm_courier_empty: 'S\'ka ende korrierë — shto një për të filluar caktimin e dërgesave.',
  adm_courier_active_orders: 'dërgesa aktive',
  adm_courier_name: 'Emri i plotë',
  adm_courier_assign_label: 'Cakto korrierin',
  adm_courier_none: '— pa caktuar —',
  adm_courier_form_required: 'Emri, telefoni, email-i dhe një fjalëkalim (min. 6 shkronja) janë të detyrueshme',
  adm_courier_created: 'Llogaria e korrierit u krijua',
  adm_tab_waiters: 'Kamerierët',
  adm_waiter_add: '+ Shto Kamerier',
  adm_waiter_desc: 'Kamerierët kanë ekranin e tyre: shohin menunë, zgjedhin tavolinën dhe e dërgojnë porosinë direkt në kuzhinë, plus një pamje me sa porosi kanë marrë sot për çdo tavolinë. S\'shohin redaktuesin e menusë, statistikat apo cilësime të tjera admin.',
  adm_waiter_empty: 'S\'ka ende kamerierë — shto një për të filluar.',
  adm_waiter_name: 'Emri i plotë',
  adm_waiter_form_required: 'Emri, telefoni, email-i dhe një fjalëkalim (min. 6 shkronja) janë të detyrueshme',
  adm_waiter_created: 'Llogaria e kamerierit u krijua',
  adm_tab_kitchen: 'Kuzhina',
  adm_kitchen_add: '+ Shto Kuzhinier',
  adm_kitchen_desc: 'Personeli i kuzhinës sheh çdo porosi të hapur të restorantit bashkë me artikujt, dhe e shënon si "gati" kur del nga kuzhina — s\'sheh redaktuesin e menusë, statistikat apo cilësime të tjera admin.',
  adm_kitchen_empty: 'S\'ka ende llogari kuzhine — shto një për të filluar.',
  adm_kitchen_name: 'Emri i plotë',
  adm_kitchen_form_required: 'Emri, telefoni, email-i dhe një fjalëkalim (min. 6 shkronja) janë të detyrueshme',
  adm_kitchen_created: 'Llogaria e kuzhinës u krijua',
  adm_staff_login_link: 'Linku i hyrjes për këtë ekip',
  adm_staff_login_copy: 'Kopjo',
  adm_staff_login_copied: 'U kopjua',
  adm_creating: 'Duke krijuar…',
  adm_tab_reviews: 'Vlerësime',
  adm_reviews_sec_restaurant: 'Komente për restorantin',
  adm_reviews_sec_dishes: 'Komente pjatash',
  adm_reviews_empty_restaurant: 'Ende s\'ka komente për restorantin.',
  adm_reviews_empty_dishes: 'Ende s\'ka komente pjatash.',
  adm_reviews_delete_confirm: 'Fshi këtë vlerësim?',
  adm_review_public: 'Publik',
  adm_review_hidden: 'Fshehur',
  adm_review_hide: 'Fshihe',
  adm_review_publish: 'Publikoje',
  adm_tab_qr: 'QR Code',
  adm_tab_support: 'Support',

  // Order status labels (admin)
  adm_status_pending: 'Në pritje',
  adm_status_confirmed: 'Konfirmuar',
  adm_status_preparing: 'Duke gatuar',
  adm_status_ready: 'Gati',
  adm_status_on_the_way: 'Po vjen te ju',
  adm_status_delivered: 'Dorëzuar',
  adm_status_cancelled: 'Anuluar',
  adm_status_resolved: 'Zgjidhur',
  adm_status_in_progress: 'Në proces',
  adm_status_open: 'Hapur',

  // Aplikacioni i korrierit
  courier_login_title: 'Korrier',
  courier_dash_title: 'Dërgesat e mia',
  courier_active: 'Aktive',
  courier_done: 'Të kryera',
  courier_empty_active: 'S\'ka dërgesa të caktuara për momentin.',
  courier_empty_done: 'S\'ka ende dërgesa të kryera.',
  courier_open_maps: 'Hap në Maps',
  courier_call_btn: 'Telefono klientin',
  courier_mark_delivered: 'Shëno të dorëzuar',

  // Kitchen app
  kitchen_login_title: 'Kuzhina',
  kitchen_dash_title: 'Porositë për t\'u përgatitur',
  kitchen_to_prepare: 'Për t\'u përgatitur',
  kitchen_ready_tab: 'Gati',
  kitchen_empty_active: 'S\'ka asgjë për t\'u përgatitur për momentin.',
  kitchen_empty_ready: 'S\'ka ende porosi të shënuara si gati sot.',
  kitchen_mark_ready: 'Shëno gati',
  kitchen_mark_preparing: 'Fillo përgatitjen',
  kitchen_table: 'Tavolina',
  kitchen_room: 'Dhoma',
  kitchen_delivery: 'Dërgesë',
  kitchen_min_ago: 'min më parë',
  kitchen_autoprint: 'Printo automatikisht porositë e reja',
  kitchen_autoprint_hint: 'fletë me artikujt dhe sasitë, pa çmime',
  kitchen_print: 'Printo',
  kitchen_paper_width: 'Gjerësia e rrotullës',
  kitchen_paper_default: 'Sipas printerit',
  kitchen_ticket_note: 'Shënim',

  // Waiter app
  waiter_login_title: 'Kamerier',
  waiter_dash_title: 'Paneli i kamerierit',
  waiter_tab_new_order: 'Porosi e re',
  waiter_tab_today: 'Sot',
  waiter_tab_rewards: 'Shpërblimet',
  waiter_rewards_intro: 'Kur një klient në tavolinë fiton në rrotë ose në kartën e loyalty-t, ai t\'a tregon kodin — futeni për të konfirmuar që është i vërtetë dhe për ta shënuar si të marrë. Kjo s\'krijon kurrë porosi apo faturë; atë që porositin e regjistroni veç, si gjithmonë.',
  waiter_pick_table: 'Zgjidh tavolinën',
  waiter_change_table: 'Ndrysho tavolinën',
  waiter_table: 'Tavolina',
  waiter_room: 'Dhoma',
  waiter_no_tables: 'S\'ka ende tavolina — administratori i restorantit i shton te skeda "Tavolina".',
  waiter_search_ph: 'Kërko në menu…',
  waiter_no_items: 'S\'u gjet asnjë artikull.',
  waiter_order_summary: 'Porosia',
  waiter_cart_empty: 'S\'ka ende artikuj të shtuar.',
  waiter_total_note: 'Total referues — nuk është faturë fiskale.',
  waiter_note_ph: 'Shënim për kuzhinën (opsionale)',
  waiter_submit: 'Dërgo porosinë në kuzhinë',
  waiter_order_sent: 'Porosia u dërgua në kuzhinë.',
  waiter_err_no_table: 'Zgjidh fillimisht tavolinën.',
  waiter_err_empty_cart: 'Shto të paktën një artikull.',
  waiter_today_orders: 'Porosi sot',
  waiter_today_total: 'Total sot',
  waiter_today_empty: 'S\'ke bërë asnjë porosi sot.',
  waiter_orders_word: 'porosi',

  // Orders tab
  adm_orders_filter_all_dates: 'Të gjitha datat',
  adm_orders_filter_today: 'Sot',
  adm_orders_filter_range: 'Interval',
  adm_orders_until: 'deri',
  adm_orders_clear: '✕ Pastro',
  adm_orders_filter_all: 'Të gjitha',
  adm_orders_filter_delivery: 'Delivery',
  adm_orders_filter_table: 'Tavolinë',
  adm_orders_filter_room: 'Dhomë',
  adm_orders_empty_today: 'Nuk ka porosi sot.',
  adm_orders_empty_range: 'Nuk ka porosi në këtë interval datash.',
  adm_orders_empty: 'Nuk ka porosi',
  adm_orders_empty_status: 'Nuk ka porosi me këtë status.',
  adm_client_fallback: 'Klient',
  adm_open_location: '📍 Hap vendodhjen ↗',
  adm_cancel_order: 'Anulo',
  adm_invoice_btn: '🧾 Fatura',
  adm_invoice_paper_width: 'Gjerësia e letrës',
  adm_orders_note: 'Shënim',
  adm_orders_count: 'porosi',
  adm_orders_label: 'porosi',

  // Menu tab
  adm_menu_new_cat: '+ Kategori e re',
  adm_menu_new_extra: '➕ Extra',
  adm_menu_new_item: '+ Artikull i ri',
  adm_menu_categories: '📂 Kategoritë',
  adm_menu_no_cats: '⚠ Nuk ka kategori ende. Krijo kategorinë e parë para se të shtosh artikuj.',
  adm_menu_orphaned: '⚠ Artikuj pa kategori',
  adm_menu_unavailable: 'Pa disponueshëm',
  adm_menu_inactive_overlay: 'Joaktive',
  adm_menu_edit_btn: '✏️ Modifiko',
  adm_menu_deactivate: '🚫 Çaktivizo',
  adm_menu_activate: '✅ Aktivizo',
  adm_menu_delete_btn: '🗑️ Fshi',
  adm_menu_set_cat: '✏️ Cakto kategori',
  adm_menu_no_extras: 'Nuk ka ekstra ende. Shto patate, salca, etj.',
  adm_menu_extras_title: 'Ekstra',
  adm_menu_extras_sub: 'Shtesa opsionale që klientët mund të zgjedhin gjatë porosisë',
  adm_menu_new_extra_btn: '➕ Extra e re',
  adm_plan_upgrade: '↑ Upgrade →',

  // Profile tab
  adm_profile_cover_empty: '📷 Foto kryesore e restorantit',
  adm_profile_upload_cover: '📁 Ngarko foto kryesore',
  adm_profile_uploading: '⏳ Duke ngarkuar...',
  adm_profile_name: 'Emri i restorantit',
  adm_profile_name_ph: 'p.sh. Taverna Gjika',
  adm_profile_name_required: 'Emri i restorantit nuk mund të jetë bosh',
  adm_profile_tagline: 'Slogan (shfaqet nën emër)',
  adm_profile_tagline_ph: 'p.sh. Shije të vërteta shqiptare',
  adm_profile_url_note: 'Linku i menusë (/r/{slug}) mbetet i njëjtë — kontakto suportin për ta ndryshuar.',
  adm_profile_getfound_title: 'Gjenden më lehtë',
  adm_profile_getfound_hint: 'Mënyra më e fortë falas për t\'u gjetur: vendos linkun e menusë te Google Business Profile dhe bio e Instagramit.',
  adm_profile_getfound_label: 'Linku i menusë tënde',
  adm_profile_getfound_copy: 'Kopjo',
  adm_profile_getfound_copied: 'U kopjua',
  adm_profile_short_desc: 'Përshkrim i shkurtër (shfaqet te menuja)',
  adm_profile_short_desc_ph: 'p.sh. Kuzhinë shqiptare autentike që nga 1985',
  adm_profile_about: 'Rreth nesh (tekst i plotë)',
  adm_profile_about_ph: 'Tregoni historinë e restorantit...',
  adm_profile_location_title: '📍 Vendodhja në Google Maps',
  adm_profile_gps_btn: '📡 Merr vendodhjen time tani (GPS)',
  adm_profile_gps_loading: '⏳ Duke marrë vendodhjen...',
  adm_profile_or_paste: 'ose ngjit link nga Maps',
  adm_profile_get: 'Merr',
  adm_profile_maps_hint: 'Hap Google Maps → klik djathtas mbi vendodhjen → kopjo URL ose koordinatat',
  adm_profile_lat: 'Gjerësia (Latitude)',
  adm_profile_lng: 'Gjatësia (Longitude)',
  adm_profile_coords_set: '✓ Koordinatat u vendosën',
  adm_profile_view_maps: '📍 Shiko vendodhjen në Google Maps ↗',
  adm_profile_delivery_title: '🚚 Çmimi i transportit',
  adm_profile_delivery_free: '✓ Transport falas — shfaqet si "Falas" tek klientët',
  adm_profile_delivery_paid: 'Klientët do të paguajnë',
  adm_profile_delivery_for: 'transport',
  adm_profile_min_order: 'Minimumi i porosisë',
  adm_profile_min_order_hint: '0 = pa minimum',
  adm_profile_radius: 'Rrezja e dorëzimit',
  adm_profile_radius_ph: 'pa limit',
  adm_profile_radius_hint: 'Lëre bosh për pa limit. Kontrollohet vetëm kur klienti jep vendodhje GPS/hartë.',
  adm_profile_delivery_hours: 'Orari i dorëzimit',
  adm_profile_delivery_hours_ph: 'p.sh. 11:00 - 22:00',
  adm_profile_delivery_hours_hint: 'Shfaqet te klienti nëse ndryshon nga orari i lokalit. Vetëm informues.',
  adm_delivery_hours_limit_toggle: 'Prano porosi delivery vetëm gjatë orarit të caktuar',
  adm_delivery_hours_limit_hint: 'Jashtë këtij orari klienti mund të shohë menunë, por s\'mund të bëjë porosi delivery. Porositë në tavolinë/dhomë nuk preken kurrë.',
  adm_delivery_open_from: 'Hapet në',
  adm_delivery_open_until: 'Mbyllet në',
  adm_profile_google_review: '⭐ Link vlerësimi Google',
  adm_profile_google_review_hint: 'Nga Google Business Profile → "Ask for reviews". Shfaqet te klienti pas një porosie të dorëzuar.',
  adm_loyalty_title: 'Karta e Besnikërisë',
  adm_loyalty_visits: 'Vizita të nevojshme',
  adm_loyalty_reward: 'Shpërblimi',
  adm_loyalty_reward_ph: 'p.sh. Kafe falas',
  adm_loyalty_hint: 'Klientët shohin progresin te "Shpërblimet e tua". Një vizitë = një porosi delivery e dorëzuar (porositë në tavolinë/me kamarier nuk numërohen).',
  adm_loyalty_items_hint: 'Shto nga një rresht për çdo pjatë — vendos sasi për "2 copë të së njëjtës pjatë", ose shto disa pjata të ndryshme për një kombo. Të gjithë rreshtat bashkë përbëjnë shpërblimin e plotë.',
  adm_loyalty_window: 'Rivendos pas (ditë)',
  adm_loyalty_window_hint: '0 = s\'rivendoset kurrë. P.sh. 7 = progresi kthehet në zero nëse klienti s\'kthehet brenda një jave.',
  adm_loyalty_min_amount: 'Porosi min. për t\'u numëruar si vizitë (Lekë)',
  adm_loyalty_min_amount_hint: '0 = çdo porosi llogaritet. P.sh. 500 = vetëm porositë 500 Lekë e sipër llogariten si vizitë.',
  adm_profile_upgrade: 'Upgrade ↑',
  adm_branding_desc: 'Logoja jote — si të njohin klientët biznesin tënd.',
  adm_branding_logo_title: 'Logoja',
  adm_branding_logo_sub: 'Shfaqet te sidebar-i i admin-it, kodet QR të printuara, dhe faturat. Imazh katror funksionon më mirë.',
  adm_branding_logo_upload: 'Ngarko logon',
  adm_profile_save: '💾 Ruaj profilin',
  adm_profile_saving: 'Duke ruajtur...',
  adm_profile_preview: 'Shiko si duket ↗',

  // Stats tab
  adm_stats_revenue: 'Të ardhura (dorëzuar)',
  adm_stats_orders_total: 'Porosi gjithsej',
  adm_stats_unique_clients: 'Klientë unikë',
  adm_stats_pending: 'Në pritje',
  adm_stats_delivered: 'Dorëzuar',
  adm_stats_today: 'Sot',
  adm_stats_active_items: 'Artikuj aktiv',
  adm_stats_by_status: 'Sipas statusit',
  adm_stats_top5: 'Top 5 artikujt',
  adm_stats_orders: 'porosi',
  adm_stats_no_data: 'Nuk ka të dhëna ende.',
  adm_stats_range_today: 'Sot',
  adm_stats_range_7d: '7 ditë',
  adm_stats_range_30d: '30 ditë',
  adm_stats_range_month: 'Këtë muaj',
  adm_stats_range_last_month: 'Muajin e kaluar',
  adm_stats_range_all: 'Gjithë kohën',
  adm_stats_vs_prev: 'krahasuar me periudhën e kaluar',
  adm_stats_aov: 'Vlera mesatare e porosisë',
  adm_stats_cancel_rate: 'Anuluar',
  adm_stats_trend_title: 'Të ardhura & porosi',
  adm_stats_peak_hours_title: 'Orët me më shumë porosi',
  adm_stats_customers_title: 'Klientë të rinj vs që kthehen',
  adm_stats_new_customers: 'Të rinj',
  adm_stats_returning_customers: 'Që kthehen',
  adm_stats_tooltip_revenue: 'Të ardhura',
  adm_stats_tooltip_orders: 'Porosi',

  // Wheel tab
  adm_wheel_desc: 'Rrota shfaqet menjëherë kur klienti hap menunë — jo pas porosisë. Cakto një dritare kohore, sa fitues lejohen brenda saj, dhe çfarë duhet të shpërndajnë në WhatsApp për ta marrë çmimin.',
  adm_wheel_label_ph: 'p.sh. Ëmbëlsirë falas',
  adm_wheel_weight_ph: 'Pesha',
  adm_wheel_weight_hint: 'Sa shpesh zgjidhet kjo pjatë kur dikush fiton, krahasuar me çmimet e tjera — p.sh. pesha 2 fiton dyfish më shpesh se pesha 1.',
  adm_wheel_stock_ph: 'Sasia (bosh = pa limit)',
  adm_wheel_stock_hint: 'Vendos një sasi për të kufizuar sa cope prej këtij shpërblimi do të japësh gjithsej — pas atij numri, rrota s\'e ofron më.',
  adm_wheel_stock_sold_out: 'Mbaruar',
  adm_wheel_min_ph: 'Min. porosie',
  adm_wheel_min_hint: 'Vetëm mbi këtë shumë (0 = gjithmonë)',
  adm_wheel_add: '➕ Shto',
  adm_wheel_empty: 'Ende s\'ka çmime — shto të paktën një që rrota të ketë çfarë të japë.',
  adm_wheel_weight_short: 'peshë',
  adm_wheel_min_short: 'min.',
  adm_wheel_qr_title: 'Linku/QR i Lucky Bite',
  adm_wheel_qr_desc: 'Vendose te restoranti (poster, tavolinë, faturë) — vetë arritja te ky link llogaritet si prani fizike, e ndarë nga QR-ja e zakonshme e menusë.',
  adm_wheel_qr_moved_hint: 'Merre linkun/QR-në e kësaj rrote për ta vendosur te restoranti, te tab-i QR Codes.',
  adm_wheel_campaign_title: 'Rreth',
  adm_wheel_winners_label: 'fitues',
  adm_wheel_start_label: 'Fillon',
  adm_wheel_end_label: 'Mbaron',
  adm_wheel_max_winners_label: 'Fitues maksimal',
  adm_wheel_max_winners_hint: 'Shpërndahen rastësisht në gjithë dritaren',
  adm_wheel_share_required_label: 'Share të nevojshëm',
  adm_wheel_share_type_label: 'Çfarë duhet të shpërndajnë fituesit?',
  adm_wheel_share_hint: 'Fituesit duhet ta shpërndajnë këtë link te 3 persona në WhatsApp për ta marrë çmimin — përndryshe i kalon dikujt tjetër.',
  adm_wheel_campaign_save: 'Ruaj fushatën',
  adm_wheel_saving: 'Duke ruajtur...',
  adm_wheel_campaign_saved: 'Fushata u ruajt ✓',
  adm_wheel_prizes_title: 'Çmimet',
  adm_wheel_library_title: 'Rrotat e tua',
  adm_wheel_new: 'Rrotë e re',
  adm_wheel_name_ph: 'Emri i rrotës (p.sh. Fundjava)',
  adm_wheel_name_label: 'Emri',
  adm_wheel_availability_title: 'Disponueshmëria',
  adm_wheel_mode_label: 'Si aktivizohet?',
  adm_wheel_mode_manual: 'On/off manual',
  adm_wheel_mode_scheduled: 'Sipas orarit',
  adm_wheel_manual_on: 'Rrota është AKTIVE tani',
  adm_wheel_scheduled_armed: 'E armatosur (do të nisë vetë brenda intervalit sipër)',
  adm_wheel_status_live: 'Aktive',
  adm_wheel_status_scheduled: 'E planifikuar',
  adm_wheel_status_off: 'Off',
  adm_wheel_no_window: 'S\'ka interval ende',
  adm_wheel_no_prizes_hint: 'ende pa çmime',
  adm_wheel_no_prizes_toast: 'Shto të paktën një çmim para se ta aktivizosh këtë rrotë.',
  adm_wheel_no_schedule_toast: 'Vendos datën e fillimit dhe mbarimit dhe ruaj fushatën para se ta aktivizosh këtë rrotë — momentet fituese planifikohen brenda atij intervali.',
  adm_wheel_delete: 'Fshi',
  adm_wheel_end_before_start: 'Ora e mbarimit duhet të jetë pas orës së fillimit.',
  adm_wheel_interval_hint: 'Një fitues del përafërsisht çdo {interval}',
  adm_reward_type_discount: '% Zbritje',
  adm_reward_type_item: 'Artikull nga menuja',
  adm_reward_pick_item: 'Zgjidh një artikull...',
  adm_reward_all_products: 'Të gjitha produktet',
  adm_addon_wheel_title: 'Zhbllokoje Rrotën e Fatit',
  adm_addon_wheel_desc: 'S\'është pjesë e paketës tënde aktuale. Shtoje si shërbim extra — anulo kur të duash.',
  adm_addon_delivery_title: 'Zhbllokoje Delivery-n',
  adm_addon_delivery_desc: 'S\'është pjesë e paketës tënde aktuale. Shto porosi online dhe delivery si shërbim extra.',
  adm_addon_unlock: 'Zhblloko',
  adm_addon_picker_title: 'Shto features',
  adm_addon_picker_desc: 'Bli saktësisht ç\'të duhet, një nga një ose të gjitha bashkë — mbi atë që ke tashmë.',
  adm_addon_picker_total: 'Totali',
  adm_addon_picker_buy: 'Bli të zgjedhurat',
  adm_addon_picker_owned: 'E ke tashmë',
  adm_addon_free_with_delivery: 'Falas me delivery',
  adm_addon_multiloc_title: 'Shumë Lokacione',
  adm_addon_multiloc_desc: 'Një login për çdo degë, një menu e përbashkët, secila me QR dhe porositë e veta.',
  adm_addon_multiloc_add_branch: '+ Shto Lokacion',
  adm_addon_multiloc_buy_slot: 'lokacion shtesë',
  adm_addon_multiloc_branch_created: 'Lokacioni u shtua',
  adm_addon_multiloc_form_required: 'Emri, telefoni dhe adresa janë të detyrueshme',
  adm_addon_multiloc_modal_title: 'Lokacion i Ri',
  adm_addon_multiloc_no_slots: 'S\'ka vend më — bli një lokacion shtesë për të shtuar më shumë degë.',
  adm_addon_bundle_hint_1: 'si shtesë e çon totalin në',
  adm_addon_bundle_hint_2: '— duke kaluar te',
  adm_addon_bundle_hint_3: 'del më lirë dhe përfshin më shumë.',

  // Tables (dine-in) tab
  adm_tables_desc: 'Çdo tavolinë ka QR-in e vet — klientët që e skanojnë porositin direkt te ajo tavolinë, pa adresë dorëzimi.',
  adm_tables_label_ph: 'p.sh. Tavolina 4',
  adm_tables_add: '➕ Shto tavolinë',
  adm_tables_empty: 'Ende s\'ka tavolina — shto një për të gjeneruar QR-in e saj.',
  adm_tables_print: '🖨 Printo',
  adm_tables_qr_hint: 'Për të marrë QR code-in e një tavoline, shko te tab-i QR Codes dhe zgjidhe atje.',
  // Dhomat (shërbim dhome në hotel) — e njëjta veçori si Tavolinat, veç e riemërtuar
  // për restorantet me business_type 'hotel' (shih isHotel() te src/lib/dineIn.ts)
  adm_tables_desc_hotel: 'Çdo dhomë ka QR-in e vet — mysafirët që e skanojnë porositin direkt te ajo dhomë, pa adresë dorëzimi.',
  adm_tables_label_ph_hotel: 'p.sh. Dhoma 4',
  adm_tables_add_hotel: '➕ Shto dhomë',
  adm_tables_empty_hotel: 'Ende s\'ka dhoma — shto një për të gjeneruar QR-in e saj.',
  // Një restorant tani mund të përzierë tavolina dhe dhoma (p.sh. restoranti i një hoteli
  // me tavolina publike dhe dhoma me shërbim), prandaj çdo hyrje zgjedh llojin e vet.
  adm_tables_kind_label: 'Lloji',
  adm_tables_kind_table: 'Tavolinë',
  adm_tables_kind_room: 'Dhomë',

  // QR tab
  adm_qr_menu_title: 'QR Code i Menusë',
  adm_qr_menu_desc: 'Vendose në tavolina — klientët skanojnë dhe shohin menunë',
  adm_qr_type_menu: 'Menu',
  adm_qr_type_table: 'Tavolinë',
  adm_qr_type_room: 'Dhomë',
  adm_qr_type_lucky: 'Lucky Bite',
  adm_qr_table_desc: 'Zgjidh një tavolinë poshtë — klientët që skanojnë këtë kod porosisin direkt për të.',
  adm_qr_lucky_desc: 'Vendose këtë te restoranti — vetë skanimi llogaritet si prani, pa leje vendndodhjeje.',
  adm_qr_no_tables: 'S\'ka ende tavolina — shto një sipër së pari.',
  adm_qr_not_purchased_title: '{feature} s\'është pjesë e paketës tënde',
  adm_qr_not_purchased_desc: 'Kontakto suportin për ta shtuar këtë veçori te llogaria jote.',
  adm_qr_contact_support: 'Kontakto suportin',
  adm_qr_offline: '✓ Funksionon offline — klientët mund ta skanojnë edhe pa WiFi nëse e kanë hapur më parë',
  adm_qr_print: '🖨️ Printo',
  adm_qr_open: 'Hap ↗',
  adm_qr_how: 'Si ta përdorësh',
  adm_qr_menu_url: '🔗 URL e menusë:',
  adm_qr_step1_t: 'Shkarko QR Code-in',
  adm_qr_step1_d: 'Kliko PNG për printim ose SVG për dizajner.',
  adm_qr_step2_t: 'Printo dhe vendos në tavolina',
  adm_qr_step2_d: 'Mund ta laminosh ose vendosësh në baza taboline.',
  adm_qr_step3_t: 'Klienti skanon',
  adm_qr_step3_d: 'Me kamerën e telefonit hapet menuja menjëherë.',
  adm_qr_step4_t: 'Porosi pa WiFi',
  adm_qr_step4_d: 'Menuja ruhet në telefon — funksionon offline.',

  // Support tab (admin side)
  adm_support_sub: 'Dërgo kërkesë ose problem tek ekipi ynë',
  adm_support_new: '📩 Kërkesë e re',
  adm_support_subject: 'Subjekti *',
  adm_support_subject_ph: 'p.sh. Problem me porosinë, Fatura nuk hapet...',
  adm_support_msg: 'Përshkrim i problemit *',
  adm_support_msg_ph: 'Shkruaj problemin ose kërkesën në detaje...',
  adm_support_photo: 'Foto (opsionale)',
  adm_support_upload_photo: '📁 Ngarko foto',
  adm_support_send: '📤 Dërgo kërkesën',
  adm_support_sending: 'Duke dërguar...',
  adm_support_my_requests: 'Kërkesat e mia',
  adm_support_no_requests: 'Nuk ke kërkesa ende.',
  adm_support_new_reply: '↩ Përgjigje e re',
  adm_support_write_msg: 'Shkruaj mesazh...',
  adm_support_write_reply: 'Shkruaj përgjigjen...',
  adm_support_send_btn: '↩ Dërgo',
  adm_support_ticket_resolved: '✓ Kjo kërkesë është zgjidhur',
  adm_our_team: '⚡ Ekipi ynë',
  adm_you: '(Ju)',

  // Modals (admin)
  adm_modal_edit_extra: '✏️ Ndrysho ekstrën',
  adm_modal_new_extra: '✨ Extra e re',
  adm_modal_name: 'Emri *',
  adm_modal_name_ph_extra: 'p.sh. Patate extra, Salcë ketchup...',
  adm_modal_desc: 'Përshkrim (opsional)',
  adm_modal_desc_ph_extra: 'p.sh. Patate të skuqura krokante...',
  adm_modal_price: 'Çmimi',
  adm_modal_sort: 'Rendi',
  adm_modal_status: 'Statusi',
  adm_modal_status_active: '✅ Aktive — shfaqet tek klientët',
  adm_modal_status_inactive: '🚫 Joaktive — e fshehur',
  adm_modal_photo: 'Foto (opsionale)',
  adm_modal_add: '➕ Shto',
  adm_modal_create: '➕ Krijo',
  adm_modal_edit_cat: 'Ndrysho kategorinë',
  adm_modal_new_cat: 'Kategori e re',
  adm_modal_name_sq: 'Emri shqip *',
  adm_modal_name_en: 'Emri anglisht',
  adm_modal_icon: 'Ikona (emoji)',
  adm_modal_edit_item: 'Modifiko artikullin',
  adm_modal_new_item: 'Artikull i ri',
  adm_modal_img_url: 'URL e fotos',
  adm_modal_or: 'ose',
  adm_modal_upload_pc: '📁 Ngarko foto nga kompjuteri',
  adm_modal_photos: 'Fotot',
  adm_modal_cover: 'Kryesore',
  adm_modal_add_photo: '+ Shto',
  adm_modal_remove_photo: 'Hiq foton',
  adm_modal_photos_hint: 'Deri në 5 foto. E para është kryesorja që shfaqet në listën e menusë.',
  adm_modal_max_photos: 'Mund të shtosh deri në 5 foto për produkt.',
  adm_modal_category: 'Kategoria *',
  adm_modal_select: 'Zgjidh...',
  adm_modal_badge: 'Badge (opsional)',
  adm_modal_badge_ph: 'p.sh. I Ri, Bestseller',
  adm_modal_featured: 'Specialitet?',
  adm_modal_featured_no: 'Jo',
  adm_modal_featured_yes: 'Po — shfaqet në krye',
  adm_modal_item_active: '✅ Aktiv — shfaqet në menu',
  adm_modal_item_inactive: '🚫 Joaktiv — fshehur nga menu',
  adm_modal_save_changes: '💾 Ruaj ndryshimet',
  adm_modal_add_item: '➕ Shto artikullin',
  adm_plan_limit_close: 'Mbyll',
  adm_plan_limit_upgrade: '📩 Dërgo Kërkesë Upgrade',

  // Toast messages (admin)
  adm_toast_status_changed: 'Statusi u ndryshua ✓',
  adm_toast_item_deleted: 'Artikulli u fshi ✓',
  adm_toast_item_name_req: 'Vendos emrin e artikullit',
  adm_toast_item_cat_req: 'Zgjidh kategorinë',
  adm_toast_item_price_req: 'Vendos çmimin',
  adm_toast_item_saved: 'Artikulli u ndryshua ✓',
  adm_toast_item_added: 'Artikulli u shtua ✓',
  adm_toast_cat_name_req: 'Vendos emrin e kategorisë',
  adm_toast_cat_saved: 'Kategoria u ndryshua ✓',
  adm_toast_cat_added: 'Kategoria u shtua ✓',
  adm_toast_cat_deleted: 'Kategoria u fshi ✓',
  adm_toast_extra_name_req: 'Vendos emrin e ekstrës',
  adm_toast_extra_saved: 'Ekstra u ndryshua ✓',
  adm_toast_extra_added: 'Ekstra u shtua ✓',
  adm_toast_extra_deleted: 'Ekstra u fshi ✓',
  adm_toast_photo_uploaded: 'Foto u ngarkua ✓',
  adm_toast_gps_not_supported: 'GPS nuk suportohet nga ky shfletues',
  adm_toast_location_set: 'Vendodhja u morr ✓',
  adm_toast_coords_set: 'Koordinatat u vendosën ✓',
  adm_toast_coords_not_found: 'Nuk u gjetën koordinatat. Provo me URL tjetër.',
  adm_toast_profile_saved: 'Profili u ruajt ✓',
  adm_toast_ticket_sent: 'Kërkesa u dërgua ✓',
  adm_toast_error: 'Gabim: ',
  adm_toast_upload_error: 'Gabim ngarkimit: ',
  adm_toast_location_error: 'Nuk u mor vendodhja: ',
  adm_toast_activated: 'Aktivizuar ✓',
  adm_toast_deactivated: 'Çaktivizuar ✓',

  // Confirm dialogs (admin)
  adm_confirm_delete_item: 'Ky veprim nuk mund të kthehet.',
  adm_confirm_delete_extra: 'Fshi',
  adm_confirm_delete_cat_items: 'ka artikuj. Fshirja do t\'i lërë pa kategori. Vazhdo?',
  adm_confirm_delete_cat: 'Fshi kategorinë',

  // Plan limit modal
  adm_plan_pkg: 'Paketa',
  adm_plan_products: 'Produkte:',
  adm_plan_categories: 'Kategori:',

  // ── Super Admin Panel ──
  sa_super_admin: 'Super Admin',
  sa_signout: '↗ Dil nga sistemi',

  // SA nav tabs
  sa_tab_dashboard: 'Përmbledhje',
  sa_tab_restaurants: 'Restorante',
  sa_tab_clients: 'Klientë',
  sa_tab_notes: 'Shënime',
  sa_tab_support: 'Support',
  sa_tab_plans: 'Paketa',
  sa_tab_finance: 'Pasqyra',
  sa_fin_total_mrr: 'MRR Total',
  sa_fin_autorenew_mrr: 'MRR Automatik',
  sa_fin_active_subs: 'Abonime Aktive',
  sa_fin_collected: 'Arkëtuar (të fundit)',
  sa_fin_churn_30d: 'Anulime (30 ditë)',
  sa_fin_recent_payments: 'Pagesat e fundit',
  sa_fin_no_payments: 'Ende s\'ka pagesa.',
  sa_fin_cancellations: 'Anulimet',
  sa_fin_no_cancellations: 'Ende s\'ka anulime.',
  sa_fin_chain_payment: 'Zinxhir',
  sa_fin_type_cafe: 'Kafe',
  sa_fin_type_restaurant: 'Restorant',
  sa_fin_type_fastfood: 'Fast-Food',
  sa_fin_type_hotel: 'Hotel',
  sa_fin_type_mensa: 'Mensë',
  sa_fin_type_other: 'Tjetër',
  sa_fin_mrr_by_type: 'MRR sipas llojit të biznesit',
  sa_fin_search_ph: 'Kërko sipas restorantit...',
  sa_fin_th_date: 'Data',
  sa_fin_th_restaurant: 'Restoranti',
  sa_fin_th_plan: 'Paketa',
  sa_fin_th_type: 'Lloji',
  sa_fin_th_amount: 'Shuma',
  sa_fin_th_status: 'Statusi',
  sa_fin_th_mrr_lost: 'MRR i humbur',
  sa_fin_business_health: 'Shëndeti i biznesit',
  sa_fin_views_not_started: 'Numërimi i shikimeve të menusë sapo filloi sot — konvertimi do të shfaqet sapo të vijnë vizita.',
  sa_fin_conversion_note: 'Konvertimi = porosi ÷ shikime menuje, që nga fillimi i numërimit.',
  sa_fin_th_business_type: 'Lloji',
  sa_fin_th_orders_month: 'Porosi/muaj',
  sa_fin_th_repeat_rate: 'Klientë të përsëritur',
  sa_fin_th_last_order: 'Porosia e fundit',
  sa_fin_th_conversion: 'Konvertimi',
  sa_fin_today: 'Sot',
  sa_fin_inactive: 'Joaktiv 30+ ditë',
  sa_fin_at_risk: 'Të ardhura në rrezik (14d)',
  sa_fin_ltv: 'LTV mesatare (të larguar)',
  sa_fin_forecast_title: 'Parashikimi i të ardhurave',
  sa_fin_forecast_30: '30 ditët e ardhshme',
  sa_fin_forecast_60: '60 ditët e ardhshme',
  sa_fin_forecast_90: '90 ditët e ardhshme',
  sa_fin_forecast_note: 'Bazuar në MRR-në aktuale të rinovimit automatik, duke supozuar zero anulime.',
  sa_fin_th_expires: 'Skadon',
  sa_fin_mrr_by_city: 'MRR sipas qytetit',
  sa_fin_city_unknown: 'I panjohur',
  sa_fin_refund_btn: 'Shëno rimbursuar',
  sa_fin_refunded_badge: 'U rimbursua',
  sa_fin_refunded_amount_label: 'U rimbursua:',
  sa_fin_refund_within_window: 'Brenda afatit të refund-it',
  sa_fin_refund_past_window: 'Jashtë afatit të refund-it',
  sa_fin_refund_days_left: 'ditë të mbetura',
  sa_fin_refund_days_over: 'ditë tej afatit',
  sa_fin_refund_prompt_title: 'Shëno rimbursuar',
  sa_fin_refund_prompt_amount: 'Shuma e rimbursimit (€) — kjo vetëm e regjistron këtu, s\'nis rimbursim real te PayPal. Zvogëloje nëse s\'po rimburson të plotë (p.sh. për të mbajtur komisionin e PayPal/Paddle):',
  sa_fin_refund_invalid_amount: 'Vendos një shumë të vlefshme, mes 0 dhe pagesës origjinale.',
  sa_refund_window_title: 'Afati i refund-it',
  sa_refund_window_intro: 'Sa ditë pas pagesës klienti mund të kërkojë ende refund. Pas kësaj, butoni funksionon prapë por e shënon si të skaduar — s\'është bllokim i ngurtë.',
  sa_refund_window_1m: 'Plan mujor',
  sa_refund_window_3m: 'Plan 3-mujor',
  sa_refund_window_12m: 'Plan vjetor',
  sa_refund_window_days_suffix: 'ditë',

  // Growth tab
  sa_tab_growth: 'Rritja',
  sa_growth_funnel_title: 'Funneli i aktivizimit',
  sa_growth_stage_signed_up: 'Regjistruar',
  sa_growth_stage_has_menu: 'Shtoi menu',
  sa_growth_stage_live: 'Live (mori porosi)',
  sa_growth_stage_paying: 'Paguan',
  sa_growth_source_title: 'Burimi i regjistrimit',
  sa_growth_source_referral: 'Referim',
  sa_growth_source_organic: 'Organik',
  sa_growth_source_ad: 'Reklamë',
  sa_growth_source_direct: 'Direkt',
  sa_growth_source_other: 'Tjetër',
  sa_growth_source_unknown: 'I paregjistruar',
  sa_growth_stuck_title: 'Të ngecur në fillim',
  sa_growth_stuck_empty: 'Asnjë restorant s\'është i ngecur — të gjithë ata që u regjistruan më shumë se 7 ditë më parë kanë shtuar të paktën një pjatë.',
  sa_growth_th_signed_up: 'Regjistruar',
  sa_growth_th_days: 'Ditë që nga atëherë',
  sa_growth_referral_note: 'Program referimi — kërkon vendim: çfarë merr restoranti që fton një tjetër?',

  // Retention tab
  sa_tab_retention: 'Mbajtja',
  sa_ret_cohort_title: 'Kurba e mbajtjes — % ende aktivë sipas moshës',
  sa_ret_month_short: 'muaj',
  sa_ret_reasons_title: 'Arsyet e anulimit',
  sa_ret_reasons_empty: 'Ende s\'ka anulime.',
  sa_ret_reason_payment_failed: 'Pagesë e dështuar',
  sa_ret_reason_expired: 'Skadoi',
  sa_ret_reason_price: 'Çmimi',
  sa_ret_reason_missing_feature: 'Mungon feature',
  sa_ret_reason_closed: 'Mbylli biznesin',
  sa_ret_reason_payment_method_removed: 'Hoqi metodën e ruajtur të pagesës',
  sa_ret_reason_other: 'Tjetër',
  sa_ret_health_title: 'Health Score',
  sa_ret_th_score: 'Rezultati',

  // SA nav groups
  sa_nav_group_overview: 'Përmbledhje',
  sa_nav_group_business: 'Biznesi',
  sa_nav_group_people: 'Klientë & Mbështetje',
  sa_nav_group_finance: 'Financa',
  sa_nav_group_stats: 'Statistika',
  sa_nav_group_developer: 'Zhvillues',
  sa_nav_group_marketing: 'Marketing',

  // SA metric pages — shared chrome
  sa_metric_range_7d: '7D',
  sa_metric_range_30d: '30D',
  sa_metric_range_90d: '90D',
  sa_metric_range_12m: '12M',
  sa_metric_range_custom: 'Personalizuar',
  sa_metric_export_csv: 'Eksporto CSV',
  sa_metric_vs_previous: 'vs periudha e mëparshme',
  sa_metric_plain_language: 'Gjuhë e thjeshtë',
  sa_metric_show_chart: 'Shfaq grafikun',
  sa_metric_manual_entry_title: 'Vlera reale e këtij muaji',
  sa_metric_manual_entry_hint: "S'ka gjurmim automatik për këtë ende — fut numrin real çdo muaj.",
  sa_metric_manual_note_placeholder: 'Shënim (opsionale)',
  sa_metric_manual_save: 'Ruaj',
  sa_metric_toast_saved: 'U ruajt',
  sa_metric_low_sample_prefix: 'Bazuar në vetëm',
  sa_metric_low_sample_suffix: 'raste deri tani — interpretoje me kujdes.',
  sa_metric_incomplete_title: 'Menuja nuk ka mbaruar akoma',
  sa_metric_incomplete_body: "S'ka gjurmim automatik për këtë metrikë ende — ç'shikon këtu ndërtohet nga numrat që fut vetë çdo muaj poshtë. Fut vlera reale çdo muaj që të bëhet e saktë.",
  sa_metric_incomplete_dismiss: 'Kuptova',
  sa_metric_demo_excluded_prefix: 'Të dhëna demo të përjashtuara:',
  sa_metric_demo_excluded_restaurants: 'restorante demo',
  sa_metric_demo_excluded_and: 'dhe',
  sa_metric_demo_excluded_rows: 'rreshta faturimi sintetikë nuk numërohen në këtë shifër.',

  // SA metric pages — Financa
  sa_tab_fin_mrr: 'MRR',
  sa_metric_fin_mrr_explainer: 'Të ardhurat mujore të përsëritura — sinjali më i qartë i shëndetit financiar. Rritje e vazhdueshme do të thotë biznes që rritet mbi vete.',
  sa_metric_fin_mrr_breakdown: 'Lëvizjet e MRR-së',
  sa_tab_fin_arr: 'ARR',
  sa_metric_fin_arr_explainer: 'Ritmi vjetor i të ardhurave të përsëritura — MRR × 12. E dobishme për planifikim vjetor dhe krahasim me koston.',
  sa_tab_fin_gross_margin: 'Gross Margin',
  sa_metric_fin_gross_margin_explainer: 'Pjesa e të ardhurave që mbetet pas kostove direkte (tarifa pagese, hosting). Sa më lart, aq më shumë mbetet fitim nga çdo euro.',
  sa_tab_fin_burn_rate: 'Burn Rate',
  sa_metric_fin_burn_rate_explainer: 'Sa para po humb (ose fiton) biznesi çdo muaj, pas shpenzimeve. Negative do të thotë se biznesi është fitimprurës.',
  sa_tab_fin_churn: 'Churn Rate',
  sa_metric_fin_churn_explainer: 'Përqindja e restoranteve aktive që anulojnë çdo periudhë. Sa më ulët, aq më shëndetshëm — do të thotë që klientët po qëndrojnë.',
  sa_metric_fin_churn_breakdown: 'Arsyet e anulimit',
  sa_tab_fin_ltv: 'LTV',
  sa_metric_fin_ltv_explainer: 'Të ardhurat mesatare gjatë gjithë jetës nga një restorant para se të braktisë. Krahasoje me CAC-in për të parë nëse rritja ia vlen koston.',
  sa_metric_fin_ltv_breakdown: 'Vlera më e lartë e jetëgjatësisë',
  sa_tab_fin_cac: 'CAC',
  sa_metric_fin_cac_explainer: 'Kostoja mesatare për të fituar një restorant të ri pagues, nga shpenzimi i marketingut që fut çdo muaj.',
  sa_tab_fin_ltv_cac: 'LTV:CAC Ratio',
  sa_metric_fin_ltv_cac_explainer: 'Sa euro vlen një klient krahasuar me sa kushton për ta fituar. Mbi 3x konsiderohet zakonisht i shëndetshëm.',
  sa_tab_fin_cash_balance: 'Gjendja e Parave',
  sa_metric_fin_cash_balance_explainer: 'Sa para ka vërtet në llogari tani — një pamje manuale që e përditëson vetë, jo e nxjerrë nga numra të tjerë.',
  sa_tab_fin_paypal_balance: 'Bilanci PayPal',
  sa_paypal_balance_intro: 'Bilanci real, live i llogarisë tënde PayPal, marrë direkt nga PayPal — jo i nxjerrë nga ç\'ka regjistruar app-i, kështu që përfshin tashmë tarifat e vetë PayPal-it.',
  sa_paypal_balance_refresh: 'Rifresko',
  sa_paypal_balance_error_title: "S'arrihet dot bilanci i PayPal ende",
  sa_paypal_balance_total: 'Bilanci total',
  sa_paypal_balance_available: 'Bilanci i disponueshëm',
  sa_paypal_balance_withheld: 'I mbajtur pezull',
  sa_paypal_balance_as_of: 'Që nga',
  sa_tab_fin_refunds_client: 'Refund Klienti',
  sa_tab_fin_refunds_influencer: 'Refund Influencer-i',

  // SA metric pages — Marketing
  sa_tab_mkt_traffic: 'Trafiku i Website-it',
  sa_metric_mkt_traffic_explainer: 'Vizitat në assaggioo.com — gjurmohen automatikisht që tani e tutje; muajt e vjetër mund t\'i futësh ende dorazi më poshtë.',
  sa_tab_mkt_menu_traffic: 'Trafiku i Menuve',
  sa_metric_mkt_menu_traffic_explainer: 'Hapjet reale, të gjurmuara automatikisht, të menuve dixhitale të restoranteve në të gjithë platformën — nga skanimi i QR-it dhe linqet e ndara.',
  sa_metric_mkt_menu_traffic_breakdown: 'Restorantet kryesore sipas hapjeve',
  sa_tab_mkt_signups: 'Regjistrimet',
  sa_metric_mkt_signups_explainer: 'Restorante të reja që krijojnë llogari. Maja e funelit — çdo gjë tjetër fillon këtu.',
  sa_metric_mkt_signups_breakdown: 'Sipas burimit',
  sa_tab_mkt_conversion: 'Trial → Paid Conversion',
  sa_metric_mkt_conversion_explainer: 'Përqindja e regjistrimeve që bëhen klientë pagues. Këtu produkti dhe çmimi vërtetohen.',
  sa_tab_mkt_channels: 'Kanalet & ROI',
  sa_metric_mkt_channels_explainer: 'Regjistrimet sipas kanalit të fitimit (reale), krahasuar me sa shpenzon në secilin (futur manualisht) — tregon cili kanal ia vlen vërtet.',
  sa_metric_mkt_channels_breakdown: 'Sipas kanalit',
  sa_metric_channels_spend_title: 'Fut shpenzimin e këtij muaji për çdo kanal',
  sa_metric_channels_spend_referral: 'Shpenzim referimi (€)',
  sa_metric_channels_spend_organic: 'Shpenzim organik/content (€)',
  sa_metric_channels_spend_ad: 'Shpenzim reklamash (€)',
  sa_metric_channels_spend_direct: 'Shpenzim direkt/tjera (€)',

  // SA Promo Codes
  sa_tab_promos: 'Promo Kode',
  sa_promo_intro: 'Kodet japin ulje vetëm te pagesa e parë e restoranteve që vijnë nga një influencer, dhe gjurmojnë komisionin që ti ia paguan vetë atij më pas.',
  sa_promo_new: 'Kod i ri promocional',
  sa_promo_cancel: 'Anulo',
  sa_promo_field_code: 'Kodi',
  sa_promo_field_influencer: 'Emri i influencer-it',
  sa_promo_field_contact: 'Kontakti',
  sa_promo_field_plan: 'Planet',
  sa_promo_plan_wizard: 'Çdo paketë (wizard)',
  sa_promo_plans_hint: 'Lëri të gjitha të pazgjedhura për ta lejuar për çdo plan (planet fikse + wizard).',
  sa_promo_plans_all: 'Të gjitha planet',
  sa_promo_field_periods: 'Periudhat e faturimit',
  sa_promo_periods_hint: 'Lëri të gjitha të pazgjedhura për ta lejuar për çdo periudhë.',
  sa_promo_period_1m: '1 muaj',
  sa_promo_period_3m: '3 muaj',
  sa_promo_period_12m: '12 muaj',
  sa_promo_period_1m_short: '1m',
  sa_promo_period_3m_short: '3m',
  sa_promo_period_12m_short: '12m',
  sa_promo_periods_all: 'Të gjitha',
  sa_promo_field_discount: 'Ulja',
  sa_promo_field_commission: 'Komisioni %',
  sa_promo_create: 'Krijo kodin',
  sa_promo_codes_title: 'Kodet',
  sa_promo_none: 'Ende asgjë.',
  sa_promo_status: 'Statusi',
  sa_promo_active: 'Aktiv',
  sa_promo_inactive: 'Joaktiv',
  sa_promo_provision: 'Aktivizo te PayPal',
  sa_promo_toast_provisioned_partial: 'U aktivizua me gabime për:',
  sa_promo_redemptions_title: 'Përdorime & komision për t\'u paguar',
  sa_promo_owed_prefix: 'Aktualisht i detyrohesh influencer-ëve:',
  sa_promo_owed_suffix: '(të papaguara).',
  sa_promo_paid: 'Shuma e paguar',
  sa_promo_commission: 'Komisioni',
  sa_promo_paid_status: 'E paguar',
  sa_promo_unpaid_status: 'E papaguar',
  sa_promo_mark_paid: 'Shëno të paguar',
  sa_promo_toast_missing: 'Fut kodin dhe emrin e influencer-it',
  sa_promo_toast_provisioned: 'Plani i uljes te PayPal është gati',
  sa_promo_delete: 'Fshi',
  sa_promo_delete_confirm: 'Fshi kodin promocional',
  sa_promo_delete_blocked: "Ky kod ka përdorime të regjistruara — çaktivizoje në vend që ta fshish, që historia e komisionit të mbetet e paprekur.",
  sa_promo_deleted: 'Kodi promocional u fshi',
  sa_promo_account_col: 'Llogaria e portalit',
  sa_promo_account_active: 'Ka llogari',
  sa_promo_account_create: 'Krijo llogari',
  sa_promo_account_email_prompt: 'Email-i i hyrjes së influencer-it:',

  // Influencer portal
  influencer_login_title: 'Portali i Influencer-it',
  influencer_login_email: 'Email',
  influencer_login_btn: 'Hyr',
  influencer_no_access: 'Kjo nuk është llogari influenceri. Nëse sapo të kanë krijuar llogarinë, kërkoji ekipit Assaggioo ta kontrollojë.',
  influencer_bad_credentials: 'Email ose fjalëkalim i gabuar.',
  influencer_portal_title: 'Paneli Im',
  influencer_stat_total_earned: 'Fituar gjithsej',
  influencer_stat_pending: 'Në pritje pagese',
  influencer_stat_paid: 'Të paguara tek ti',
  influencer_stat_businesses: 'Biznese të sjella',
  influencer_codes_title: 'Kodet e tua promocionale',
  influencer_code_expires: 'skadon',
  influencer_code_expired: 'I skaduar',
  influencer_code_no_expiry: 'Pa afat',
  influencer_table_title: 'Referimet',
  influencer_empty: 'Ende asnjë referim — nda kodin tënd për të filluar.',
  influencer_th_date: 'Data',
  influencer_th_business: 'Biznesi',
  influencer_th_commission: 'Komisioni',
  influencer_th_status: 'Statusi',
  influencer_status_paid: 'E paguar',
  influencer_status_pending: 'Në pritje',
  influencer_status_clawed_back: 'E rikthyer (refund)',
  sa_promo_search_placeholder: 'Kërko sipas kodit ose influencer-it...',
  sa_promo_influencer_earnings_title: 'Fitimet e influencer-ëve',
  sa_promo_influencer_th_earned: 'Fituar gjithsej',
  sa_promo_influencer_th_businesses: 'Bizneset e sjella',
  sa_promo_no_influencers: 'Ende s\'ka influencer — shto një së pari.',
  sa_promo_select_influencer: 'Zgjidh një influencer...',
  sa_promo_field_expires: 'Skadon',
  sa_promo_expired: 'Skaduar',
  sa_promo_no_expiry: 'Pa afat',

  // SA Influencers
  sa_tab_influencers: 'Influencer-ët',
  sa_inf_intro: 'Krijo influencer-in së pari, pastaj jepi një ose disa kode promocionale. Sapo të krijosh llogarinë e tij në portal, ai mund të hyjë dhe të shohë referimet dhe fitimet e veta.',
  sa_inf_login_link: 'Linku i hyrjes në portalin e influencerëve',
  sa_inf_new: 'Influencer i ri',
  sa_inf_create: 'Krijo influencer-in',
  sa_inf_toast_missing_name: 'Fut emrin e influencer-it',
  sa_inf_delete_confirm: 'Fshi influencer-in',
  sa_inf_delete_blocked: 'Ky influencer ka ende kode promocionale të lidhura me të — fshi ose rialoko ato kode së pari.',
  sa_inf_deleted: 'Influencer-i u fshi',
  sa_inf_email_invalid: 'Shkruaj një email të vlefshëm.',
  sa_inf_account_broken: 'Llogari e prishur',
  sa_inf_account_repair: 'Riparo',
  sa_inf_account_repaired: 'Llogaria e influencer-it u riparua — tani mund të hyjë.',
  sa_inf_login_not_deleted: 'por hyrja e portalit nuk u fshi:',

  // SA Refunds
  sa_refunds_client_intro: 'Pagesa që iu kthyen (refund) një restoranti.',
  sa_refunds_client_none: 'Ende asgjë.',
  sa_refunds_th_date: 'Data',
  sa_refunds_th_restaurant: 'Restoranti',
  sa_refunds_th_plan: 'Plani',
  sa_refunds_th_amount: 'Shuma',
  sa_refunds_influencer_intro: 'Përdorime kodesh promocionale ku pagesa përkatëse më vonë u kthye (refund) — nëse komisioni ishte paguar tashmë, duhet rikthyer nga influencer-i.',
  sa_refunds_influencer_needs_clawback: 'Duhet rikthim',
  sa_refunds_influencer_no_clawback_needed: 'S\'është paguar ende — s\'ka çfarë të rikthehet',
  sa_refunds_influencer_clawed_back: 'U rikthye',
  sa_refunds_influencer_mark_clawback: 'Shëno të rikthyer',

  // SA Cash ledger
  sa_ledger_title: 'Lëvizjet e parave',
  sa_ledger_intro: 'Çdo euro hyrje-dalje — fitime nga bizneset, refunde te bizneset, dhe komisione influencer-ësh të rikthyera pas një refundi.',
  sa_ledger_filter_all: 'Të gjitha',
  sa_ledger_filter_earning: 'Fitime',
  sa_ledger_filter_refund: 'Refunde',
  sa_ledger_total_in: 'Hyrje gjithsej',
  sa_ledger_total_out: 'Dalje gjithsej',
  sa_ledger_net: 'Neto',
  sa_ledger_th_type: 'Lloji',
  sa_ledger_kind_earning: 'Fitim',
  sa_ledger_kind_refund_client: 'Refund — Klient',
  sa_ledger_kind_refund_influencer: 'Refund — Influencer',

  // Operations tab
  sa_tab_operations: 'Operacionet',
  sa_ops_avg_response: 'Përgjigja mesatare',
  sa_ops_avg_resolution: 'Koha mesatare e zgjidhjes',
  sa_ops_audit_title: 'Regjistri i veprimeve',
  sa_ops_audit_empty: 'Ende s\'ka veprime të regjistruara.',
  sa_ops_th_actor: 'Kush',
  sa_ops_th_action: 'Veprimi',
  sa_ops_errors_24h: 'Gabime (24h)',
  sa_ops_errors_title: 'Gabimet',
  sa_ops_errors_empty: 'Asnjë gabim i regjistruar — gjithçka në rregull.',
  sa_ops_th_last_seen: 'Shfaqur së fundmi',
  sa_ops_th_source: 'Burimi',
  sa_ops_th_message: 'Mesazhi',
  sa_ops_th_count: 'Sa herë',
  sa_ops_plan_change: 'Ndryshim plani',
  sa_ops_decision_note: 'Program referimi dhe fushata email drejt klientëve — të dyja kërkojnë vendim biznesi (struktura e shpërblimit, përmbajtja, opt-out) përpara se të ndërtohen si veçori reale.',

  sa_tab_reviews: 'Vlerësime',
  sa_reviews_empty: 'Ende s\'ka vlerësime.',
  sa_reviews_empty_search: 'Nuk u gjet asnjë vlerësim.',
  sa_reviews_search_ph: 'Kërko sipas restorantit ose komentit...',
  sa_reviews_filter_low: '3 yje ose më pak',
  sa_reviews_avg: 'Vlerësimi mesatar',
  sa_reviews_delete_confirm: 'Fshi këtë vlerësim?',

  // SA status labels
  sa_status_active: 'Aktiv',
  sa_status_inactive: 'Çaktiv',
  sa_status_expired: 'Skaduar',
  sa_status_starts: 'Nis',

  // Dashboard
  sa_dash_total_restaurants: 'Gjithsej Restorante',
  sa_dash_active: 'Aktive',
  sa_dash_inactive: 'Joaktive',
  sa_dash_clients: 'Klientë',
  sa_dash_notes: 'Shënime',
  sa_dash_currencies: 'Monedhat aktive',
  sa_dash_status_chart: 'Statusi i restoranteve',
  sa_needs_title: 'Kërkon vëmendje',
  sa_needs_at_risk: 'Skadon së shpejti',
  sa_needs_suspended: 'Pagesë e dështuar',
  sa_needs_stuck: 'Ngecur në fillim',
  sa_dash_shop_active: 'Shop aktiv',
  sa_dash_login_active: 'Login aktiv',
  sa_dash_admin_pending: '⏳ Admin pending',
  sa_dash_recent: 'Shtuar kohët e fundit',
  sa_dash_view_all: 'Shiko të gjitha restorante →',
  sa_dash_by_currency: 'Sipas monedhës',
  sa_dash_quick_actions: 'Aksione të shpejta',
  sa_dash_create_rest: 'Krijo restorant të ri',
  sa_dash_view_clients: 'Shiko klientët',
  sa_dash_new_note: 'Shënim i ri',
  sa_dash_mrr: 'Të ardhura mujore (MRR)',
  sa_dash_expiring_title: '⏰ Skadon brenda 7 ditësh',
  sa_dash_expiring_empty: 'Asnjë skadim i afërt',
  sa_dash_expiring_expired: 'Ka skaduar',
  sa_dash_expiring_today: 'Skadon sot',
  sa_dash_expiring_days: 'ditë mbetur',
  sa_dash_trial_conversion: 'Konvertimi i provës',
  sa_dash_trial_no_data: 'Ende asnjë provë e përfunduar',
  sa_dash_trial_breakdown: 'konvertuar · humbur',
  sa_dash_growth_title: 'Rritja e platformës',
  sa_dash_signups_chart_title: 'Restorante të reja / javë',
  sa_dash_platform_orders_title: 'Porosi & GMV në platformë / javë',
  sa_dash_plan_mix_title: 'Restorante & MRR sipas paketës',
  sa_dash_tooltip_signups: 'Restorante të reja',
  sa_dash_tooltip_orders: 'Porosi',
  sa_dash_tooltip_gmv: 'GMV',
  sa_dash_tooltip_mrr: 'MRR',
  sa_dash_tooltip_count: 'Restorante',

  sa_op_edit: 'Ndrysho',
  sa_op_delete: 'Fshi',
  sa_op_cancel: 'Anulo',

  sa_tab_chains: 'Zinxhirë',
  sa_chain_add: '+ Zinxhir i ri',
  sa_chain_empty: 'Ende s\'ka zinxhirë — shto të parin.',
  sa_chain_locations: 'lokacione',
  sa_chain_expires: 'skadon',
  sa_chain_mrr_total: 'MRR total',
  sa_chain_branches: 'Degët',
  sa_chain_no_branches: 'Ende s\'ka degë të lidhura.',
  sa_chain_menu_source: 'Cakto si burim menuje të përbashkët',
  sa_chain_select_restaurant: 'Zgjidh një restorant...',
  sa_chain_add_branch: 'Shto degë',
  sa_chain_admins: 'Qasje admini',
  sa_chain_no_admins: 'Ende s\'ka admin me qasje.',
  sa_chain_admin_email_ph: 'email admini...',
  sa_chain_grant_access: 'Jep qasje',
  sa_chain_name_required: 'Emri kërkohet',
  sa_chain_save: 'Ruaj',
  sa_chain_delete_confirm: 'Fshi këtë zinxhir? Degët mbeten — thjesht shkëputen.',
  sa_chain_max_reached: 'U arrit limiti i lokacioneve për këtë paketë zinxhiri.',
  sa_chain_remove_branch_confirm: 'Hiq këtë degë nga zinxhiri?',
  sa_chain_admin_not_found: 'S\'u gjet llogari admini me atë email.',
  sa_chain_access_granted: 'Qasja u dha',
  sa_chain_edit_title: 'Redakto zinxhirin',
  sa_chain_new_title: 'Zinxhir i ri',
  sa_chain_name: 'Emri i zinxhirit',
  sa_chain_name_ph: 'p.sh. Teddy Grill Group',
  sa_chain_max_locations: 'Lokacione maksimale',
  sa_chain_price: 'Çmimi (€ / periudhë)',
  sa_chain_expires_label: 'Skadon më',

  // Restaurants tab (SA)
  sa_rest_total: 'Total:',
  sa_rest_restaurants: 'restorante',
  sa_rest_filtered: 'filtered nga',
  sa_rest_new: 'Restorant i ri',
  sa_rest_search_ph: 'Kërko sipas emrit ose slug...',
  sa_rest_filter_all: 'Gjithsej',
  sa_rest_selected: 'të zgjedhura',
  sa_rest_delete_selected: 'Fshi të zgjedhurat',
  sa_rest_th_name: 'Restoranti',
  sa_rest_th_slug: 'Slug / URL',
  sa_rest_th_status: 'Statusi',
  sa_rest_th_currency: 'Monedha',
  sa_rest_th_phone: 'Telefon',
  sa_rest_th_actions: 'Aksione',
  sa_rest_empty_search: 'Nuk u gjet asnjë restorant me këtë kërkim.',
  sa_rest_empty: 'Nuk ka restorante ende.',
  sa_rest_action_open: 'Hap menunë',
  sa_rest_action_edit: 'Edito restorantin',
  sa_rest_action_create_admin: 'Krijo llogarinë e admin-it',
  sa_rest_action_fix_admin: 'Bëj admin profilin ekzistues',
  sa_rest_shop_disabled: 'Shop çaktiv',
  sa_rest_login_disabled: 'Login çaktiv',
  sa_rest_autorenew_badge: 'Auto',
  sa_rest_autorenew_title: 'Ky restorant rinovohet vetë çdo muaj përmes PayPal',
  sa_plan_modal_autorenew_active: 'Abonimi me rinovim automatik aktiv',
  sa_plans_sync_paypal: 'Sinkronizo me PayPal',
  sa_plans_sync_paypal_success: 'Planet u sinkronizuan me PayPal',
  sa_plans_sync_paypal_partial: 'Disa plane u sinkronizuan, të tjera dështuan:',

  // Pagination (SA)
  sa_page_of: 'nga',

  // Clients tab (SA)
  sa_clients_label: 'klientë',
  sa_clients_search_ph: 'Kërko sipas emrit, email, telefon ose zonë...',
  sa_clients_refresh: 'Rifresko',
  sa_clients_th_client: 'Klienti',
  sa_clients_th_email: 'Email',
  sa_clients_th_phone: 'Telefon',
  sa_clients_th_zone: 'Zona',
  sa_clients_th_restaurant: 'Restoranti',
  sa_clients_th_orders: 'Porosi',
  sa_clients_th_spent: 'Shpenzuar',
  sa_clients_th_registered: 'Regjistruar',
  sa_clients_th_actions: 'Aksione',
  sa_clients_filter_ordered: 'Me porosi',
  sa_clients_filter_no_orders: 'Pa porosi',
  sa_clients_empty_search: 'Nuk u gjet asnjë klient.',
  sa_clients_empty: 'Nuk ka klientë të regjistruar ende. (Sesionet anonime — Rrota e Fatit, shfletim si vizitor — janë përjashtuar nga kjo listë.)',
  sa_clients_pwd_btn: 'Rivendos fjalëkalimin',

  // Notes tab (SA)
  sa_notes_sub: 'Ide dhe plane për zhvillimin e programit. Ruhen lokalisht.',
  sa_notes_new: '+ Shënim i ri',
  sa_notes_edit_title: '️ Edito shënimin',
  sa_notes_new_title: 'Shënim i ri',
  sa_notes_title_ph: 'Titulli (opsional)...',
  sa_notes_text_ph: 'Shkruaj idenë ose planin tënd këtu...',
  sa_notes_color: 'Ngjyra:',
  sa_notes_empty: 'Nuk ke shënime ende. Shto idenë e parë!',

  // Support tab (SA)
  sa_support_open: 'hapur',
  sa_support_resolved: 'zgjidhur',
  sa_support_refresh: '↻ Rifresko',
  sa_support_search_ph: 'Kërko biznes...',
  sa_support_filter_all: 'Të gjitha',
  sa_support_filter_open: 'Pa zgjidhur',
  sa_support_filter_resolved: 'Zgjidhur',
  sa_support_empty: 'Nuk ka kërkesa support.',
  sa_support_status_resolved: 'Zgjidhur',
  sa_support_status_in_progress: 'Në proces',
  sa_support_status_open: 'Hapur',
  sa_support_resolve_btn: 'Zgjidh',
  sa_support_you: 'Ju (Ekipi)',
  sa_support_write_reply: 'Shkruaj përgjigjen...',
  sa_support_send: '↩ Dërgo',
  sa_support_ticket_resolved: 'Kjo kërkesë është zgjidhur',
  sa_support_restaurant_fallback: 'Restorant',

  // Plans tab (SA)
  sa_plans_sub: 'Ndrysho limitet, çmimet dhe funksionet · Ndryshimet pasqyrohen automatikisht në website',
  sa_plans_by_plan: 'Restorante sipas paketës',
  sa_features_title: 'Features à la carte',
  sa_features_desc: 'I njëjti katalog që shet wizard-i dhe "Shto features" te paneli admin, individualisht — ndryshimi i çmimit këtu ndryshon menjëherë kudo. Features s\'mund të fshihen këtej, vetëm çmohen.',
  sa_plans_prices: 'Çmimet (EUR total për periudhën)',
  sa_plans_limits: 'Limitet',
  sa_plans_features: 'Funksionet',
  sa_plans_no_limit: 'bosh = pa limit',
  sa_plans_max_products: 'Max Produkte',
  sa_plans_max_cats: 'Max Kategori',
  sa_plans_max_orders: 'Max Porosi/Muaj',
  sa_plans_feat_shop: 'Dërgesë & porosi online',
  sa_plans_feat_shop_sub: 'Ekrani Orders (dërgesë) · Paneli i korrierit · dyqan online',
  sa_plans_feat_dinein: 'Porosi nga tavolina',
  sa_plans_feat_dinein_sub: 'Ekrani Orders (tavolinë / dhomë) · kode QR për tavolinë · QR dhome',
  sa_plans_feat_waiter: 'Porosi nga kamarieri',
  sa_plans_feat_waiter_sub: 'Ekrani Orders (kamarier) · Paneli i kamarierit · llogari kamarierësh',
  sa_plans_feat_wheel_sub: 'Tab-i Rewards · rrotë çmimesh me vendndodhje, ndaj-për-të-marrë',
  sa_plans_feat_loyalty_sub: 'Tab-i Rewards · kartë besnikërie me vizita',
  sa_plans_feat_email: 'Emaile rikthimi',
  sa_plans_feat_daily: 'Karta e ditës me gërvishtje',
  sa_plans_feat_daily_sub: 'Një mendim ditor që klientët e gërvishtin kur hapin menunë',
  sa_plans_feat_email_sub: 'Email automatik për klientët që s\'janë dukur',
  sa_plans_feat_kitchen_note: 'Çdo kanal porosie shfaq edhe panelin e Kuzhinës.',
  sa_plans_feat_support: 'Support Chat Live (aktiv gjithmonë)',
  sa_plans_base_note: 'Falas me çdo plan (s\'ndryshohet): menu dixhitale · pjatët & çmimet · pjatët extra · statistikat · eksport CSV · llogari klientësh me vlerësime & pëlqime.',
  sa_plans_price_1m: '1 Muaj (€/muaj)',
  sa_plans_price_3m: '3 Muaj total (€)',
  sa_plans_price_12m: 'Vjetore / 12 Muaj (€)',
  sa_plans_save_btn: 'Ruaj',
  sa_plans_new_plan_btn: '+ Paketë e Re',
  sa_plans_new_plan_slug_label: 'ID e Paketës (unike, shkronja të vogla, pa hapësira)',
  sa_plans_new_plan_slug_ph: 'p.sh. premium',
  sa_plans_catalog: 'Katalogu & Shfaqja',
  sa_plans_label: 'Emri i shfaqur',
  sa_plans_icon: 'Ikona (emoji)',
  sa_plans_color: 'Ngjyra',
  sa_plans_sort_order: 'Radha e shfaqjes',
  sa_plans_is_popular: 'Shëno si "Më i Popullarizuar"',
  sa_plans_is_public: 'Shfaq te faqja publike e çmimeve',
  sa_plans_public_toggle_on: 'E dukshme — shfaqet te faqja e çmimeve dhe ekrani i upgrade-it të adminit',
  sa_plans_public_toggle_off: 'E fshehur — s\'shfaqet as te faqja e çmimeve, as te ekrani i upgrade-it të adminit',
  sa_plans_delete_btn: 'Fshi paketën',
  sa_plans_delete_confirm: 'Të fshihet paketa "{name}"? Kjo s\'kthehet mbrapsht.',
  sa_plans_delete_in_use: 'S\'mund të fshihet — {count} restorant(e) janë ende në këtë paketë.',
  sa_plans_show_price: 'Shfaq çmimin (fikur = kartë "Na kontakto")',
  sa_plans_trial_days: 'Provë falas (ditë, 0 = pa provë)',
  sa_plans_wheel_enabled: 'Përfshin Rrotën e Fatit',
  sa_plans_loyalty_enabled: 'Përfshin shpërblim për klientë të përsëritur',
  sa_plans_loyalty_free_with_delivery: 'Falas — përfshihet automatikisht me delivery',
  sa_plans_includes_plan: 'Paketa "Gjithçka nga"',
  sa_plans_includes_plan_none: '— asnjë —',
  sa_plans_description: 'Përshkrimi (Anglisht)',
  sa_plans_description_al: 'Përshkrimi (Shqip)',

  // Plan modal (SA)
  sa_plan_modal_plan: 'Paketa',
  sa_plan_custom_price: 'Çmimi custom',
  sa_plan_custom_price_hint: 'Çmimi mujor i negociuar për këtë restorant — s\'shfaqet publikisht, përdoret vetëm për rinovimin e tij (përfshirë PayPal).',
  sa_plan_modal_expires: 'Data e Skadimit (lëre bosh = pa skadim)',
  sa_plan_modal_notes: 'Shënime Pagese',
  sa_plan_modal_notes_ph: 'p.sh. Pagoi cash 29€ për Qershor 2025',
  sa_plan_modal_history: 'Histori',
  sa_plan_modal_by: 'nga',
  sa_plan_modal_save: 'Ruaj Planin',
  sa_plan_modal_saving: 'Duke ruajtur...',
  sa_plan_option_month: '/muaj',
  sa_plan_modal_payments: 'Pagesat',
  sa_plan_modal_cancellations: 'Anulime',
  sa_cancel_status_cancelled: 'U anulua',
  sa_cancel_status_suspended: 'U pezullua (pagesë e dështuar)',
  sa_cancel_status_expired: 'Skadoi',
  sa_cancel_months_active: 'muaj aktiv',

  // Password modal (SA)
  sa_pwd_modal_title: 'Ndrysho fjalëkalimin',
  sa_pwd_modal_client_label: 'Klienti',
  sa_pwd_modal_field: 'Fjalëkalimi i ri (min 6 karaktere)',
  sa_pwd_modal_min: 'Minimum 6 karaktere',
  sa_pwd_modal_valid: 'Fjalëkalimi është i vlefshëm',
  sa_pwd_modal_no_key: 'Service key mungon — ndryshimi nuk është i mundur.',
  sa_pwd_modal_save: 'Ndrysho',
  sa_pwd_modal_saving: 'Duke ndryshuar...',

  // SA toast messages
  sa_toast_config_saved: 'Konfigurimi u ruajt',
  sa_toast_plan_changed: 'Plani u ndryshua në',
  sa_toast_pwd_changed: 'Fjalëkalimi u ndryshua',
  sa_toast_rest_deleted: 'u fshi',
  sa_toast_rests_deleted: 'restorante u fshinë',
  sa_toast_rests_deleted_partial: '{ok} u fshinë, {failed} s\'u fshinë dot',
  sa_toast_account_created: 'Llogaria për',
  sa_toast_account_created2: 'u krijua',
  sa_toast_profile_fixed: 'Profili u rregullua si admin',
  sa_toast_note_error: 'Gabim: ',
  sa_toast_upload_error: 'Gabim ngarkimit: ',
  sa_toast_error: 'Gabim: ',
  sa_toast_service_key_missing: 'Service key mungon',
  sa_toast_pwd_min_chars: 'Fjalëkalimi duhet të ketë min 6 karaktere',
  sa_toast_account_failed: 'Llogaria dështoi:',
  sa_toast_profile_not_found: 'Profili nuk u gjet — krijo llogarinë me butonin',
  sa_toast_ticket_resolved: 'Kërkesa u shënua si e zgjidhur',
  sa_toast_unknown: 'i panjohur',
  sa_support_resolve_msg: 'Kjo kërkesë u shënua si e zgjidhur. Nëse keni pyetje të tjera, hapni një kërkesë të re.',
  sa_confirm_delete_rest_prefix: 'Fshi',
  sa_confirm_delete_rest: 'Kjo fshi gjithçka (menu, kategori, porosi). I pakthyeshëm!',
  sa_confirm_delete_selected_prefix: 'Fshi',
  sa_confirm_delete_selected: 'restorante të zgjedhura? I pakthyeshëm!',
  sa_pwd_prompt: 'Fjalëkalimi për (min 6 karaktere)',
  sa_page_label: 'Faqja',

  // ── SA Restaurant Form ──
  sa_form_back: '← Kthehu',
  sa_form_edit_title: 'Edito:',
  sa_form_new_title: 'Restorant i ri',
  sa_form_saving: 'Duke ruajtur...',
  sa_form_save: 'Ruaj ndryshimet',
  sa_form_create: 'Krijo restorantin',
  sa_form_sec_identity: '️ Identiteti i restorantit',
  sa_form_logo_label: 'Logo / Ikona',
  sa_form_uploading: '⏳ Duke ngarkuar...',
  sa_form_upload_btn: 'Ngarko imazh',
  sa_form_upload_hint: 'PNG ose JPG · Rekomandohet 256×256 px',
  sa_form_upload_error: 'Gabim ngarkimit: ',
  sa_form_name_label: 'Emri i restorantit *',
  sa_form_slug_label: 'Slug / URL *',
  sa_form_public_address: 'Adresa publike: ',
  sa_form_tagline_label: 'Tagline / Moto',
  sa_form_sec_business: 'Të dhënat e biznesit',
  sa_form_nipt_label: 'NIPT',
  sa_form_nipt_hint: 'Numri i identifikimit tatimor',
  sa_form_currency_label: 'Monedha',
  sa_form_phone_label: 'Numri i telefonit',
  sa_form_hours_label: 'Orari i punës',
  sa_form_address_label: 'Adresa',
  sa_form_sec_account: 'Llogaria e admin-it të restorantit',
  sa_form_admin_email_label: 'Email i admin-it',
  sa_form_admin_pwd_label: 'Fjalëkalimi (min 6 karaktere)',
  sa_form_account_auto_create: 'Llogaria krijohet automatikisht kur ruhet restoranti.',
  sa_form_account_hint: 'Vendos email dhe fjalëkalim për të krijuar llogarinë e adminit.',
  sa_form_account_exists: 'Llogaria admin ekziston',
  sa_form_change_email: '️ Ndrysho email-in',
  sa_form_new_email_placeholder: 'Email i ri (aktualisht: ',
  sa_form_change_btn: 'Ndrysho',
  sa_form_change_pwd: 'Ndrysho fjalëkalimin',
  sa_form_new_pwd_placeholder: 'Fjalëkalim i ri (min 6 karaktere)',
  sa_form_min_chars: 'Minimum 6 karaktere',
  sa_form_account_not_created: '⏳ Llogaria admin nuk është krijuar ende',
  sa_form_account_create_hint: 'Pas ruajtjes, krijo llogarinë me butonin nga lista e restoranteve.',
  sa_form_name_slug_required: 'Emri dhe slug janë të detyrueshëm',
  sa_form_rest_updated: 'Restoranti u ndryshua',
  sa_form_rest_created: 'Restoranti u shtua',
  sa_form_rest_created_with_account: 'Restoranti u shtua dhe llogaria admin u krijua',
  sa_form_account_failed: 'Llogaria dështoi: ',
  sa_form_pwd_changed: 'Fjalëkalimi u ndryshua',
  sa_form_email_changed: 'Email-i u ndryshua',
  sa_form_sec_payment: '💳 Pagesa e marrë',
  sa_form_payment_hint: 'Sa ka paguar klienti realisht për këtë plan — shumica e klientëve të shtuar dorazi do jenë falas, kështu që kjo fillon nga 0. Regjistrohet gjithsesi te Financa, që klientët falas të mos zhduken nga llogaritë.',
  sa_form_payment_amount: 'Shuma e paguar (€)',
  sa_form_payment_amount_hint: 'Lëre 0 nëse ky klient është falas.',
  sa_form_payment_period: 'Periudha e faturimit',
  sa_form_sec_activation: '⏰ Aktivizimi me kohë',
  sa_form_optional: '— opsional',
  sa_form_active_from: 'Aktiv nga data',
  sa_form_active_until: 'Aktiv deri në datën',
  sa_form_starts: '▶ Nis: ',
  sa_form_ends: '⏹ Mbaron: ',
  sa_form_clear_dates: 'Fshi datat',
  sa_form_sec_access: 'Akseset dhe cilësimet',
  sa_form_toggle_active: 'Restoranti aktiv',
  sa_form_toggle_active_on: 'Klientët e shohin menunë dhe mund të bëjnë porosi',
  sa_form_toggle_active_off: 'Restoranti nuk shfaqet te klientët fare',
  sa_form_sec_chain: 'Zinxhiri',
  sa_form_chain_shared_menu: 'përdor menunë e përbashkët të zinxhirit',
  sa_form_chain_hint: 'Menaxho degët, menunë e përbashkët dhe qasjen e adminit nga tab-i Zinxhirë.',
  sa_form_sec_plan: 'Paketa dhe extra',
  sa_form_business_type: 'Lloji i biznesit',
  sa_form_business_type_unset: 'I papërcaktuar',
  sa_form_business_type_hint: 'Çdo lloj ka paketën e vet të veçorive (p.sh. pa delivery për Kafe/Restorant/Hotel) dhe përdoret për të ndarë MRR-në dhe konvertimin e QR-menusë te Financa. "Tjetër" / "I papërcaktuar" heq kufizimet — përdore për përjashtime të qëllimshme.',
  sa_form_not_for_type: 'Nuk ofrohet për këtë lloj biznesi (zgjidh "Tjetër" për ta anashkaluar)',
  sa_form_signup_source: 'Burimi i regjistrimit',
  sa_form_signup_source_hint: 'Si e gjeti ky restorant Assaggioo-n — shfaqet te Rritja.',
  sa_form_extras_hint: 'Zgjedhja e paketës mbush automatikisht çfarë përfshin — më poshtë mund të aktivizosh/çaktivizosh çdo extra veç e veç.',
  sa_form_toggle_wheel: 'Rrota e Fatit',
  sa_form_toggle_wheel_on: 'Klientët mund të rrotullojnë për një çmim',
  sa_form_toggle_wheel_off: 'S\'është e disponueshme për klientët',
  sa_form_toggle_dinein: 'Dine-in / kode QR tavolinash',
  sa_form_toggle_dinein_on: 'Kodet QR të tavolinave aktive',
  sa_form_toggle_dinein_off: 'Jo e disponueshme',
  sa_form_toggle_waiter: 'Porositje nga kamerieri',
  sa_form_toggle_waiter_on: 'Llogaritë e kamerierëve mund të krijohen',
  sa_form_toggle_waiter_off: 'Jo e disponueshme',
  sa_form_toggle_listed: 'I listuar te /discover',
  sa_form_toggle_listed_on: 'I dukshëm në direktorinë publike',
  sa_form_toggle_listed_off: 'I fshehur nga direktoria publike',
  sa_form_toggle_loyalty: 'Karta e Besnikërisë',
  sa_form_toggle_loyalty_on: 'Shpërblimet për vizita janë aktive',
  sa_form_toggle_loyalty_off: 'S\'është e disponueshme për klientët',
  sa_form_loyalty_free_with_delivery: 'Përfshirë falas — vjen me delivery',
  sa_form_plan_history_created: 'Restoranti u krijua me këtë paketë',
  sa_form_plan_custom_hint: 'Paketë me porosi — aktivizo saktësisht features që po paguan ky restorant.',
  sa_form_inc_menu: 'Menu dixhitale · pjatët & çmimet',
  sa_form_inc_stats: 'Statistikat e biznesit',
  sa_form_inc_csv: 'Eksport CSV',
  sa_form_inc_branding: 'Branding i personalizuar',
  sa_form_base_includes: 'Falas me çdo plan',
  sa_form_plan_adds: 'Ky plan shton edhe',
  sa_form_inc_extras: 'Pjatët extra',
  sa_form_inc_reviews_likes: 'Vlerësime + pëlqime',
  sa_form_bundled_note: 'Një kanal porosie aktivizon edhe ekranin Orders dhe panelin e stafit përkatës (korrier / kamarier). Llogaritë e klientëve, vlerësimet, pëlqimet dhe eksporti CSV janë falas me çdo plan.',
  sa_form_toggle_delivery: 'Dërgesë & porosi online',
  sa_form_toggle_delivery_on: 'Porosi dërgese + paneli i korrierit',
  sa_form_toggle_delivery_off: 'Pa dërgesë',
  sa_form_toggle_accounts: 'Llogari për klientët',
  sa_form_toggle_email: 'Emaile rikthimi',
  sa_form_toggle_email_on: 'Fushata rikthimi automatike',
  sa_form_toggle_email_off: 'Jo e disponueshme',
  sa_form_toggle_daily: 'Karta e ditës me gërvishtje',
  sa_form_toggle_daily_on: 'Klientët e shohin kartën një herë në ditë',
  sa_form_toggle_daily_off: 'Jo e disponueshme',

  // ── Trial / subscription ──
  trial_soon_title: 'Po azhurnojmë!',
  trial_soon_sub: 'Ky restorant po bën ndryshime të veçanta për të ofruar një përvojë edhe më të mirë. Rikthehuni shpejt!',
  trial_expired_overlay_title: 'Periudha e provës mbaroi',
  trial_expired_overlay_sub: 'Periudha juaj 7-ditore e provës ka skaduar. Zgjidhni një paketë për të mbajtur restorantin tuaj online dhe vazhduar me Assaggioo.',
  trial_expired_upgrade_btn: 'Shiko Paketat',
  trial_expired_contact: 'Keni nevojë për ndihmë? Kontaktoni assaggioo1@gmail.com',
  trial_days_left: 'ditë të mbetura nga periudha e provës',
  trial_upgrade_now: 'Upgrade tani',
  reg_check_email_title: 'Kontrollo inbox-in!',
  reg_check_email_sub: 'Dërguam një link konfirmimi te',
  reg_check_email_hint: 'Pas klikimit të linkut, do të ridrejtoheni për të përfunduar konfigurimin e restorantit.',
  reg_check_email_spam: 'Nuk e morët emailin? Kontrolloni dosjen spam.',
  reg_go_login: 'Shko te Login',
  reg_phone_required: 'Numri i telefonit është i detyrueshëm',
  reg_address_required: 'Adresa është e detyrueshme',
  reg_create_account: 'Krijo Llogarinë',
  reg_terms_prefix: 'Duke vazhduar, pranoni',
  reg_terms_and: 'dhe',
  reg_wizard_hint: 'Nuk e di ende çfarë features të duhen?',
  reg_wizard_link: 'Provo Ndërtuesin e Paketës, hap pas hapi →',
  reg_trial_note: '7 ditë falas · Pa kartë krediti',
  reg_first_name: 'Emri',
  reg_first_name_ph: 'Arben',
  reg_first_name_required: 'Emri është i detyrueshëm',
  reg_last_name: 'Mbiemri',
  reg_last_name_ph: 'Gjika',
  reg_last_name_required: 'Mbiemri është i detyrueshëm',
  reg_step_account: 'Llogaria',
  reg_step_email: 'Email',
  reg_account_title: 'Krijo llogarinë',
  reg_account_sub: 'Do të dërgojmë një link konfirmimi te email-i juaj',

  // ── Setup page (post email confirmation) ──
  setup_title: 'Konfiguro restorantin',
  setup_sub: 'Gati! Plotësoni të dhënat e restorantit tuaj.',
  setup_tagline_label: 'Slogan / Përshkrim i shkurtër',
  setup_tagline_ph: 'p.sh. Pizza më e mirë në Tiranë',
  setup_need_confirm_title: 'Konfirmoni email-in tuaj',
  setup_need_confirm_sub: 'Klikoni linkun që dërguam te email-i juaj, pastaj kthehuni këtu për të përfunduar konfigurimin.',
  setup_go_register: '← Kthehu te Regjistrimi',
  setup_creating: 'Duke krijuar restorantin...',
  setup_success_title: 'Gati!',
  setup_success_sub: 'Paneli juaj po hapet...',
  setup_go_admin: 'Hap Panelin Admin →',
  setup_free_plan_note: '7 ditë provë falas u aktivizuan. Starter: menu dixhitale, produkte & kategori pa limit, pjatët extra, statistika, eksport CSV, dhe llogari klientësh me vlerësime & pëlqime — të gjitha falas.',
  setup_paid_plan_note: 'Kjo paketë s\'ka provë falas — do të kërkohet abonim për ta aktivizuar',
  setup_step_label: 'Konfigurimi i Restorantit',

  // ── Admin Plans tab ──
  adm_tab_plans: 'Paketat',
  adm_tab_bills: 'Faturat',
  adm_bills_header: 'Historiku i pagesave',
  adm_bills_autorenew_active: 'Rinovimi automatik aktiv',
  adm_bills_autorenew_desc: 'Paketa jote rinovohet vetë çdo muaj.',
  adm_bills_autorenew_cancel: 'Anulo rinovimin automatik',
  adm_paymethod_title: 'Metoda e pagesës',
  adm_paymethod_desc: 'E ruajtur që të mos e lidhësh sërish çdo herë që rinovon ose shton diçka.',
  adm_paymethod_connected: 'Lidhur më',
  adm_paymethod_remove: 'Fshi',
  adm_paymethod_add: 'Shto metodë pagese',
  adm_paymethod_empty: 'Ende s\'ke asnjë metodë pagese të ruajtur. Kliko "Shto metodë pagese" për të lidhur PayPal me rinovim automatik.',
  adm_paymethod_add_notice: 'Lidhja me rinovimin automatik aktiv e ruan si metodën tënde të pagesës.',
  adm_paymethod_replace_notice: 'Ke tashmë një metodë pagese të lidhur — duke e plotësuar këtë, ajo do zëvendësohet me këtë të re dhe abonimi i vjetër në PayPal do anulohet. Qasja jote e paguar s\'preket.',
  adm_paymethod_remove_confirm: 'Ta heqësh këtë metodë pagese nga llogaria jote? Kjo vetëm e fsheh nga paneli — nuk anulon një abonim aktiv (për atë përdor "Anulo rinovimin automatik").',
  adm_paymethod_remove_confirm_active: 'Kjo është metoda e pagesës që po përdor rinovimi automatik aktualisht — duke e hequr, do të anulohet edhe rinovimi automatik (PayPal s\'do të të tarifojë më). Do mbash qasjen deri në fund të periudhës aktuale. Vazhdo?',
  adm_paymethod_removed_toast: 'Metoda e pagesës u hoq.',
  adm_paymethod_removed_autorenew_toast: 'Metoda e pagesës u hoq dhe rinovimi automatik u anulua.',
  adm_bills_cancel_confirm: 'Të anulohet rinovimi automatik? Do mbash qasjen deri në fund të periudhës aktuale, pastaj do duhet ta rinovosh vetë.',
  adm_bills_cancel_reason_label: 'Na thuaj pse? (opsionale, na ndihmon të përmirësohemi)',
  adm_bills_cancel_reason_ph: 'Zgjidh një arsye...',
  adm_bills_cancel_success: 'Rinovimi automatik u anulua.',
  adm_bills_empty: 'Ende s\'ka pagesa.',
  adm_bills_download: 'Shkarko faturën',
  adm_bills_type_one_time: 'Pagesë',
  adm_bills_type_sub_start: 'Fillim abonimi',
  adm_bills_type_sub_renewal: 'Rinovim automatik',
  adm_plans_header: 'Paketat & Abonimenti',
  adm_plans_status_trial: 'Periudhë prove',
  adm_plans_days_left: 'ditë të mbetura',
  adm_plans_status_active: 'Paketa aktive',
  adm_plans_status_expired: 'Periudha e provës skadoi',
  adm_plans_active_until: 'Aktiv deri',
  adm_plans_popular: 'Më popullori',
  adm_plans_current_badge: 'Aktualisht',
  adm_plans_upgrade_to: 'Kalio tek',
  adm_plans_renew: 'Rinovo tani',
  adm_plans_processing: 'Duke ridrejtuar...',
  adm_plans_activated: 'Paketa u aktivizua! ✓',
  adm_plans_contact: 'Pyetje? Kontaktoni assaggioo1@gmail.com',
  pay_modal_title: 'Përfundo Ngritjen e Paketës',
  pay_modal_summary: 'Përmbledhja e paketës',
  pay_modal_promo_placeholder: 'Kod promocional',
  pay_modal_promo_apply: 'Apliko',
  pay_modal_promo_applied: 'Kodi',
  pay_modal_promo_off: 'ulje',
  pay_modal_autorenew: 'Rinovim automatik mujor',
  pay_modal_autorenew_on: 'PayPal do të tërheqë pagesën automatikisht çdo muaj — anulo kurdo.',
  pay_modal_autorenew_off: 'Pagesë një-herëshe — do duhet ta rinovosh vetë.',
  pay_modal_paypal: 'Paguaj me PayPal',
  pay_modal_not_configured: 'Pagesat nuk janë konfiguruar ende. Kontaktoni support.',
  pay_modal_success: '✓ Paketa u aktivizua me sukses!',
  pay_modal_error: 'Pagesa dështoi. Provoni sërish.',
  adm_plans_select_period: 'Periudha e faturimit',
  adm_plans_per_month: '/muaj',
  adm_plans_total: 'gjithsej',
  adm_plans_save: 'Kurseni',

  // ── Wizard-i à la carte i regjistrimit (/register/wizard) ──
  wiz_loading: 'Duke ngarkuar…',
  wiz_catalog_error: 'Nuk arritëm të ngarkojmë features. {msg}',
  wiz_shortcut_link: "S'të duhet ta personalizosh? Fillo falas me formularin klasik →",
  wiz_shortcut_link_short: 'Regjistrimi klasik →',
  wiz_continue: 'Vazhdo',
  wiz_step_of: 'Hapi {n} nga {total}',
  wiz_finishing: 'Po e përfundojmë regjistrimin tënd…',
  wiz_unexpected_error: 'Ndodhi një gabim i papritur.',
  wiz_free: 'Falas',
  wiz_free_lower: 'falas',
  wiz_per_month_long: '/ muaj',
  wiz_per_month: '/muaj',

  // llojet e biznesit
  wiz_bt_cafe: 'Kafene',
  wiz_bt_restaurant: 'Restorant',
  wiz_bt_fastfood: 'Fast-Food',
  wiz_bt_mensa: 'Mensë',
  wiz_bt_hotel: 'Hotel',

  // hapi 1 — lloji i biznesit
  wiz_type_title: 'Çfarë lloj biznesi ke?',
  wiz_type_sub: 'Çdo lloj biznesi ka paketën e vet të veçorive — ne të tregojmë vetëm ato që i përshtaten biznesit tënd.',
  bp_summary_cafe: 'Kafe: menu me QR, porosi nga tavolina, kamerierë, Lucky Bite dhe karta e ditës. Pa delivery.',
  bp_summary_mensa: 'Mensë: menu me QR dhe delivery, me kartën e besnikërisë të përfshirë falas.',
  bp_summary_fastfood: 'Fast-Food: menu me QR dhe delivery, me kartën e besnikërisë të përfshirë falas, plus emaile win-back.',
  bp_summary_restaurant: 'Restorant: menu me QR, porosi në tavolinë, kamerierë dhe rezervime tavolinash, plus Lucky Bite, emaile win-back dhe karta e ditës. Pa delivery.',
  bp_summary_hotel: 'Hotel: menu me QR, room service nga telefoni i mysafirit, kamerierë dhe rezervime për restorantin e hotelit.',
  bp_hotel_roomservice_label: 'Room service me QR',
  bp_hotel_roomservice_desc: 'Mysafirët skanojnë kodin në dhomë, porosisin nga telefoni dhe kuzhina e çon lart. Dhomat dhe tavolinat e restorantit mund të bashkëjetojnë.',
  wiz_feat_reco: '★ E rekomanduar për biznesin tënd',
  setup_biz_type: 'Lloji i biznesit',
  setup_biz_type_ph: 'Zgjidh…',
  setup_biz_type_required: 'Zgjidh çfarë lloj biznesi ke',
  setup_plan_mismatch: 'Paketa {plan} përfshin veçori që nuk ekzistojnë për një biznes të tipit {type}. Fillo me planin falas Starter dhe shto saktësisht ato që të përshtaten.',
  setup_plan_use_starter: 'Përdor Starter',
  adm_addon_not_for_type: 'Nuk ofrohet për llojin tënd të biznesit',

  // hapi 2 — problematikat
  wiz_problem_title: 'Çfarë problematike ke?',
  wiz_problem_sub: 'Zgjidh një ose më shumë — na ndihmon të kuptojmë çfarë të duhet.',
  wiz_problem_queue: 'Klientët presin gjatë radhën',
  wiz_problem_printed: 'Menuja ime është e printuar/vjetëruar',
  wiz_problem_online: 'Dua më shumë porosi online',
  wiz_problem_know: 'Dua të njoh klientët e mi',
  wiz_problem_churn: 'Humbas klientë që s\'kthehen më',
  wiz_problem_other: 'Tjetër',
  wiz_problem_freetext: 'Na trego me pak fjalë...',

  // hapat e features
  wiz_feat_step_of: 'Feature {n} nga {total}',
  wiz_feat_placeholder_tag: 'vend-mbajtëse ilustrimi',
  wiz_feat_always: '✓ Gjithmonë e përfshirë',
  wiz_feat_bundle_included: 'Përfshirë falas:',
  wiz_feat_subtotal: 'Deri tani',

  // fatura
  wiz_bill_topbar: 'Fatura',
  wiz_bill_title: 'Fatura jote',
  wiz_period_1m: 'Mujor',
  wiz_period_3m: '3-Muajsh',
  wiz_period_12m: 'Vjetor',
  wiz_cycle_1m: 'çdo muaj',
  wiz_cycle_3m: 'çdo 3 muaj',
  wiz_cycle_12m: 'çdo vit',
  wiz_billed: 'faturohet {cycle}',
  wiz_total_discount: ' · ulje totale ~{pct}%',
  wiz_go_to_payment: 'Vazhdo te Pagesa',
  wiz_go_to_account: 'Krijo Llogarinë Tënde',
  wiz_session_banner_1: 'Po vazhdon si ',
  wiz_session_banner_2: '. Nuk je ti?',
  wiz_session_banner_fallback: 'llogaria jote ekzistuese',
  wiz_session_logout_link: 'Dil dhe fillo nga e para',
  wiz_session_logout_busy: 'Duke dalë…',

  // kodi promo
  wiz_promo_placeholder: 'Kod promo',
  wiz_promo_apply: 'Apliko',
  wiz_promo_invalid: 'Kod jo i vlefshëm.',
  wiz_promo_inactive: 'Ky kod nuk është më aktiv.',
  wiz_promo_expired: 'Ky kod ka skaduar.',
  wiz_promo_plan_only: 'Ky kod vlen vetëm për: {plans}.',
  wiz_promo_period_only: 'Ky kod vlen vetëm për angazhim {periods}.',
  wiz_promo_applied: '✓ Kodi u aplikua — ekstra −{pct}%',
  wiz_promo_or: 'ose',
  wiz_promo_plan_wizard: 'paketë e personalizuar (wizard)',
  wiz_commit_1m: '1-mujor',
  wiz_commit_3m: '3-mujor',
  wiz_commit_12m: '12-mujor',

  // hapi i llogarisë
  wiz_acc_topbar: 'Llogaria',
  wiz_acc_topbar_yours: 'Llogaria jote',
  wiz_acc_title: 'Krijo llogarinë',
  wiz_acc_sub: 'Krijo llogarinë tënde — hapi tjetër është pagesa që aktivizon restorantin.',
  wiz_acc_biz_name: 'Emri i biznesit *',
  wiz_acc_first: 'Emri *',
  wiz_acc_last: 'Mbiemri *',
  wiz_acc_phone: 'Telefoni *',
  wiz_acc_email: 'Email *',
  wiz_acc_password: 'Fjalëkalimi *',
  wiz_acc_password2: 'Konfirmo fjalëkalimin *',
  wiz_acc_busy: 'Duke vazhduar...',
  wiz_acc_going_payment: 'Po vazhdojmë te pagesa…',
  wiz_acc_check_email_title: 'Kontrollo email-in tënd',
  wiz_acc_check_email_body_1: 'Të dërguam një link konfirmimi te ',
  wiz_acc_check_email_body_2: '. Sapo ta konfirmosh, do të kthehesh automatikisht këtu dhe restoranti do të krijohet.',
  wiz_acc_taken_title: 'Kjo email ekziston tashmë',
  wiz_acc_taken_sub_1: 'Ka një llogari me ',
  wiz_acc_taken_sub_2: '. Fut fjalëkalimin për të vazhduar.',
  wiz_acc_taken_continue: 'Vazhdo dhe krijo restorantin',
  wiz_acc_taken_other: 'Përdor një email tjetër',
  wiz_acc_wrong_password: 'Fjalëkalim i gabuar.',
  wiz_err_biz_required: 'Emri i biznesit është i detyrueshëm',
  wiz_err_first_required: 'Emri është i detyrueshëm',
  wiz_err_last_required: 'Mbiemri është i detyrueshëm',
  wiz_err_phone_required: 'Telefoni është i detyrueshëm',
  wiz_err_email_invalid: 'Email jo i vlefshëm',
  wiz_err_password_short: 'Fjalëkalimi duhet të ketë të paktën 6 karaktere',
  wiz_err_password_mismatch: 'Fjalëkalimet nuk përputhen',
  wiz_err_password_required: 'Fjalëkalimi është i detyrueshëm',

  // hapi i pagesës
  wiz_pay_topbar: 'Pagesa',
  wiz_pay_title: 'Paguaj dhe Aktivizo',
  wiz_pay_sub: 'Ky është një blerje reale — jo provë falas. Abonimi faturohet {cycle} derisa ta anulosh.',
  wiz_pay_cycle_note: 'faturohet {cycle} · rinovohet automatikisht',
  wiz_pay_preparing: 'Po përgatisim pagesën…',
  wiz_pay_retry: 'Provo Përsëri',
  wiz_pay_paypal_error: 'Ndodhi një gabim me PayPal. Provo përsëri.',
  wiz_pay_not_configured: 'Pagesat nuk janë konfiguruar ende. Kontakto suportin.',
  wiz_pay_creating: 'Pagesa u konfirmua — po krijojmë restorantin tënd…',
  wiz_pay_session_expired: 'Sesioni ka skaduar. Ju lutem hyni përsëri.',
  wiz_pay_provision_failed: 'Nuk arritëm të përgatisim pagesën.',
  wiz_pay_not_confirmed: 'Pagesa nuk u konfirmua.',
  wiz_pay_partial_failure: 'Pagesa u konfirmua nga PayPal, por diçka shkoi keq gjatë krijimit të restorantit. Kontakto suportin ({email}) me këtë referencë abonimi: {ref}',

  // përfundimi
  wiz_done_title: 'Mirë se vjen në Assaggioo!',
  wiz_done_sub: 'Pagesa u konfirmua — restoranti yt është aktiv. Po të çojmë te paneli yt i administrimit.',
};

export type Translations = Record<keyof typeof en, string>;
export type Lang = 'en' | 'sq';

const TRANSLATIONS: Record<Lang, Translations> = { en, sq };

// Add a new entry here (plus a translations object + Lang union member, and a
// flag SVG in public/flags/) to support another language — the dropdown below
// needs no further changes. Flags are bundled SVGs, not emoji: Windows Chrome
// has no color-flag glyphs and falls back to plain "GB"/"AL" text otherwise.
const LANGUAGES: { code: Lang; flag: string; name: string }[] = [
  { code: 'en', flag: '/flags/gb.svg', name: 'English' },
  { code: 'sq', flag: '/flags/al.svg', name: 'Shqip' },
];

/* ─────────────────────────────────────────────────────────────────────
   CONTEXT
───────────────────────────────────────────────────────────────────── */

interface LangCtx {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: Translations;
  toggle: () => void;
}

const Ctx = createContext<LangCtx>({
  lang: 'en', setLang: () => {}, t: en, toggle: () => {},
});

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [lang, setLangState] = useState<Lang>(() => {
    const stored = localStorage.getItem('assaggioo_lang');
    return (stored === 'sq' || stored === 'en') ? stored : 'en';
  });

  const setLang = useCallback((l: Lang) => {
    setLangState(l);
    localStorage.setItem('assaggioo_lang', l);
  }, []);

  const toggle = useCallback(() => {
    setLang(lang === 'en' ? 'sq' : 'en');
  }, [lang, setLang]);

  useEffect(() => {
    document.documentElement.lang = lang;
  }, [lang]);

  return (
    <Ctx.Provider value={{ lang, setLang, t: TRANSLATIONS[lang], toggle }}>
      {children}
    </Ctx.Provider>
  );
}

export function useLanguage() { return useContext(Ctx); }
export function useT() { return useContext(Ctx).t; }

/* ─────────────────────────────────────────────────────────────────────
   LANG TOGGLE BUTTON
───────────────────────────────────────────────────────────────────── */

export function LangToggle({ className = '', style }: { className?: string; style?: React.CSSProperties }) {
  const { lang, setLang } = useLanguage();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const current = LANGUAGES.find(l => l.code === lang) ?? LANGUAGES[0];

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  return (
    <div ref={wrapRef} className={`lang-toggle ${className}`} style={style}>
      <button
        type="button"
        onClick={() => setOpen(o => !o)}
        className="lang-toggle-btn"
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        <img className="lang-toggle-flag" src={current.flag} alt="" width="18" height="12" />
        <span className="lang-toggle-text">{current.code.toUpperCase()}</span>
        <svg className="lang-toggle-caret" width="9" height="6" viewBox="0 0 9 6" fill="none">
          <path d="M1 1L4.5 4.5L8 1" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
        </svg>
      </button>
      {open && (
        <ul className="lang-toggle-menu" role="listbox">
          {LANGUAGES.map(l => (
            <li key={l.code} role="option" aria-selected={l.code === lang}>
              <button
                type="button"
                className={`lang-toggle-option ${l.code === lang ? 'active' : ''}`}
                onClick={() => { setLang(l.code); setOpen(false); }}
              >
                <span className="lang-toggle-check">{l.code === lang ? '✓' : ''}</span>
                <img className="lang-toggle-flag" src={l.flag} alt="" width="19" height="13" />
                <span className="lang-toggle-name">{l.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
